import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users,
  Shield,
  BarChart3,
  Calendar,
  Filter,
  Download,
  RotateCcw,
  ArrowLeft,
  FileEdit,
  Layers,
  TrendingUp,
  TrendingDown,
  Activity,
  Target,
  Zap,
  Trophy,
  Swords,
  Clock,
  PieChart as PieChartIcon,
} from "lucide-react";
import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  Cell,
  BarChart,
  Bar,
  Legend,
  LineChart,
  Line,
  LabelList,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from "recharts";
import {
  deriveTeamPerformanceFromRaw,
  buildRolePoolRecommendations,
  summarizeTeamPriority,
  buildCrossTeamHeroComparison,
  buildHeroMetaByPosition,
  buildLeagueRoleBenchmarks,
  buildTeamDraftPreferences,
} from "../utils/teamPerformancePipeline";
import {
  parseOpponentCombosForTeam,
  normalizeOppToComboStats,
  parseWorkbookToComboStats,
  parseFsOneHeroFromRplPlayerStatsP,
  parseEarlyGameMacroP,
  aggregateEarlyGameMacro,
  aggregateEarlyMacroByOpponent,
  parseFsTeamReportP,
  aggregateFsTeamReport,
} from "../utils/excelParser";
import { analyzeTeamByAI, askCoachByAI } from "../utils/aiApi";
import { BRAND_COLORS, TEAM_SHORT } from "../theme/brandTheme";
import { getHeroImageUrl } from "../utils/heroCatalog";
import { num, pct, clamp } from "../components/teamPerformance/tpFormat";
import {
  T,
  FONT,
  CHART_AXIS_TICK,
  CHART_LEGEND_STYLE,
  CHART_GRID,
  ChartTooltip,
  ChartPanel,
  EmptyChart,
} from "../components/teamPerformance/tpUi";
import {
  SideWinRateChart,
  ScrimVsOfficialChart,
  GameLengthHistogram,
  WinRateTrendChart,
  RolePerformanceRadar,
  RoleGroupedBars,
  RoleDamageShareChart,
  DraftPickBanChart,
  DraftWinRateChart,
  MacroTimeSeriesChart,
  MacroLeadSplitChart,
  MacroOpponentBars,
} from "../components/teamPerformance/TpCharts";

/* Theme (T), FONT, CHART_* constants now live in
   ../components/teamPerformance/tpUi and are imported above. */
const AI_QUICK_PROMPTS = [
  "สรุปผลงานทีมช่วง 30 วันที่ผ่านมา จุดแข็ง/จุดอ่อน",
  "เทียบกับเดือนก่อน KPI ไหนดีขึ้นและแย่ลง",
  "ทีม/คนไหนต่ำกว่าเป้าต่อเนื่องเกิน 2 สัปดาห์",
  "หา outlier ที่ผลงานสูงผิดปกติและต่ำผิดปกติ",
  "ถ้าโฟกัสแค่ 3 เรื่องสัปดาห์นี้ ควรทำอะไรก่อน",
  "คาดการณ์สิ้นเดือนและโอกาสถึงเป้าเป็นกี่เปอร์เซ็นต์",
  "ช่วงวันไหน performance ตกบ่อยที่สุด",
  "แบ่งกลุ่ม Top / Mid / Low performer พร้อมคำแนะนำ",
  "ช่วยทำ Action Plan 14 วันสำหรับทีมต่ำกว่าเป้า",
  "สรุปเป็น executive summary 5 บรรทัด",
];

/* ── animation variants ── */
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.07, duration: 0.45, ease: "easeOut" } }),
};

/* ═══════════════════════════════════════════════════════════════
   Global Style injection
   ═══════════════════════════════════════════════════════════════ */
const GlobalInject = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&family=Rajdhani:wght@400;500;600;700&display=swap');
    .tp-root *{box-sizing:border-box;font-family:${FONT};}
    @keyframes tpScan{0%{top:-10%;opacity:0}50%{opacity:1}100%{top:110%;opacity:0}}
    @keyframes tpPulse{0%,100%{opacity:1}50%{opacity:.55}}
    @keyframes tpGlow{0%,100%{box-shadow:0 0 8px ${T.primary}66}50%{box-shadow:0 0 22px ${T.primary}aa}}
    @keyframes tpAiSweep{0%{transform:translateX(-130%)}100%{transform:translateX(130%)}}
    @keyframes tpAiPulse{0%,100%{box-shadow:0 0 0 0 rgba(255,16,38,0.24)}50%{box-shadow:0 0 0 12px rgba(255,16,38,0)}}
    @keyframes tpAiFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-3px)}}
    .tp-table tr:hover td{background:rgba(255,16,38,0.06)!important}
    .tp-table thead th{position:sticky;top:0;z-index:2}
    .tp-select{width:100%;padding:9px 12px;background:#ffffff;border:1.5px solid rgba(255,16,38,0.22);border-radius:10px;color:${T.text};outline:none;font-size:14px;font-family:${FONT};transition:border .25s}
    .tp-select:focus{border-color:${T.primary};box-shadow:0 0 12px ${T.primary}44}
    .tp-select option{background:#ffffff;color:#111827}
    .tp-input{width:100%;padding:9px 12px;background:#ffffff;border:1.5px solid rgba(255,16,38,0.22);border-radius:10px;color:${T.text};outline:none;font-size:14px;font-family:${FONT};transition:border .25s}
    .tp-input:focus{border-color:${T.primary};box-shadow:0 0 12px ${T.primary}44}
    .tp-scroll::-webkit-scrollbar{width:6px;height:6px}
    .tp-scroll::-webkit-scrollbar-track{background:rgba(255,16,38,.08);border-radius:8px}
    .tp-scroll::-webkit-scrollbar-thumb{background:linear-gradient(to bottom,${T.primary},${T.primaryDark});border-radius:8px}
    .tp-main-with-ai{padding-right:0}
    .tp-top-grid{display:block;margin-bottom:14px}
    .tp-ai-dock{position:static;width:auto;max-width:none;height:auto;margin-top:12px;z-index:auto;align-self:auto}
    @media (max-width:1200px){
      .tp-main-with-ai{padding-right:0}
      .tp-top-grid{display:block}
      .tp-ai-dock{position:static;width:auto;max-width:none;height:auto;margin-top:0}
    }
    .tp-ai-command{position:relative;border-radius:14px;border:1px solid rgba(255,16,38,0.32);background:radial-gradient(circle at 0% 0%, rgba(255,16,38,0.08), transparent 42%),radial-gradient(circle at 100% 0%, rgba(255,77,95,0.08), transparent 42%),linear-gradient(160deg,#ffffff,#fff7f8);padding:12px;overflow:hidden}
    .tp-ai-command:before{content:"";position:absolute;top:0;bottom:0;width:42%;background:linear-gradient(90deg,transparent,rgba(255,16,38,0.10),transparent);animation:tpAiSweep 6s linear infinite;pointer-events:none}
    .tp-ai-orb{width:10px;height:10px;border-radius:50%;background:${T.accent};animation:tpAiPulse 1.8s ease-in-out infinite, tpAiFloat 2.4s ease-in-out infinite}
  `}</style>
);

/* ═══════════════════════════════════════════════════════════════
   Decorative elements
   ═══════════════════════════════════════════════════════════════ */
const GridBg = () => (
  <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(255,16,38,0.04) 1px,transparent 1px),linear-gradient(90deg,rgba(255,16,38,0.04) 1px,transparent 1px)", backgroundSize: "60px 60px", opacity: 0.25, pointerEvents: "none" }} />
);

const ScanLine = () => (
  <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 5, borderRadius: 16 }}>
    <div style={{ position: "absolute", left: 0, right: 0, height: 2, background: `linear-gradient(90deg,transparent,${T.primary},transparent)`, animation: "tpScan 3.5s linear infinite", boxShadow: `0 0 12px ${T.primary}` }} />
  </div>
);

const CornerAccents = ({ color = T.primary }) => (
  <>
    <div style={{ position: "absolute", top: 0, left: 0, width: 50, height: 50, borderTop: `2.5px solid ${color}`, borderLeft: `2.5px solid ${color}`, borderRadius: "14px 0 0 0", pointerEvents: "none", filter: `drop-shadow(0 0 4px ${color})` }} />
    <div style={{ position: "absolute", bottom: 0, right: 0, width: 50, height: 50, borderBottom: `2.5px solid ${color}`, borderRight: `2.5px solid ${color}`, borderRadius: "0 0 14px 0", pointerEvents: "none", filter: `drop-shadow(0 0 4px ${color})` }} />
  </>
);

/* ═══════════════════════════════════════════════════════════════
   Reusable UI blocks
   ═══════════════════════════════════════════════════════════════ */
function GlassCard({ children, style = {}, neon = false, delay = 0, className = "" }) {
  return (
    <motion.div className={className} variants={fadeUp} initial="hidden" animate="show" custom={delay} style={{ position: "relative", background: T.card, backdropFilter: "blur(18px)", borderRadius: 16, border: neon ? `1.5px solid ${T.primary}` : `1px solid ${T.border}`, boxShadow: neon ? T.glow : "none", overflow: "hidden", ...style }}>
      {neon && <ScanLine />}
      <CornerAccents />
      {children} 
    </motion.div>
  );
}

function MetricCard({ label, value, icon: Icon, color = T.primary, delay = 0, helperText = "" }) {
  return (
    <motion.div variants={fadeUp} initial="hidden" animate="show" custom={delay} style={{ background: T.glass, border: `1px solid ${T.border}`, borderRadius: 14, padding: "14px 16px", minHeight: helperText ? 104 : 86, position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: 10, right: 12, opacity: 0.12 }}>
        {Icon && <Icon size={38} />}
      </div>
      <div style={{ color: T.muted, fontSize: 12, fontWeight: 600, letterSpacing: 0.6, textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 30, fontWeight: 900, marginTop: 6, color, lineHeight: 1.1 }}>{value}</div>
      {helperText && <div style={{ marginTop: 6, color: T.muted, fontSize: 11, lineHeight: 1.25 }}>{helperText}</div>}
    </motion.div>
  );
}

function BtnGlow({ children, onClick, variant = "default", icon: Icon, style: s = {} }) {
  const isPrimary = variant === "primary";
  return (
    <button onClick={onClick} style={{ display: "inline-flex", alignItems: "center", gap: 6, border: `1.5px solid ${isPrimary ? T.primary : T.border}`, borderRadius: 12, padding: "9px 16px", cursor: "pointer", color: T.text, background: isPrimary ? `linear-gradient(135deg,${T.primary},${T.primaryDark})` : "rgba(255,16,38,0.04)", fontWeight: 700, fontSize: 13, fontFamily: FONT, transition: "all .25s", ...s }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = T.glowSm; e.currentTarget.style.borderColor = T.primary; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = isPrimary ? T.primary : T.border; }}>
      {Icon && <Icon size={15} />}
      {children}
    </button>
  );
}

function FieldLabel({ label, icon: Icon, children }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 5, color: T.muted, fontSize: 11, fontWeight: 600, marginBottom: 5, letterSpacing: 0.5, textTransform: "uppercase" }}>
        {Icon && <Icon size={13} />}
        {label}
      </div>
      {children}
    </label>
  );
}

function ScopeChip({ label, value, tone = "default" }) {
  const toneStyle = tone === "warn"
    ? { color: T.warn, border: "rgba(255,209,102,0.45)", bg: "rgba(255,209,102,0.10)" }
    : tone === "good"
      ? { color: T.success, border: "rgba(0,208,132,0.42)", bg: "rgba(0,208,132,0.10)" }
      : { color: T.accent, border: "rgba(255,16,38,0.32)", bg: T.tintMd };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, borderRadius: 999, padding: "4px 10px", border: `1px solid ${toneStyle.border}`, background: toneStyle.bg, fontSize: 11, fontWeight: 700, color: toneStyle.color, letterSpacing: 0.2 }}>
      <span style={{ color: T.muted }}>{label}:</span> {value}
    </span>
  );
}

/* num / pct / clamp now imported from ../components/teamPerformance/tpFormat */
function isCoreStatBucketRow(r) {
  const b = String(r?.bucket || "").toLowerCase();
  return b === "scrim" || b === "official";
}
function hasTierStats(stats) {
  return ((stats?.combo2?.length || 0) + (stats?.combo3?.length || 0) + (stats?.oneHero?.length || 0)) > 0;
}
function mapComboRows(stats) {
  const rows2 = (stats?.combo2 || []).map((c) => {
    const total = num(c.total);
    const win = num(c.win);
    return {
      type: c.comboType || "2-heroes",
      combo: c.combo || "-",
      total,
      win,
      lose: Math.max(0, total - win),
      wr: pct(win, total),
    };
  });
  const rows3 = (stats?.combo3 || []).map((c) => {
    const total = num(c.total);
    const win = num(c.win);
    return {
      type: c.comboType || "3-heroes",
      combo: c.combo || "-",
      total,
      win,
      lose: Math.max(0, total - win),
      wr: pct(win, total),
    };
  });
  return [...rows2, ...rows3].sort((a, b) => num(b.total) - num(a.total) || num(b.wr) - num(a.wr));
}
function buildOneHeroRowsFromGames(games) {
  const m = new Map();
  (games || []).forEach((g) => {
    const t = String(g?.type || "").trim().toLowerCase();
    if (t !== "rpl" && t !== "scrim") return;
    const picks = Array.isArray(g?.fsPick) ? g.fsPick : [];
    const positions = Array.isArray(g?.fsPosition) ? g.fsPosition : [];
    for (let i = 0; i < Math.min(picks.length, positions.length); i++) {
      const hero = String(picks[i] || "").trim();
      const role = normalizeRoleKey(positions[i]) || "-";
      if (!hero) continue;
      const key = `${hero}|${role}`;
      const cur = m.get(key) || { hero, role, total: 0, win: 0 };
      cur.total += 1;
      cur.win += (g?.win === 1 || g?.win === "1") ? 1 : 0;
      m.set(key, cur);
    }
  });
  return [...m.values()]
    .map((x) => ({ ...x, lose: Math.max(0, x.total - x.win), wr: pct(x.win, x.total) }))
    .sort((a, b) => num(b.total) - num(a.total) || num(b.wr) - num(a.wr));
}
function buildComboRowsFromRaw(rows, teamCode) {
  const pairMap = new Map();
  const tripleMap = new Map();
  const touch = (store, heroes, type, winAdd) => {
    const combo = heroes.join(" + ");
    const key = `${type}|${combo}`;
    const cur = store.get(key) || { type, combo, total: 0, win: 0 };
    cur.total += 1;
    cur.win += winAdd;
    store.set(key, cur);
  };
  (rows || []).forEach((r) => {
    if (normalizeTeamCode(r.team) !== teamCode) return;
    const picks = (r.picks || []).map((h) => String(h || "").trim()).filter(Boolean).slice(0, 5);
    if (picks.length < 2) return;
    const winAdd = num(r.win);
    for (let i = 0; i < picks.length; i++) {
      for (let j = i + 1; j < picks.length; j++) {
        touch(pairMap, [picks[i], picks[j]], "2-heroes", winAdd);
      }
    }
    if (picks.length >= 3) {
      for (let i = 0; i < picks.length; i++) {
        for (let j = i + 1; j < picks.length; j++) {
          for (let k = j + 1; k < picks.length; k++) {
            touch(tripleMap, [picks[i], picks[j], picks[k]], "3-heroes", winAdd);
          }
        }
      }
    }
  });
  return [...pairMap.values(), ...tripleMap.values()]
    .map((x) => ({ ...x, lose: Math.max(0, x.total - x.win), wr: pct(x.win, x.total) }))
    .sort((a, b) => num(b.total) - num(a.total) || num(b.wr) - num(a.wr));
}

function DeltaCell({ value, big = false }) {
  const n = num(value);
  const color = n >= 0 ? T.success : T.danger;
  const sign = n > 0 ? "+" : "";
  return <span style={{ color, fontWeight: 800, fontSize: big ? 30 : "inherit" }}>{`${sign}${n.toFixed(2)}`}</span>;
}
function fmtMetric(v, digits = 2) {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(digits) : "-";
}

function bucketLabel(v) {
  if (v === "official") return "match day";
  if (v === "scrim") return "scrim";
  return v || "-";
}

function HeroCells({ heroes = [], mode = "text" }) {
  const list = (heroes || []).map((h) => String(h || "").trim()).filter(Boolean).slice(0, 5);
  if (!list.length) return <span style={{ color: T.muted }}>-</span>;
  if (mode !== "image") return <span>{list.join(", ")}</span>;

  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
      {list.map((hero, idx) => {
        const src = getHeroImageUrl(hero);
        return (
          <div
            key={`${hero}-${idx}`}
            title={hero}
            style={{
              width: 26,
              height: 26,
              borderRadius: 6,
              overflow: "hidden",
              border: `1px solid ${T.border}`,
              background: "rgba(255,255,255,0.05)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              color: T.text,
              fontSize: 9,
              fontWeight: 700,
              lineHeight: 1,
              textAlign: "center",
              padding: 2,
            }}
          >
            {src ? (
              <img src={src} alt={hero} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              hero.slice(0, 2).toUpperCase()
            )}
          </div>
        );
      })}
    </div>
  );
}
function ymdLabel(v) {
  const s = String(v || "");
  if (!/^\d{8}$/.test(s)) return "-";
  return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
}
function isInferredType(v) {
  return String(v || "").toUpperCase().includes("_INFERRED");
}
function normalizeRoleKey(v) {
  const r = String(v || "").trim().toUpperCase();
  if (r === "ROAMING" || r === "SUPPORT") return "SUP";
  if (r === "JUNGLE") return "JUG";
  if (r === "MIDLANE") return "MID";
  return r;
}
function normalizeTeamCode(v) {
  const t = String(v || "").trim().toUpperCase();
  if (t === "TLN") return "TEN";
  if (t === "EAG") return "EA";
  if (t === "HDG") return "HD";
  return t;
}
function ymdToDateUTC(ymd) {
  const s = String(ymd || "");
  if (!/^\d{8}$/.test(s)) return null;
  const y = Number(s.slice(0, 4));
  const m = Number(s.slice(4, 6));
  const d = Number(s.slice(6, 8));
  const dt = new Date(Date.UTC(y, m - 1, d));
  return Number.isFinite(dt.getTime()) ? dt : null;
}
function dateToYmdUTC(date) {
  if (!(date instanceof Date) || !Number.isFinite(date.getTime())) return "";
  const y = String(date.getUTCFullYear()).padStart(4, "0");
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${y}${m}${d}`;
}
function ymdMonthKey(ymd) {
  const s = String(ymd || "");
  if (!/^\d{8}$/.test(s)) return "";
  return `${s.slice(0, 4)}-${s.slice(4, 6)}`;
}
function pearsonFromPairs(pairs = []) {
  const rows = (pairs || []).filter((p) => Number.isFinite(num(p?.x)) && Number.isFinite(num(p?.y)));
  const n = rows.length;
  if (n < 4) return null;
  let sx = 0; let sy = 0; let sxx = 0; let syy = 0; let sxy = 0;
  rows.forEach((p) => {
    const x = num(p.x);
    const y = num(p.y);
    sx += x; sy += y; sxx += x * x; syy += y * y; sxy += x * y;
  });
  const nume = n * sxy - sx * sy;
  const den = Math.sqrt(Math.max(0, (n * sxx - sx * sx) * (n * syy - sy * sy)));
  if (!Number.isFinite(den) || den <= 0) return null;
  return +(nume / den).toFixed(3);
}

/* ── CSV export helpers ── */
function toCSV(rows, columns) {
  const esc = (v) => { const s = String(v ?? ""); return (s.includes(",") || s.includes('"') || s.includes("\n")) ? `"${s.replace(/"/g, '""')}"` : s; };
  const header = columns.map(c => c.label).join(",");
  const lines = rows.map(r => columns.map(c => esc(r[c.key])).join(","));
  return [header, ...lines].join("\n");
}
function downloadCSV(filename, csvText) {
  const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
}

const FILTER_STORAGE_KEY = "team_performance_filters_v1";
const COACH_MODE_KEY = "team_performance_coach_mode_v1";
const QUALITY_GATE_KEY = "team_performance_quality_gate_v1";
const PROCESS_STORAGE_KEY = "team_performance_process_v1";
const UX_PRESET_KEY = "team_performance_ux_preset_v1";
const DEFAULT_QUALITY_CONFIG = { inferredWarnPct: 8, minOfficialRowsPerTeam: 5, minTeamRowsToCheckOfficial: 20, minTotalRows: 250 };
const DEFAULT_PROCESS_CONFIG = {
  autoPriority: true,
  seasonTag: "RPL 2026",
  patchTag: "",
  patchStartDate: "",
  lastFeedbackAt: "",
};

/* ChartTooltip / ChartPanel now imported from ../components/teamPerformance/tpUi */

/* ═══════════════════════════════════════════════════════════════
   Quadrant Breakdown: Diverging bar by delta
   ═══════════════════════════════════════════════════════════════ */

function DeltaScatterChart({ data, teamList }) {
  const rows = useMemo(() => {
    return [...data]
      .sort((a, b) => Math.abs(num(b.deltaWinRate)) - Math.abs(num(a.deltaWinRate)))
      .slice(0, 12)
      .map((d) => ({
        id: `${d.hero}-${d.team}`,
        label: `${d.hero} (${d.team})`.slice(0, 18),
        deltaWR: num(d.deltaWinRate),
        scrimWR: num(d.scrimWinRate),
        officialWR: num(d.officialWinRate),
      }));
  }, [data]);

  const xDomain = useMemo(() => {
    const maxAbs = rows.reduce((m, r) => Math.max(m, Math.abs(num(r.deltaWR))), 0);
    const d = Math.max(6, Math.ceil(maxAbs + 1));
    return [-d, d];
  }, [rows]);
  const exec = useMemo(() => {
    const pos = rows.filter((r) => r.deltaWR > 0);
    const neg = rows.filter((r) => r.deltaWR < 0);
    const avg = rows.length ? rows.reduce((a, r) => a + r.deltaWR, 0) / rows.length : 0;
    return { pos: pos.length, neg: neg.length, avg: +avg.toFixed(2) };
  }, [rows]);

  if (!data.length) return <EmptyChart />;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 8, color: T.muted, fontSize: 12, flexWrap: "wrap" }}>
        <span>Top 12 (Diverging ΔWR)</span>
        <span>ทีมที่มีข้อมูล: {teamList.length} ทีม</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))", gap: 8, marginBottom: 8 }}>
        <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: "8px 10px", background: "linear-gradient(135deg,rgba(0,208,132,0.16),rgba(0,208,132,0.03))" }}>
          <div style={{ fontSize: 10, color: T.muted }}>Positive ΔWR</div>
          <div style={{ fontSize: 18, fontWeight: 900, color: T.success }}>{exec.pos}</div>
        </div>
        <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: "8px 10px", background: "linear-gradient(135deg,rgba(255,77,109,0.16),rgba(255,77,109,0.03))" }}>
          <div style={{ fontSize: 10, color: T.muted }}>Negative ΔWR</div>
          <div style={{ fontSize: 18, fontWeight: 900, color: T.danger }}>{exec.neg}</div>
        </div>
        <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: "8px 10px", background: "linear-gradient(135deg,rgba(255,16,38,0.16),rgba(255,16,38,0.03))" }}>
          <div style={{ fontSize: 10, color: T.muted }}>Avg ΔWR</div>
          <div style={{ fontSize: 18, fontWeight: 900, color: exec.avg >= 0 ? T.success : T.danger }}>{exec.avg > 0 ? "+" : ""}{exec.avg}%</div>
        </div>
      </div>
      <ChartPanel minHeight={Math.max(340, rows.length * 34 + 100)}>
        <ResponsiveContainer width="100%" height={Math.max(330, rows.length * 34 + 90)}>
          <BarChart data={rows} layout="vertical" margin={{ top: 10, right: 18, bottom: 10, left: 30 }}>
            <CartesianGrid {...CHART_GRID} />
            <ReferenceLine x={0} stroke={T.refStroke} strokeDasharray="4 4" />
            <XAxis type="number" domain={xDomain} tickFormatter={(v) => `${v}%`} stroke={T.axisStroke} tick={CHART_AXIS_TICK} />
            <YAxis type="category" dataKey="label" width={110} stroke={T.axisStroke} tick={CHART_AXIS_TICK} />
            <Tooltip
              content={<ChartTooltip formatter={(v, name, item) => {
                if (name === "ΔWinRate") return `${num(v).toFixed(2)}%`;
                const p = item?.payload || {};
                return `Scrim ${num(p.scrimWR).toFixed(1)}% | Match ${num(p.officialWR).toFixed(1)}%`;
              }} />}
            />
            <Legend wrapperStyle={CHART_LEGEND_STYLE} />
            <Bar dataKey="deltaWR" name="ΔWinRate" radius={[0, 6, 6, 0]} maxBarSize={22}>
              {rows.map((r) => <Cell key={r.id} fill={r.deltaWR >= 0 ? T.success : T.danger} />)}
              <LabelList dataKey="deltaWR" position="right" formatter={(v) => `${num(v).toFixed(1)}%`} style={{ fill: BRAND_COLORS.chartTick, fontWeight: 700, fontSize: 11 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartPanel>
      <div style={{ marginTop: 6, color: T.muted, fontSize: 11 }}>
        ค่าไปทางขวาของ 0 = ดีขึ้น, ซ้ายของ 0 = แย่ลง
      </div>

    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   GROUPED BAR: Scrim WR vs Match Day WR
   ═══════════════════════════════════════════════════════════════ */
function WinRateCompareChart({ data }) {
  const top = useMemo(() => [...data]
    .sort((a, b) => Math.abs(num(b.deltaWinRate)) - Math.abs(num(a.deltaWinRate)))
    .slice(0, 8)
    .map(d => ({
      name: `${d.hero} (${d.team})`.length > 18 ? `${`${d.hero} (${d.team})`.slice(0, 17)}…` : `${d.hero} (${d.team})`,
      fullName: d.hero,
      team: d.team,
      Scrim: num(d.scrimWinRate),
      MatchDay: num(d.officialWinRate),
      delta: num(d.deltaWinRate)
    })), [data]);
  const overview = useMemo(() => {
    const scr = top.length ? top.reduce((a, r) => a + num(r.Scrim), 0) / top.length : 0;
    const off = top.length ? top.reduce((a, r) => a + num(r.MatchDay), 0) / top.length : 0;
    return { scr: +scr.toFixed(1), off: +off.toFixed(1), delta: +(off - scr).toFixed(1) };
  }, [top]);
  if (!top.length) return <EmptyChart />;

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 8, marginBottom: 8 }}>
        <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: "8px 10px", background: "linear-gradient(135deg,rgba(255,16,38,0.16),rgba(255,16,38,0.03))" }}>
          <div style={{ fontSize: 10, color: T.muted }}>Avg Scrim WR</div>
          <div style={{ fontSize: 18, fontWeight: 900, color: T.primaryLight }}>{overview.scr}%</div>
        </div>
        <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: "8px 10px", background: "linear-gradient(135deg,rgba(255,16,38,0.16),rgba(255,16,38,0.03))" }}>
          <div style={{ fontSize: 10, color: T.muted }}>Avg Match WR</div>
          <div style={{ fontSize: 18, fontWeight: 900, color: T.accent }}>{overview.off}%</div>
        </div>
        <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: "8px 10px", background: "linear-gradient(135deg,rgba(0,208,132,0.16),rgba(0,208,132,0.03))" }}>
          <div style={{ fontSize: 10, color: T.muted }}>Net Gap</div>
          <div style={{ fontSize: 18, fontWeight: 900, color: overview.delta >= 0 ? T.success : T.danger }}>{overview.delta > 0 ? "+" : ""}{overview.delta}%</div>
        </div>
      </div>
      <ChartPanel minHeight={360}>
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={top} margin={{ top: 12, right: 18, bottom: 24, left: 4 }} barGap={8}>
            <CartesianGrid {...CHART_GRID} />
            <XAxis dataKey="name" stroke={T.axisStroke} tick={CHART_AXIS_TICK} interval={0} angle={-12} textAnchor="end" height={58} />
            <YAxis stroke={T.axisStroke} tick={CHART_AXIS_TICK} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
            <Tooltip content={<ChartTooltip formatter={(v) => `${num(v).toFixed(1)}%`} />} />
            <Legend wrapperStyle={CHART_LEGEND_STYLE} />
            <Bar dataKey="Scrim" fill={T.primaryLight} radius={[6, 6, 0, 0]} maxBarSize={28} />
            <Bar dataKey="MatchDay" fill={T.primaryDark} radius={[6, 6, 0, 0]} maxBarSize={28} />
          </BarChart>
        </ResponsiveContainer>
      </ChartPanel>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   HORIZONTAL BAR: Coverage breakdown
   ═══════════════════════════════════════════════════════════════ */
function CoverageDonut({ stats }) {
  const coverageData = [
    { name: "Scrim", value: num(stats.scrimRows), fill: T.primaryLight },
    { name: "Match Day", value: num(stats.officialRows), fill: T.primaryDark },
    { name: "Other", value: Math.max(0, num(stats.rawRows) - num(stats.scrimRows) - num(stats.officialRows)), fill: "rgba(255,16,38,0.25)" },
  ]
    .filter((d) => d.value > 0)
    .map((d) => ({
      ...d,
      pct: stats.rawRows > 0 ? (d.value * 100) / stats.rawRows : 0,
    }));
  if (!coverageData.length) return <EmptyChart />;

  return (
    <ChartPanel minHeight={280}>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={coverageData} layout="vertical" margin={{ top: 10, right: 24, bottom: 6, left: 12 }}>
          <CartesianGrid {...CHART_GRID} />
          <XAxis type="number" stroke={T.axisStroke} tick={CHART_AXIS_TICK} domain={[0, Math.max(1, stats.rawRows)]} />
          <YAxis type="category" dataKey="name" width={90} stroke={T.axisStroke} tick={CHART_AXIS_TICK} />
          <Tooltip content={<ChartTooltip formatter={(v) => `${v} rows`} />} />
          <Legend wrapperStyle={CHART_LEGEND_STYLE} />
          <Bar dataKey="value" name="Rows" radius={[0, 6, 6, 0]} maxBarSize={24}>
            {coverageData.map((d, i) => <Cell key={`${d.name}-${i}`} fill={d.fill} />)}
            <LabelList dataKey="pct" position="right" formatter={(v) => `${num(v).toFixed(1)}%`} style={{ fill: BRAND_COLORS.chartTick, fontWeight: 700, fontSize: 11 }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div style={{ display: "flex", justifyContent: "center", gap: 14, flexWrap: "wrap", marginTop: 6, color: T.muted, fontSize: 12 }}>
        {coverageData.map((d) => (
          <span key={d.name}>{d.name}: {d.value} ({d.pct.toFixed(1)}%)</span>
        ))}
      </div>
    </ChartPanel>
  );
}

/* ═══════════════════════════════════════════════════════════════
   STACKED BAR: Team Priority
   ═══════════════════════════════════════════════════════════════ */
function PriorityBarChart({ priority }) {
  const data = useMemo(
    () => (priority || [])
      .slice(0, 12)
      .map((p) => ({ name: p.team, trainMore: p.trainMore, stageReady: p.stageReady }))
      .sort((a, b) => (num(b.trainMore) + num(b.stageReady)) - (num(a.trainMore) + num(a.stageReady))),
    [priority]
  );
  if (!data.length) return <EmptyChart />;

  return (
    <ChartPanel minHeight={Math.max(260, data.length * 34 + 80)}>
      <ResponsiveContainer width="100%" height={Math.max(250, data.length * 34 + 70)}>
        <BarChart data={data} layout="vertical" margin={{ top: 10, right: 22, bottom: 10, left: 10 }} barGap={2}>
          <CartesianGrid {...CHART_GRID} />
          <XAxis type="number" stroke={T.axisStroke} tick={CHART_AXIS_TICK} allowDecimals={false} />
          <YAxis type="category" dataKey="name" width={58} stroke={T.axisStroke} tick={CHART_AXIS_TICK} />
          <Tooltip content={<ChartTooltip formatter={(v) => `${v} ฮีโร่`} />} />
          <Legend wrapperStyle={CHART_LEGEND_STYLE} />
          <Bar dataKey="trainMore" name="ต้องฝึกเพิ่ม" stackId="prio" fill={T.danger} radius={[0, 0, 0, 0]} />
          <Bar dataKey="stageReady" name="พร้อมแข่ง" stackId="prio" fill={T.success} radius={[0, 6, 6, 0]}>
            <LabelList dataKey="stageReady" position="right" formatter={(v) => `${v}`} style={{ fill: BRAND_COLORS.chartTick, fontWeight: 700, fontSize: 11 }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartPanel>
  );
}

function TeamGapBarChart({ rows = [] }) {
  const sorted = useMemo(() => [...rows].sort((a, b) => num(a.avgDeltaWR) - num(b.avgDeltaWR)), [rows]);
  const domain = useMemo(() => {
    const maxAbs = sorted.reduce((m, r) => Math.max(m, Math.abs(num(r.avgDeltaWR)), Math.abs(num(r.avgDeltaPR))), 0);
    const d = Math.max(4, Math.ceil(maxAbs + 1));
    return [-d, d];
  }, [sorted]);

  if (!sorted.length) return <EmptyChart />;

  return (
    <ChartPanel minHeight={Math.max(250, sorted.length * 36 + 80)}>
      <ResponsiveContainer width="100%" height={Math.max(240, sorted.length * 36 + 70)}>
        <BarChart data={sorted} layout="vertical" margin={{ top: 8, right: 26, bottom: 8, left: 50 }} barGap={5}>
          <CartesianGrid {...CHART_GRID} />
          <ReferenceLine x={0} stroke="rgba(255,16,38,0.25)" strokeDasharray="4 4" />
          <XAxis type="number" stroke={T.axisStroke} tick={CHART_AXIS_TICK} domain={domain} unit="%" />
          <YAxis type="category" dataKey="team" stroke={T.axisStroke} tick={CHART_AXIS_TICK} width={40} />
          <Tooltip content={<ChartTooltip formatter={(v) => `${num(v).toFixed(2)}%`} />} />
          <Legend wrapperStyle={CHART_LEGEND_STYLE} />
          <Bar dataKey="avgDeltaWR" name="Avg ΔWinRate" radius={[0, 6, 6, 0]} maxBarSize={14}>
            {sorted.map((r) => <Cell key={r.team} fill={num(r.avgDeltaWR) >= 0 ? T.success : T.danger} />)}
            <LabelList dataKey="avgDeltaWR" position="right" formatter={(v) => `${num(v).toFixed(2)}%`} style={{ fill: BRAND_COLORS.chartTick, fontWeight: 700, fontSize: 11 }} />
          </Bar>
          <Bar dataKey="avgDeltaPR" name="Avg ΔPickRate" radius={[0, 6, 6, 0]} maxBarSize={14}>
            {sorted.map((r) => <Cell key={`${r.team}-pr`} fill={num(r.avgDeltaPR) >= 0 ? T.primaryDark : T.primaryLight} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartPanel>
  );
}

function ScrimMatchSlopeChart({ data = [] }) {
  const top = useMemo(() => {
    return [...(data || [])]
      .sort((a, b) => Math.abs(num(b.deltaWinRate)) - Math.abs(num(a.deltaWinRate)))
      .slice(0, 10)
      .map((r) => ({
        id: `${r.team}-${r.hero}`,
        hero: r.hero,
        team: r.team,
        scrim: clamp(num(r.scrimWinRate), 0, 100),
        match: clamp(num(r.officialWinRate), 0, 100),
        delta: num(r.deltaWinRate),
      }));
  }, [data]);

  const summary = useMemo(() => {
    const up = top.filter((r) => r.delta > 0).length;
    const down = top.filter((r) => r.delta < 0).length;
    const avg = top.length ? top.reduce((a, r) => a + r.delta, 0) / top.length : 0;
    return { up, down, avg: +avg.toFixed(2) };
  }, [top]);

  if (!top.length) return <EmptyChart />;

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 8, marginBottom: 10 }}>
        <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: "7px 10px", background: "rgba(0,208,132,0.10)" }}>
          <div style={{ fontSize: 10, color: T.muted }}>Improved</div>
          <div style={{ fontSize: 18, fontWeight: 900, color: T.success }}>{summary.up}</div>
        </div>
        <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: "7px 10px", background: "rgba(255,77,109,0.10)" }}>
          <div style={{ fontSize: 10, color: T.muted }}>Dropped</div>
          <div style={{ fontSize: 18, fontWeight: 900, color: T.danger }}>{summary.down}</div>
        </div>
        <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: "7px 10px", background: T.tintLg }}>
          <div style={{ fontSize: 10, color: T.muted }}>Avg ΔWR</div>
          <div style={{ fontSize: 18, fontWeight: 900, color: summary.avg >= 0 ? T.success : T.danger }}>{summary.avg > 0 ? "+" : ""}{summary.avg}%</div>
        </div>
      </div>

      <ChartPanel minHeight={380}>
        <div style={{ color: T.muted, fontSize: 11, display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
          <span>Scrim WR</span>
          <span>Match WR</span>
        </div>
        <div className="tp-scroll" style={{ maxHeight: 330, overflowY: "auto", paddingRight: 6 }}>
          {top.map((r) => {
            const minX = Math.min(r.scrim, r.match);
            const span = Math.max(1.6, Math.abs(r.match - r.scrim));
            const isUp = r.match >= r.scrim;
            return (
              <div key={r.id} style={{ borderTop: "1px solid rgba(255,16,38,0.06)", padding: "9px 0" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 5 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: T.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.hero} <span style={{ color: T.muted }}>({r.team})</span></div>
                  </div>
                  <div style={{ color: isUp ? T.success : T.danger, fontSize: 12, fontWeight: 800 }}>
                    {isUp ? "+" : ""}{r.delta.toFixed(1)}%
                  </div>
                </div>
                <div style={{ position: "relative", height: 22, background: T.tintXs, border: "1px solid rgba(255,16,38,0.08)", borderRadius: 999 }}>
                  <div style={{ position: "absolute", top: "50%", left: `${minX}%`, width: `${span}%`, height: 2, marginTop: -1, background: isUp ? "rgba(0,208,132,0.95)" : "rgba(255,77,109,0.95)" }} />
                  <div title={`Scrim ${r.scrim.toFixed(1)}%`} style={{ position: "absolute", top: "50%", left: `${r.scrim}%`, width: 10, height: 10, marginTop: -5, marginLeft: -5, borderRadius: "50%", background: T.primaryLight, boxShadow: "0 0 0 2px rgba(255,157,64,0.18)" }} />
                  <div title={`Match ${r.match.toFixed(1)}%`} style={{ position: "absolute", top: "50%", left: `${r.match}%`, width: 10, height: 10, marginTop: -5, marginLeft: -5, borderRadius: "50%", background: T.primaryDark, boxShadow: "0 0 0 2px rgba(255,16,38,0.20)" }} />
                </div>
                <div style={{ marginTop: 4, display: "flex", justifyContent: "space-between", color: T.muted, fontSize: 11 }}>
                  <span>{r.scrim.toFixed(1)}%</span>
                  <span>{r.match.toFixed(1)}%</span>
                </div>
              </div>
            );
          })}
        </div>
      </ChartPanel>
    </div>
  );
}

/* EmptyChart now imported from ../components/teamPerformance/tpUi */

/* ═══════════════════════════════════════════════════════════════
   Section title
   ═══════════════════════════════════════════════════════════════ */
function SectionTitle({ icon: Icon, title, subtitle }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
      {Icon && <div style={{ width: 32, height: 32, borderRadius: 8, background: `linear-gradient(135deg,${T.primary},${T.primaryDark})`, display: "flex", alignItems: "center", justifyContent: "center" }}><Icon size={17} color="#fff" /></div>}
      <div>
        <h3 style={{ margin: 0, fontSize: 16, color: T.primaryLight, fontWeight: 800 }}>{title}</h3>
        {subtitle && <div style={{ color: T.muted, fontSize: 11 }}>{subtitle}</div>}
      </div>
    </div>
  );
}

function EarlyGameMacroSection({ agg, byOpponent = [], compareOpponent, compareHeadToHeadOnly }) {
  const MARKS = [4, 8, 12, 15];
  const games = agg?.games || 0;
  const totalGames = agg?.totalGames || 0;
  const scopeMode = agg?.scopeMode || "empty";
  const scopeNote = agg?.scopeLabel || (compareHeadToHeadOnly && compareOpponent ? `vs ${compareOpponent}` : "ทุกเกม");
  const fs = agg?.firstSlayer || { fsPct: 0, opPct: 0, samePct: 0, nonePct: 0, wrWhenFs: 0, fs: 0, op: 0, same: 0, none: 0 };

  const fmt2 = (n) => (Number.isFinite(n) ? n.toFixed(2) : "0.00");
  const fmt1 = (n) => (Number.isFinite(n) ? n.toFixed(1) : "0.0");
  const fmt0 = (n) => (Number.isFinite(n) ? Math.round(n).toString() : "0");

  const isGoldMetric = (k) => k === "gold";
  const fmtAvg = (n, k) => isGoldMetric(k) ? `${fmt1(n)}k` : fmt2(n);

  const [showTables, setShowTables] = useState(false);

  const sections = [
    { key: "objectives", title: "Objectives (Dragon + Slayer)", unit: "", hint: "จำนวน Dragon + Slayer สะสม (เฉลี่ย) ที่แต่ละช่วงเวลา" },
    { key: "tower", title: "Tower", unit: "", hint: "จำนวนป้อมสะสม (เฉลี่ย) ที่แต่ละช่วงเวลา" },
    { key: "gold", title: "Gold (k)", unit: "k", hint: "เงินรวมทีม (พัน) ที่แต่ละช่วงเวลา" },
  ];

  return (
    <GlassCard style={{ padding: "14px 18px", marginTop: 14 }} delay={2.6}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <SectionTitle icon={Clock} title="Early Game Macro" subtitle={`First Slayer / Objectives / Tower / Gold ที่ 4'·8'·12'·15' · ${scopeNote} · ${games} เกม`} />
        {games > 0 && (
          <BtnGlow icon={Layers} variant={showTables ? "primary" : "default"} onClick={() => setShowTables((v) => !v)} style={{ padding: "6px 12px", fontSize: 12 }}>
            {showTables ? "ซ่อนตาราง" : "ดูตาราง"}
          </BtnGlow>
        )}
      </div>

      {games === 0 ? (
        <div style={{ marginTop: 12, padding: 16, color: T.muted, fontSize: 12, border: `1px dashed ${T.border}`, borderRadius: 12, textAlign: "center" }}>
          {totalGames > 0
            ? `ไม่มี macro data ${scopeNote} (มี ${totalGames} เกมรวม — ปิด Head-to-Head Only เพื่อดูทั้งหมด)`
            : "ยังไม่มีข้อมูล macro (ต้องการคอลัมน์ 4'/8'/12'/15' ในชีท All_Scrim)"}
        </div>
      ) : (
        <>
          {scopeMode === "fallback" && (
            <div style={{ marginTop: 8, padding: "8px 12px", color: T.warn, fontSize: 12, border: `1px solid ${T.warn}33`, background: "rgba(255,209,102,0.08)", borderRadius: 10 }}>
              {scopeNote}
            </div>
          )}

          {Array.isArray(agg?.insights) && agg.insights.length > 0 && (
            <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
              <div style={{ color: T.muted, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 700 }}>
                สรุปประเด็นเด่น
              </div>
              {agg.insights.map((it, idx) => {
                const tone = it.tone === "good"
                  ? { border: "rgba(0,208,132,0.35)", bg: "rgba(0,208,132,0.10)", color: T.success }
                  : it.tone === "bad"
                    ? { border: "rgba(255,77,109,0.35)", bg: "rgba(255,77,109,0.10)", color: T.danger }
                    : { border: T.border, bg: "rgba(255,255,255,0.03)", color: T.muted };
                return (
                  <div key={`${it.title}-${idx}`} style={{ border: `1px solid ${tone.border}`, borderRadius: 10, background: tone.bg, padding: "9px 12px" }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: tone.color }}>{it.title}</div>
                    <div style={{ fontSize: 12, color: T.text, marginTop: 3 }}>{it.text}</div>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 10 }}>
            <FirstObjectiveBlock
              title="First Blood"
              hint="kill แรกในเกม"
              fsPct={agg?.firstBlood?.fsPct || 0}
              opPct={agg?.firstBlood?.opPct || 0}
              fsCount={agg?.firstBlood?.fs || 0}
              opCount={agg?.firstBlood?.op || 0}
              wrWhenFs={agg?.firstBlood?.wrWhenFs || 0}
            />
            <FirstObjectiveBlock
              title="First Abyssal"
              hint="abyssal dragon ตัวแรก"
              fsPct={agg?.firstAbyssal?.fsPct || 0}
              opPct={agg?.firstAbyssal?.opPct || 0}
              fsCount={agg?.firstAbyssal?.fs || 0}
              opCount={agg?.firstAbyssal?.op || 0}
              wrWhenFs={agg?.firstAbyssal?.wrWhenFs || 0}
            />
            <FirstObjectiveBlock
              title="First Slayer"
              hint="slayer ตัวแรก (ใช้ข้อมูล 4'/8'/12'/15')"
              fsPct={fs.fsPct}
              opPct={fs.opPct}
              fsCount={fs.fs}
              opCount={fs.op}
              wrWhenFs={fs.wrWhenFs}
              tieLabel={`เสมอ ${fmt1(fs.samePct)}% (${fs.same}) · ไม่มี ${fmt1(fs.nonePct)}% (${fs.none})`}
            />
          </div>

          {byOpponent.length > 0 && (
            <div style={{ marginTop: 12, border: `1px solid ${T.border}`, borderRadius: 12, background: "rgba(255,255,255,0.03)", padding: "12px 14px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                <div style={{ color: T.primaryLight, fontWeight: 800 }}>อัตราชนะแยกตามคู่แข่ง</div>
                <div style={{ color: T.muted, fontSize: 11 }}>เรียงตาม WR · ใช้ดูว่าทีมไหน early game ลำบาก</div>
              </div>
              <div style={{ marginTop: 8 }}>
                <MacroOpponentBars byOpponent={byOpponent} />
              </div>
              {showTables && (
                <div className="tp-scroll" style={{ overflowX: "auto", marginTop: 8 }}>
                  <table className="tp-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr>
                        {["Opponent", "Games", `${TEAM_SHORT} WR`, "First Blood %", "First Slayer %", "Δ Gold @4'", "Δ Tower @8'", "Δ Obj @12'", "Δ Obj @15'"].map((h) => (
                          <th key={h} style={{ padding: "9px 8px", whiteSpace: "nowrap", textAlign: "left", color: T.muted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", background: T.tintLg, borderBottom: `2px solid ${T.border}` }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {byOpponent.map((r) => (
                        <tr key={r.opponent}>
                          <td style={tdS}><span style={{ color: T.primaryLight, fontWeight: 800 }}>{r.opponent}</span></td>
                          <td style={tdS}>{r.games} ({r.wins}W)</td>
                          <td style={tdS}>
                            <span style={{ color: r.wr >= 60 ? T.success : r.wr >= 40 ? T.warn : T.danger, fontWeight: 700 }}>
                              {r.wr.toFixed(0)}%
                            </span>
                          </td>
                          <td style={tdS}>{r.firstBloodFsPct.toFixed(0)}%</td>
                          <td style={tdS}>{r.firstSlayerFsPct.toFixed(0)}%</td>
                          <td style={tdS}><DeltaCell value={+r.deltaGold4.toFixed(2)} /></td>
                          <td style={tdS}><DeltaCell value={+r.deltaTower8.toFixed(2)} /></td>
                          <td style={tdS}><DeltaCell value={+r.deltaObj12.toFixed(2)} /></td>
                          <td style={tdS}><DeltaCell value={+r.deltaObj15.toFixed(2)} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {sections.map((sec) => (
            <div key={sec.key} style={{ marginTop: 12, border: `1px solid ${T.border}`, borderRadius: 12, background: "rgba(255,255,255,0.03)", padding: "12px 14px" }}>
              <div style={{ color: T.primaryLight, fontWeight: 800, marginBottom: 8 }}>{sec.title}</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 10 }}>
                <MacroTimeSeriesChart byMark={agg?.byMark} metricKey={sec.key} unit={sec.unit} hint={sec.hint} />
                <MacroLeadSplitChart byMark={agg?.byMark} metricKey={sec.key} />
              </div>
              {showTables && (
                <div className="tp-scroll" style={{ overflowX: "auto", marginTop: 10 }}>
                  <table className="tp-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr>
                        {["Time", `${TEAM_SHORT} avg`, "OP avg", "Δ", `${TEAM_SHORT} lead%`, "Tie%", "OP lead%", `WR when ${TEAM_SHORT} lead`, "Games"].map((h) => (
                          <th key={h} style={{ padding: "9px 8px", whiteSpace: "nowrap", textAlign: "left", color: T.muted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", background: T.tintLg, borderBottom: `2px solid ${T.border}` }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {MARKS.map((t) => {
                        const m = agg?.byMark?.[t]?.[sec.key] || {};
                        return (
                          <tr key={t}>
                            <td style={tdS}><span style={{ color: T.primaryLight, fontWeight: 700 }}>{t}'</span></td>
                            <td style={tdS}>{fmtAvg(m.fsAvg, sec.key)}</td>
                            <td style={tdS}>{fmtAvg(m.opAvg, sec.key)}</td>
                            <td style={tdS}><DeltaCell value={+fmt2(m.delta)} /></td>
                            <td style={tdS}>{fmt1(m.fsLeadPct)}%</td>
                            <td style={tdS}>{fmt1(m.tiePct)}%</td>
                            <td style={tdS}>{fmt1(m.opLeadPct)}%</td>
                            <td style={tdS}>{fmt1(m.wrWhenFsLead)}%</td>
                            <td style={tdS}>{fmt0(m.sampled)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}

          <div style={{ marginTop: 10, color: T.muted, fontSize: 11 }}>
            หมายเหตุ: เส้น = ค่าเฉลี่ย FS vs คู่แข่ง · แท่ง = สัดส่วนใครนำ (จากคอลัมน์ AVT) · กด "ดูตาราง" เพื่อดูตัวเลขละเอียด (Δ, WR เมื่อ FS นำ)
          </div>
        </>
      )}
    </GlassCard>
  );
}

function FirstObjectiveBlock({ title, hint, fsPct, opPct, fsCount, opCount, wrWhenFs, tieLabel }) {
  const safe = (v) => (Number.isFinite(v) ? v : 0);
  const fs = safe(fsPct);
  const op = safe(opPct);
  const wr = safe(wrWhenFs);
  return (
    <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, background: "rgba(255,255,255,0.03)", padding: "12px 14px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
        <div style={{ color: T.primaryLight, fontWeight: 800, fontSize: 14 }}>{title}</div>
        <div style={{ color: T.muted, fontSize: 11 }}>{hint}</div>
      </div>
      <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: `${Math.max(8, fs)}fr ${Math.max(8, op)}fr`, gap: 4, height: 10, borderRadius: 999, overflow: "hidden", background: "rgba(255,255,255,0.08)" }}>
        <div style={{ background: "linear-gradient(90deg,#ff4d5f,#ff1026)" }} />
        <div style={{ background: "linear-gradient(90deg,#22d3ee,#0ea5e9)" }} />
      </div>
      <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 12 }}>
        <div>
          <div style={{ color: T.primaryLight, fontWeight: 900, fontSize: 18 }}>{fs.toFixed(0)}%</div>
          <div style={{ color: T.muted }}>{TEAM_SHORT} · {fsCount} เกม</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ color: "#62e8ff", fontWeight: 900, fontSize: 18 }}>{op.toFixed(0)}%</div>
          <div style={{ color: T.muted }}>OP · {opCount} เกม</div>
        </div>
      </div>
      <div style={{ marginTop: 6, color: wr >= 60 ? T.success : wr >= 40 ? T.warn : T.danger, fontSize: 12, fontWeight: 700 }}>
        WR เมื่อ FS ได้ก่อน: {wr.toFixed(0)}%
      </div>
      {tieLabel && <div style={{ marginTop: 3, color: T.muted, fontSize: 11 }}>{tieLabel}</div>}
    </div>
  );
}

function CoachHints({ items = [] }) {
  if (!items.length) return null;
  return (
    <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
      {items.map((item, idx) => {
        const tone = item.tone === "good"
          ? { border: "rgba(0,208,132,0.35)", bg: "rgba(0,208,132,0.10)", color: T.success }
          : item.tone === "warn"
            ? { border: "rgba(255,209,102,0.38)", bg: "rgba(255,209,102,0.10)", color: T.warn }
            : { border: "rgba(255,77,109,0.35)", bg: "rgba(255,77,109,0.10)", color: T.danger };
        return (
          <div key={`${item.title}-${idx}`} style={{ border: `1px solid ${tone.border}`, borderRadius: 10, background: tone.bg, padding: "9px 11px" }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: tone.color }}>{item.title}</div>
            <div style={{ fontSize: 12, color: T.text, marginTop: 3 }}>{item.text}</div>
          </div>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════ */
export default function TeamPerformancePage({ teamPerformance = null, comboStats = null, excelFile = null, games = [] }) {
  const navigate = useNavigate();
  // ป้ายบอกแหล่งข้อมูล: ใช้ชื่อไฟล์ที่อัปโหลดจริง ไม่ใช่ซีซั่นที่เขียนตายตัวไว้
  const sourceLabel = excelFile?.name
    ? String(excelFile.name).replace(/\.(xlsx|xls)$/i, "")
    : "ไฟล์ที่อัปโหลด";

  const [data, setData] = useState(teamPerformance);

  const [team, setTeam] = useState("ALL");
  const [role, setRole] = useState("ALL");
  const [bucket, setBucket] = useState("ALL");
  const [minGames, setMinGames] = useState(3);
  const [viewMode, setViewMode] = useState("delta");
  const [analysisMode, setAnalysisMode] = useState("with_scrim");
  const [inferredMode, setInferredMode] = useState("all");
  const [showInferBadge, setShowInferBadge] = useState(true);
  const [heroCellMode, setHeroCellMode] = useState("text");
  const [qualityConfig, setQualityConfig] = useState(() => {
    try {
      const saved = localStorage.getItem(QUALITY_GATE_KEY);
      if (saved) {
        const p = JSON.parse(saved);
        return {
          inferredWarnPct: Math.max(0, Number(p.inferredWarnPct) || DEFAULT_QUALITY_CONFIG.inferredWarnPct),
          minOfficialRowsPerTeam: Math.max(0, Number(p.minOfficialRowsPerTeam) || DEFAULT_QUALITY_CONFIG.minOfficialRowsPerTeam),
          minTeamRowsToCheckOfficial: Math.max(1, Number(p.minTeamRowsToCheckOfficial) || DEFAULT_QUALITY_CONFIG.minTeamRowsToCheckOfficial),
          minTotalRows: Math.max(1, Number(p.minTotalRows) || DEFAULT_QUALITY_CONFIG.minTotalRows),
        };
      }
    } catch { }
    return DEFAULT_QUALITY_CONFIG;
  });
  const [processConfig, setProcessConfig] = useState(() => {
    try {
      const saved = localStorage.getItem(PROCESS_STORAGE_KEY);
      if (saved) {
        const p = JSON.parse(saved);
        return {
          autoPriority: p.autoPriority !== false,
          seasonTag: String(p.seasonTag || DEFAULT_PROCESS_CONFIG.seasonTag),
          patchTag: String(p.patchTag || ""),
          patchStartDate: String(p.patchStartDate || ""),
          lastFeedbackAt: String(p.lastFeedbackAt || ""),
        };
      }
    } catch { }
    return DEFAULT_PROCESS_CONFIG;
  });
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [coachMode, setCoachMode] = useState(() => {
    try {
      return localStorage.getItem(COACH_MODE_KEY) !== "0";
    } catch {
      return true;
    }
  });
  const OUR_TEAM = "FS";
  const [compareOpponent, setCompareOpponent] = useState("");
  const [compareHeadToHeadOnly, setCompareHeadToHeadOnly] = useState(true);
  const [compareRole, setCompareRole] = useState("SUP");
  const [draftTeam, setDraftTeam] = useState("FS");
  const [simpleView, setSimpleView] = useState(() => {
    try {
      return localStorage.getItem("team_performance_simple_view_v1") !== "0";
    } catch {
      return true;
    }
  });
  const [uxPreset, setUxPreset] = useState(() => {
    try {
      return localStorage.getItem(UX_PRESET_KEY) || "coach";
    } catch {
      return "coach";
    }
  });
  const [earlyMacroRows, setEarlyMacroRows] = useState([]);
  const [fsReportRows, setFsReportRows] = useState([]);
  const [tierFsOneHero, setTierFsOneHero] = useState([]);
  const [activeTab, setActiveTab] = useState(() => {
    try { return localStorage.getItem("team_performance_active_tab_v1") || "overview"; } catch { return "overview"; }
  });
  const [roleMetric, setRoleMetric] = useState("gpm");
  useEffect(() => {
    try { localStorage.setItem("team_performance_active_tab_v1", activeTab); } catch { /* ignore */ }
  }, [activeTab]);
  const [tierFsCombos, setTierFsCombos] = useState({ combo2: [], combo3: [] });
  const [tierOpponentStats, setTierOpponentStats] = useState({ combo2: [], combo3: [], oneHero: [] });
  const [tierStatsLoading, setTierStatsLoading] = useState(false);
  const [tierStatsError, setTierStatsError] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");
  const [aiResult, setAiResult] = useState(null);
  const [aiUpdatedAt, setAiUpdatedAt] = useState("");
  const [coachQuestion, setCoachQuestion] = useState("");
  const [coachLoading, setCoachLoading] = useState(false);
  const [coachError, setCoachError] = useState("");
  const [coachReplies, setCoachReplies] = useState([]);

  /* ── persist filters ── */
  useEffect(() => {
    try {
      const saved = localStorage.getItem(FILTER_STORAGE_KEY);
      if (!saved) return;
      const p = JSON.parse(saved);
      if (p.team) setTeam(p.team);
      if (p.role) setRole(p.role);
      if (p.bucket) setBucket(p.bucket);
      if (p.viewMode) setViewMode(p.viewMode);
      if (p.analysisMode) setAnalysisMode(p.analysisMode);
      if (p.inferredMode) setInferredMode(p.inferredMode);
      if (typeof p.showInferBadge === "boolean") setShowInferBadge(p.showInferBadge);
      if (p.heroCellMode === "text" || p.heroCellMode === "image") setHeroCellMode(p.heroCellMode);
      if (p.dateFrom) setDateFrom(p.dateFrom);
      if (p.dateTo) setDateTo(p.dateTo);
      if (p.minGames) setMinGames(Math.max(1, Number(p.minGames) || 1));
    } catch (_) { }
  }, []);

  useEffect(() => {
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify({ team, role, bucket, minGames, viewMode, analysisMode, inferredMode, showInferBadge, heroCellMode, dateFrom, dateTo }));
  }, [team, role, bucket, minGames, viewMode, analysisMode, inferredMode, showInferBadge, heroCellMode, dateFrom, dateTo]);

  useEffect(() => {
    localStorage.setItem(COACH_MODE_KEY, coachMode ? "1" : "0");
  }, [coachMode]);
  useEffect(() => {
    localStorage.setItem(QUALITY_GATE_KEY, JSON.stringify(qualityConfig));
  }, [qualityConfig]);
  useEffect(() => {
    localStorage.setItem(PROCESS_STORAGE_KEY, JSON.stringify(processConfig));
  }, [processConfig]);
  useEffect(() => {
    localStorage.setItem("team_performance_simple_view_v1", simpleView ? "1" : "0");
  }, [simpleView]);
  useEffect(() => {
    localStorage.setItem(UX_PRESET_KEY, uxPreset);
  }, [uxPreset]);

  useEffect(() => { setData(teamPerformance || null); }, [teamPerformance]);

  /* ── derived data ── */
  const teams = useMemo(() => {
    const byCoverage = (data?.meta?.coverage?.byTeam || []).map((x) => x.team).filter(Boolean);
    const byRaw = [...new Set((data?.raw || []).map((r) => r.team).filter(Boolean))];
    const base = (byCoverage.length ? byCoverage : byRaw).filter((t) => !/_ban_pick$/i.test(String(t)));
    return ["ALL", ...base.sort()];
  }, [data]);

  useEffect(() => {
    if (team !== "ALL" && !teams.includes(team)) setTeam("ALL");
  }, [team, teams]);

  const roleByTeamHero = useMemo(() => {
    const map = new Map();
    (data?.agg?.rolePool || []).forEach(r => {
      if (r.bucket !== "official" && r.bucket !== "scrim") return;
      const key = `${r.team}|${r.hero}`;
      const cur = map.get(key);
      if (!cur || num(r.pick) > num(cur.pick)) map.set(key, r);
    });
    return map;
  }, [data]);

  const scopedRaw = useMemo(() => {
    return (data?.raw || [])
      .filter(r => team === "ALL" || r.team === team)
      .filter(r => inferredMode === "all" || (inferredMode === "only_inferred" ? isInferredType(r.type) : !isInferredType(r.type)))
      .filter(r => !dateFrom || String(r.date || "") >= dateFrom)
      .filter(r => !dateTo || String(r.date || "") <= dateTo);
  }, [data, team, inferredMode, dateFrom, dateTo]);

  const scopedStatRaw = useMemo(() => {
    return scopedRaw.filter(isCoreStatBucketRow);
  }, [scopedRaw]);

  const filteredRaw = useMemo(() => {
    if (bucket === "ALL") return scopedStatRaw;
    if (bucket === "other") return scopedRaw.filter((r) => String(r.bucket || "") === "other");
    return scopedStatRaw.filter((r) => r.bucket === bucket);
  }, [scopedRaw, scopedStatRaw, bucket]);

  const derived = useMemo(() => deriveTeamPerformanceFromRaw(filteredRaw, { includeOther: bucket === "ALL" || bucket === "other", minGamesForDelta: minGames }), [filteredRaw, bucket, minGames]);
  const analysisRaw = useMemo(() => {
    if (analysisMode === "official_only") return scopedStatRaw.filter(r => r.bucket === "official");
    return scopedStatRaw;
  }, [analysisMode, scopedStatRaw]);
  const analysisDerived = useMemo(
    () => deriveTeamPerformanceFromRaw(analysisRaw, { includeOther: analysisMode === "with_scrim", minGamesForDelta: minGames }),
    [analysisRaw, analysisMode, minGames]
  );

  const filteredDelta = useMemo(() => {
    return (derived?.delta || [])
      .map(r => { const ri = roleByTeamHero.get(`${r.team}|${r.hero}`); return { ...r, role: ri?.role || "-" }; })
      .filter(r => role === "ALL" || r.role === role)
      .sort((a, b) => Math.abs(num(b.deltaWinRate)) - Math.abs(num(a.deltaWinRate)));
  }, [derived, role, roleByTeamHero]);

  const analysisDelta = useMemo(() => {
    return (analysisMode === "with_scrim" ? (analysisDerived?.delta || []) : [])
      .map(r => { const ri = roleByTeamHero.get(`${r.team}|${r.hero}`); return { ...r, role: ri?.role || "-" }; })
      .filter(r => role === "ALL" || r.role === role)
      .sort((a, b) => Math.abs(num(b.deltaWinRate)) - Math.abs(num(a.deltaWinRate)));
  }, [analysisMode, analysisDerived, roleByTeamHero, role]);

  const priority = useMemo(() => summarizeTeamPriority(analysisDelta), [analysisDelta]);
  const teamGapSummary = useMemo(() => {
    const m = new Map();
    analysisDelta.forEach((r) => {
      const key = r.team || "UNKNOWN";
      const cur = m.get(key) || {
        team: key,
        heroes: 0,
        scrimWRSum: 0,
        officialWRSum: 0,
        deltaWRSum: 0,
        deltaPRSum: 0,
        deltaPresenceSum: 0,
        topUp: null,
        topDown: null,
      };
      cur.heroes += 1;
      cur.scrimWRSum += num(r.scrimWinRate);
      cur.officialWRSum += num(r.officialWinRate);
      cur.deltaWRSum += num(r.deltaWinRate);
      cur.deltaPRSum += num(r.deltaPickRate);
      cur.deltaPresenceSum += num(r.deltaPresenceRate);
      if (!cur.topUp || num(r.deltaWinRate) > num(cur.topUp.deltaWinRate)) cur.topUp = r;
      if (!cur.topDown || num(r.deltaWinRate) < num(cur.topDown.deltaWinRate)) cur.topDown = r;
      m.set(key, cur);
    });
    return [...m.values()]
      .map((x) => ({
        team: x.team,
        heroes: x.heroes,
        avgScrimWR: +(x.scrimWRSum / Math.max(1, x.heroes)).toFixed(2),
        avgOfficialWR: +(x.officialWRSum / Math.max(1, x.heroes)).toFixed(2),
        avgDeltaWR: +(x.deltaWRSum / Math.max(1, x.heroes)).toFixed(2),
        avgDeltaPR: +(x.deltaPRSum / Math.max(1, x.heroes)).toFixed(2),
        avgDeltaPresence: +(x.deltaPresenceSum / Math.max(1, x.heroes)).toFixed(2),
        topUpHero: x.topUp ? `${x.topUp.hero} (${num(x.topUp.deltaWinRate).toFixed(1)}%)` : "-",
        topDownHero: x.topDown ? `${x.topDown.hero} (${num(x.topDown.deltaWinRate).toFixed(1)}%)` : "-",
      }))
      .sort((a, b) => num(a.avgDeltaWR) - num(b.avgDeltaWR));
  }, [analysisDelta]);

  const roleRecs = useMemo(() => {
    const src = analysisDerived?.agg?.rolePool || [];
    return buildRolePoolRecommendations(src, { minPick: minGames })
      .filter(r => team === "ALL" || r.team === team)
      .filter(r => role === "ALL" || r.role === role);
  }, [analysisDerived, minGames, team, role]);

  /* ── Cross-Team Hero Comparison ── */
  const crossTeamData = useMemo(() => {
    const src = analysisDerived?.agg?.heroSummary || [];
    return buildCrossTeamHeroComparison(src, { minPick: Math.max(1, minGames) });
  }, [analysisDerived, minGames]);

  const [selectedHero, setSelectedHero] = useState("");
  const selectedCross = useMemo(() => {
    if (!selectedHero) return crossTeamData[0] || null;
    return crossTeamData.find(c => c.hero === selectedHero) || crossTeamData[0] || null;
  }, [crossTeamData, selectedHero]);
  const selectedCrossDeltaRows = useMemo(() => {
    if (!selectedCross) return [];
    return selectedCross.teams
      .map((t) => ({ name: t.team, delta: num(t.deltaWR), scrim: t.scrimWR, official: t.officialWR }))
      .sort((a, b) => b.delta - a.delta);
  }, [selectedCross]);
  /* ── Hero Meta by Position ── */
  const heroMeta = useMemo(() => {
    return buildHeroMetaByPosition(
      analysisDerived?.agg?.rolePool || [],
      analysisDelta,
      { minPick: Math.max(1, minGames) }
    );
  }, [analysisDerived, analysisDelta, minGames]);

  const stats = useMemo(() => {
    const rows = filteredDelta;
    const avgWinGap = rows.length ? rows.reduce((a, r) => a + num(r.deltaWinRate), 0) / rows.length : 0;
    const avgPickGap = rows.length ? rows.reduce((a, r) => a + num(r.deltaPickRate), 0) / rows.length : 0;
    return {
      heroCount: rows.length,
      avgWinGap, avgPickGap,
      rawRows: filteredRaw.length,
      scrimRows: filteredRaw.filter(r => r.bucket === "scrim").length,
      officialRows: filteredRaw.filter(r => r.bucket === "official").length,
    };
  }, [filteredDelta, filteredRaw]);

  const bySource = useMemo(() => {
    const m = new Map();
    filteredRaw.forEach(r => { const k = r.sourceKind || "unknown"; m.set(k, (m.get(k) || 0) + 1); });
    return [...m.entries()].map(([source, rows]) => ({ source, rows })).sort((a, b) => b.rows - a.rows);
  }, [filteredRaw]);
  const scopedSummary = useMemo(() => {
    const teams = new Set();
    let scrim = 0;
    let official = 0;
    let other = 0;
    scopedRaw.forEach((r) => {
      if (r.team) teams.add(r.team);
      if (r.bucket === "scrim") scrim += 1;
      else if (r.bucket === "official") official += 1;
      else other += 1;
    });
    return {
      teams: teams.size,
      rows: scopedRaw.length,
      scrim,
      official,
      other,
      deltaHeroes: analysisDelta.length,
    };
  }, [scopedRaw, analysisDelta.length]);

  const compareOpponentOptions = useMemo(() => {
    const set = new Set();
    (data?.raw || []).forEach((r) => {
      const t = normalizeTeamCode(r.team);
      if (t && t !== OUR_TEAM) set.add(t);
      const c = normalizeTeamCode(r.competitor);
      if (c && c !== OUR_TEAM) set.add(c);
    });
    return [...set].sort();
  }, [data, OUR_TEAM]);

  useEffect(() => {
    if (!compareOpponentOptions.length) {
      if (compareOpponent) setCompareOpponent("");
      return;
    }
    const preferred = compareOpponentOptions.includes("BRU")
      ? "BRU"
      : compareOpponentOptions[0];
    if (!compareOpponent || !compareOpponentOptions.includes(compareOpponent)) {
      setCompareOpponent(preferred);
    }
  }, [compareOpponent, compareOpponentOptions]);
  useEffect(() => {
    setTierFsCombos({
      combo2: comboStats?.combo2 || [],
      combo3: comboStats?.combo3 || [],
    });
  }, [comboStats]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!excelFile) {
        setTierFsOneHero([]);
        return;
      }
      try {
        const rows = await parseFsOneHeroFromRplPlayerStatsP(excelFile);
        if (!cancelled) setTierFsOneHero(Array.isArray(rows) ? rows : []);
      } catch {
        if (!cancelled) setTierFsOneHero([]);
      }
    })();
    return () => { cancelled = true; };
  }, [excelFile]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!excelFile) {
        setEarlyMacroRows([]);
        return;
      }
      try {
        const rows = await parseEarlyGameMacroP(excelFile);
        if (!cancelled) setEarlyMacroRows(Array.isArray(rows) ? rows : []);
      } catch {
        if (!cancelled) setEarlyMacroRows([]);
      }
    })();
    return () => { cancelled = true; };
  }, [excelFile]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!excelFile) {
        setFsReportRows([]);
        return;
      }
      try {
        const rows = await parseFsTeamReportP(excelFile);
        if (!cancelled) setFsReportRows(Array.isArray(rows) ? rows : []);
      } catch {
        if (!cancelled) setFsReportRows([]);
      }
    })();
    return () => { cancelled = true; };
  }, [excelFile]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!excelFile || !compareOpponent) {
        setTierOpponentStats({ combo2: [], combo3: [], oneHero: [] });
        setTierStatsError("");
        return;
      }
      setTierStatsLoading(true);
      setTierStatsError("");
      try {
        const oppRaw = await parseOpponentCombosForTeam(excelFile, compareOpponent);
        let normalized = normalizeOppToComboStats(oppRaw);
        if (!hasTierStats(normalized)) {
          const fallback = await parseWorkbookToComboStats(excelFile, {
            sheets: [compareOpponent],
            includeOneHero: true,
          });
          normalized = {
            combo2: fallback?.combo2 || [],
            combo3: fallback?.combo3 || [],
            oneHero: fallback?.oneHero || [],
          };
        }
        if (!cancelled) setTierOpponentStats(normalized);
      } catch (err) {
        if (!cancelled) {
          setTierOpponentStats({ combo2: [], combo3: [], oneHero: [] });
          setTierStatsError(err?.message || "โหลดสถิติแบบ TierData ไม่สำเร็จ");
        }
      } finally {
        if (!cancelled) setTierStatsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [excelFile, compareOpponent]);

  const compareBaseRows = useMemo(() => {
    const baseRows = (data?.raw || [])
      .filter((r) => inferredMode === "all" || (inferredMode === "only_inferred" ? isInferredType(r.type) : !isInferredType(r.type)))
      .filter((r) => !dateFrom || String(r.date || "") >= dateFrom)
      .filter((r) => !dateTo || String(r.date || "") <= dateTo);
    if (bucket === "other") {
      return baseRows.filter((r) => String(r.bucket || "") === "other");
    }
    return baseRows
      .filter((r) => isCoreStatBucketRow(r))
      .filter((r) => bucket === "ALL" || r.bucket === bucket);
  }, [data, inferredMode, dateFrom, dateTo, bucket]);

  const benchmarkBaseRows = useMemo(() => {
    const baseRows = (data?.raw || [])
      .filter((r) => inferredMode === "all" || (inferredMode === "only_inferred" ? isInferredType(r.type) : !isInferredType(r.type)))
      .filter((r) => !dateFrom || String(r.date || "") >= dateFrom)
      .filter((r) => !dateTo || String(r.date || "") <= dateTo);
    if (bucket === "other") return [];
    let rows = baseRows
      .filter((r) => isCoreStatBucketRow(r))
      .filter((r) => bucket === "ALL" || r.bucket === bucket);
    if (analysisMode === "official_only") {
      rows = rows.filter((r) => r.bucket === "official");
    }
    return rows;
  }, [data, inferredMode, dateFrom, dateTo, bucket, analysisMode]);

  const latestScopedDate = useMemo(() => {
    const dates = compareBaseRows
      .map((r) => String(r.date || ""))
      .filter((d) => /^\d{8}$/.test(d))
      .sort((a, b) => b.localeCompare(a));
    return dates[0] || "";
  }, [compareBaseRows]);

  const patchScopedRows = useMemo(() => {
    const start = String(processConfig.patchStartDate || "");
    if (!start || !/^\d{8}$/.test(start)) return [];
    let rows = compareBaseRows.filter((r) => String(r.date || "") >= start);
    if (analysisMode === "official_only") {
      rows = rows.filter((r) => r.bucket === "official");
    }
    return rows;
  }, [compareBaseRows, processConfig.patchStartDate, analysisMode]);

  const patchDerived = useMemo(() => {
    if (!patchScopedRows.length) return { agg: { rolePool: [] }, delta: [] };
    return deriveTeamPerformanceFromRaw(patchScopedRows, {
      includeOther: analysisMode === "with_scrim",
      minGamesForDelta: minGames,
    });
  }, [patchScopedRows, analysisMode, minGames]);

  const patchMetaByRole = useMemo(() => {
    return buildHeroMetaByPosition(
      patchDerived?.agg?.rolePool || [],
      patchDerived?.delta || [],
      { minPick: Math.max(1, minGames) }
    );
  }, [patchDerived, minGames]);

  const patchRoleRecs = useMemo(() => {
    return buildRolePoolRecommendations(patchDerived?.agg?.rolePool || [], { minPick: Math.max(1, minGames) });
  }, [patchDerived, minGames]);

  const patchFeedbackRows = useMemo(() => {
    const rows = [];
    const roleOrder = ["DSL", "JUG", "MID", "ADL", "SUP"];
    roleOrder.forEach((pos) => {
      const top = (patchMetaByRole?.[pos] || [])[0];
      if (!top) {
        rows.push({ role: pos, focus: "เติมข้อมูล", note: "ยังไม่มี sample หลังแพตช์พอสำหรับ role นี้" });
        return;
      }
      rows.push({
        role: pos,
        focus: top.hero,
        note: `${top.status} · WR ${num(top.wr).toFixed(1)}% · PR ${num(top.pr).toFixed(1)}%${top.recommendation ? ` · ${top.recommendation}` : ""}`,
      });
    });
    return rows;
  }, [patchMetaByRole]);

  const processChecklist = useMemo(() => {
    const totalTeams = (data?.meta?.coverage?.byTeam || []).filter((t) => !/_ban_pick$/i.test(String(t.team || ""))).length;
    const teamsWithRows = new Set(compareBaseRows.map((r) => r.team).filter(Boolean)).size;
    const hasAllTeams = totalTeams === 0 ? teamsWithRows > 0 : teamsWithRows >= totalTeams;
    const modeReady = compareBaseRows.some((r) => r.bucket === "scrim") && compareBaseRows.some((r) => r.bucket === "official");
    const patchReady = !!processConfig.patchTag && /^\d{8}$/.test(processConfig.patchStartDate) && patchScopedRows.length > 0;
    const roleMetaReady = ["DSL", "JUG", "MID", "ADL", "SUP"].every((pos) => (patchMetaByRole?.[pos] || []).length > 0);
    const feedbackReady = patchFeedbackRows.length >= 5;
    const staleByDate = !!latestScopedDate && !!processConfig.lastFeedbackAt && latestScopedDate > processConfig.lastFeedbackAt;
    return {
      staleByDate,
      items: [
        { key: "teams", label: "Ingest ทุกทีม", ok: hasAllTeams, detail: `${teamsWithRows}/${Math.max(totalTeams, teamsWithRows)} ทีม` },
        { key: "mode", label: "พร้อมทั้งรวม/ไม่รวมผลซ้อม", ok: modeReady, detail: modeReady ? "มีทั้ง scrim และ official" : "ขาด bucket หนึ่ง" },
        { key: "patch", label: "Patch ล่าสุดถูก sync", ok: patchReady, detail: patchReady ? `${processConfig.patchTag} (${patchScopedRows.length} rows)` : "ยังไม่ครบ patch tag/date" },
        { key: "meta", label: "Hero meta+pool ครบ 5 ตำแหน่ง", ok: roleMetaReady, detail: roleMetaReady ? "ครบทุก role" : "บาง role ยังไม่มี sample" },
        { key: "feedback", label: "Feedback พร้อมนำไปซ้อม", ok: feedbackReady, detail: `${patchFeedbackRows.length} รายการ` },
      ],
    };
  }, [compareBaseRows, data, patchScopedRows.length, processConfig.patchTag, processConfig.patchStartDate, processConfig.lastFeedbackAt, patchMetaByRole, patchFeedbackRows.length, latestScopedDate]);

  const leagueRoleBenchmark = useMemo(() => {
    const targetBucket = analysisMode === "official_only" ? "official" : (bucket === "ALL" ? "official" : bucket);
    return buildLeagueRoleBenchmarks(benchmarkBaseRows, { bucket: targetBucket, minSamples: Math.max(1, minGames) });
  }, [benchmarkBaseRows, analysisMode, bucket, minGames]);

  const benchmarkRowsView = useMemo(() => {
    return (leagueRoleBenchmark?.roleRows || [])
      .filter((r) => team === "ALL" || r.team === team)
      .filter((r) => role === "ALL" || r.role === role)
      .sort((a, b) => num(a.deltaDpm) - num(b.deltaDpm));
  }, [leagueRoleBenchmark, team, role]);

  const firstBloodView = useMemo(() => {
    return (leagueRoleBenchmark?.teamFirstBlood || [])
      .filter((r) => team === "ALL" || r.team === team)
      .sort((a, b) => num(a.firstBloodRate) - num(b.firstBloodRate));
  }, [leagueRoleBenchmark, team]);

  const compareRows = useMemo(() => {
    if (!compareOpponent) return [];
    return compareBaseRows.filter((r) => {
      const teamCode = normalizeTeamCode(r.team);
      if (teamCode !== OUR_TEAM && teamCode !== compareOpponent) return false;
      if (!compareHeadToHeadOnly) return true;
      const opponentCode = normalizeTeamCode(r.competitor);
      return (
        (teamCode === OUR_TEAM && opponentCode === compareOpponent) ||
        (teamCode === compareOpponent && opponentCode === OUR_TEAM)
      );
    });
  }, [compareBaseRows, compareOpponent, compareHeadToHeadOnly, OUR_TEAM]);

  const draftPreference = useMemo(() => {
    return buildTeamDraftPreferences(compareBaseRows, { minGames: Math.max(1, minGames) });
  }, [compareBaseRows, minGames]);

  const draftTeamOptions = useMemo(() => {
    const base = draftPreference?.teams || [];
    if (team !== "ALL" && base.includes(team)) return [team];
    return base;
  }, [draftPreference, team]);

  useEffect(() => {
    if (!draftTeamOptions.length) {
      if (draftTeam) setDraftTeam("");
      return;
    }
    const preferred = team !== "ALL" && draftTeamOptions.includes(team)
      ? team
      : (draftTeamOptions.includes("FS") ? "FS" : draftTeamOptions[0]);
    if (!draftTeam || !draftTeamOptions.includes(draftTeam)) {
      setDraftTeam(preferred);
    }
  }, [draftTeamOptions, draftTeam, team]);

  const selectedDraftTeam = useMemo(() => draftPreference?.byTeam?.[draftTeam] || null, [draftPreference, draftTeam]);
  const selectedOpponentPref = useMemo(() => {
    if (!compareOpponent) return null;
    return draftPreference?.byOpponent?.[compareOpponent] || null;
  }, [draftPreference, compareOpponent]);
  const tierFsOneHeroRows = useMemo(() => {
    const parsed = (tierFsOneHero || [])
      .map((h) => {
        const total = num(h.total);
        const win = num(h.win);
        return {
          hero: h.hero || h.name || "-",
          role: (h.position || "-").toUpperCase(),
          total,
          win,
          lose: Math.max(0, total - win),
          wr: num(h.winrate) || pct(win, total),
        };
      });
    if (parsed.length > 0) {
      return parsed.sort((a, b) => num(b.total) - num(a.total) || num(b.wr) - num(a.wr));
    }
    return buildOneHeroRowsFromGames(games);
  }, [tierFsOneHero, games]);
  const tierFsComboRows = useMemo(() => {
    const parsed = mapComboRows(tierFsCombos);
    if (parsed.length > 0) return parsed;
    return buildComboRowsFromRaw(compareBaseRows, OUR_TEAM);
  }, [tierFsCombos, compareBaseRows, OUR_TEAM]);
  const tierOpponentOneHeroRows = useMemo(() => {
    return (tierOpponentStats?.oneHero || [])
      .map((h) => {
        const total = num(h.total);
        const win = num(h.win);
        return {
          hero: h.hero || "-",
          role: (h.position || "-").toUpperCase(),
          total,
          win,
          lose: Math.max(0, total - win),
          wr: num(h.winrate) || pct(win, total),
        };
      })
      .sort((a, b) => num(b.total) - num(a.total) || num(b.wr) - num(a.wr));
  }, [tierOpponentStats]);
  const tierOpponentComboRows = useMemo(() => mapComboRows(tierOpponentStats), [tierOpponentStats]);

  const compareSummary = useMemo(() => {
    const mk = (teamCode) => {
      const rows = compareRows.filter((r) => normalizeTeamCode(r.team) === teamCode);
      const games = rows.length;
      const wins = rows.reduce((a, r) => a + num(r.win), 0);
      const scrim = rows.filter((r) => r.bucket === "scrim");
      const official = rows.filter((r) => r.bucket === "official");
      const heroSet = new Set();
      rows.forEach((r) => (r.picks || []).forEach((h) => heroSet.add(h)));
      const scrimWins = scrim.reduce((a, r) => a + num(r.win), 0);
      const officialWins = official.reduce((a, r) => a + num(r.win), 0);
      return {
        team: teamCode,
        games,
        wins,
        wr: pct(wins, games),
        scrimGames: scrim.length,
        scrimWR: pct(scrimWins, scrim.length),
        officialGames: official.length,
        officialWR: pct(officialWins, official.length),
        uniqueHeroes: heroSet.size,
      };
    };

    const derivedCompare = deriveTeamPerformanceFromRaw(compareRows, {
      includeOther: bucket === "ALL" || bucket === "other",
      minGamesForDelta: minGames,
    });
    const deltaMap = new Map();
    (derivedCompare?.delta || []).forEach((r) => {
      const k = normalizeTeamCode(r.team);
      const cur = deltaMap.get(k) || { sum: 0, n: 0 };
      cur.sum += num(r.deltaWinRate);
      cur.n += 1;
      deltaMap.set(k, cur);
    });

    const fs = mk(OUR_TEAM);
    const op = mk(compareOpponent || "-");
    const fsDelta = deltaMap.get(OUR_TEAM);
    const opDelta = deltaMap.get(compareOpponent);
    return {
      fs: { ...fs, avgDeltaWR: fsDelta?.n ? +(fsDelta.sum / fsDelta.n).toFixed(2) : 0, deltaHeroes: fsDelta?.n || 0 },
      opponent: { ...op, avgDeltaWR: opDelta?.n ? +(opDelta.sum / opDelta.n).toFixed(2) : 0, deltaHeroes: opDelta?.n || 0 },
    };
  }, [compareRows, compareOpponent, bucket, minGames, OUR_TEAM]);
  const roleDuel = useMemo(() => {
    const summarizeRole = (teamCode) => {
      const rows = compareRows.filter((r) => normalizeTeamCode(r.team) === teamCode);
      let games = 0;
      let gpmSum = 0;
      let gpmN = 0;
      let killSum = 0;
      let killN = 0;
      let deathSum = 0;
      let deathN = 0;
      let assistSum = 0;
      let assistN = 0;

      rows.forEach((r) => {
        const rs = r.roleStats || {};
        const stat = rs?.[compareRole];
        if (!stat) return;
        games += 1;
        const kill = num(stat.kill);
        const death = num(stat.death);
        const assist = num(stat.assist);
        const gpm = num(stat.gpm);
        if (Number.isFinite(gpm)) {
          gpmSum += gpm;
          gpmN += 1;
        }
        if (Number.isFinite(kill)) {
          killSum += kill;
          killN += 1;
        }
        if (Number.isFinite(death)) {
          deathSum += death;
          deathN += 1;
        }
        if (Number.isFinite(assist)) {
          assistSum += assist;
          assistN += 1;
        }
      });

      return {
        games,
        gpm: gpmN > 0 ? +(gpmSum / gpmN).toFixed(2) : null,
        kill: killN > 0 ? +(killSum / killN).toFixed(2) : null,
        death: deathN > 0 ? +(deathSum / deathN).toFixed(2) : null,
        assist: assistN > 0 ? +(assistSum / assistN).toFixed(2) : null,
      };
    };

    const left = summarizeRole(OUR_TEAM);
    const right = summarizeRole(compareOpponent || "");
    const kdaOf = (s) => (
      s.kill != null && s.death != null && s.assist != null
        ? +(((s.kill + s.assist) / Math.max(1, s.death)).toFixed(2))
        : null
    );
    const avgKdaLeft = kdaOf(left);
    const avgKdaRight = kdaOf(right);
    const metrics = [
      { key: "mvpTimes", label: "MVP TIMES", left: null, right: null, suffix: "", missingNote: "ไม่มีข้อมูลในชีท" },
      { key: "kp", label: "%KP", left: null, right: null, suffix: "%", missingNote: "ไม่มีข้อมูลในชีท" },
      { key: "avgMvp", label: "AVG MVP", left: null, right: null, suffix: "", missingNote: "ไม่มีข้อมูลในชีท" },
      { key: "avgKda", label: "AVG KDA", left: avgKdaLeft, right: avgKdaRight, suffix: "", missingNote: "ต้องมี kill/death/assist ในชีท" },
      { key: "goldPerMin", label: "GOLD / MIN", left: left.gpm, right: right.gpm, suffix: "", missingNote: "ต้องมี GPM ในชีท" },
    ];
    return { left, right, metrics };
  }, [compareRows, compareRole, compareOpponent, OUR_TEAM]);
  const roleDuelRadarData = useMemo(() => {
    const rows = roleDuel.metrics.map((m) => ({
      metric: m.label,
      left: m.left,
      right: m.right,
    })).map((m) => ({
      metric: m.metric,
      left: m.left == null ? 0 : num(m.left),
      right: m.right == null ? 0 : num(m.right),
    }));
    const points = rows.map((r) => {
      const maxV = Math.max(1, r.left, r.right);
      return {
        metric: r.metric,
        fs: +((r.left * 100) / maxV).toFixed(2),
        opponent: +((r.right * 100) / maxV).toFixed(2),
      };
    });
    const hasData = roleDuel.metrics.some((m) => m.left != null || m.right != null);
    return { points, hasData };
  }, [roleDuel]);
  const compareRadarData = useMemo(() => {
    const fs = compareSummary?.fs || {};
    const op = compareSummary?.opponent || {};
    const heroMax = Math.max(1, num(fs.uniqueHeroes), num(op.uniqueHeroes));
    const deltaToScore = (v) => clamp(50 + num(v) * 5, 0, 100);
    return [
      { metric: "WinRate", fs: num(fs.wr), opponent: num(op.wr) },
      { metric: "Scrim WR", fs: num(fs.scrimWR), opponent: num(op.scrimWR) },
      { metric: "Match WR", fs: num(fs.officialWR), opponent: num(op.officialWR) },
      { metric: "Hero Pool", fs: +((num(fs.uniqueHeroes) * 100) / heroMax).toFixed(2), opponent: +((num(op.uniqueHeroes) * 100) / heroMax).toFixed(2) },
      { metric: "Delta Form", fs: deltaToScore(fs.avgDeltaWR), opponent: deltaToScore(op.avgDeltaWR) },
    ];
  }, [compareSummary]);

  const fsVsAllSummary = useMemo(() => {
    const summarize = (rows, teamCode) => {
      const teamRows = rows.filter((r) => normalizeTeamCode(r.team) === teamCode);
      const games = teamRows.length;
      const wins = teamRows.reduce((a, r) => a + num(r.win), 0);
      const scrimRows = teamRows.filter((r) => r.bucket === "scrim");
      const officialRows = teamRows.filter((r) => r.bucket === "official");
      const scrimWins = scrimRows.reduce((a, r) => a + num(r.win), 0);
      const officialWins = officialRows.reduce((a, r) => a + num(r.win), 0);
      return {
        games,
        wr: pct(wins, games),
        scrimGames: scrimRows.length,
        scrimWR: pct(scrimWins, scrimRows.length),
        officialGames: officialRows.length,
        officialWR: pct(officialWins, officialRows.length),
      };
    };

    const rows = [];
    compareOpponentOptions.forEach((op) => {
      const scoped = compareBaseRows.filter((r) => {
        const teamCode = normalizeTeamCode(r.team);
        if (teamCode !== OUR_TEAM && teamCode !== op) return false;
        if (!compareHeadToHeadOnly) return true;
        const opponentCode = normalizeTeamCode(r.competitor);
        return (
          (teamCode === OUR_TEAM && opponentCode === op) ||
          (teamCode === op && opponentCode === OUR_TEAM)
        );
      });

      const fs = summarize(scoped, OUR_TEAM);
      const opp = summarize(scoped, op);
      if (fs.games === 0 && opp.games === 0) return;

      rows.push({
        opponent: op,
        fsGames: fs.games,
        fsWR: fs.wr,
        fsScrimWR: fs.scrimWR,
        fsOfficialWR: fs.officialWR,
        oppGames: opp.games,
        oppWR: opp.wr,
        oppScrimWR: opp.scrimWR,
        oppOfficialWR: opp.officialWR,
        wrGap: +(fs.wr - opp.wr).toFixed(2),
      });
    });

    return rows.sort((a, b) => num(b.wrGap) - num(a.wrGap));
  }, [compareBaseRows, compareOpponentOptions, compareHeadToHeadOnly, OUR_TEAM]);

  const earlyMacroAgg = useMemo(() => {
    if (!earlyMacroRows.length) {
      return { ...aggregateEarlyGameMacro([]), scopeMode: "empty", scopeLabel: "", totalGames: 0 };
    }
    const totalGames = earlyMacroRows.length;
    const wantsH2H = compareHeadToHeadOnly && compareOpponent;
    if (wantsH2H) {
      const filtered = earlyMacroRows.filter(
        (g) => String(g.competitor || "").toUpperCase() === String(compareOpponent || "").toUpperCase()
      );
      if (filtered.length > 0) {
        return { ...aggregateEarlyGameMacro(filtered), scopeMode: "h2h", scopeLabel: `vs ${compareOpponent}`, totalGames };
      }
      return {
        ...aggregateEarlyGameMacro(earlyMacroRows),
        scopeMode: "fallback",
        scopeLabel: `ไม่มี macro data vs ${compareOpponent} · กำลังแสดงทุกเกม`,
        totalGames,
      };
    }
    return { ...aggregateEarlyGameMacro(earlyMacroRows), scopeMode: "all", scopeLabel: "ทุกเกม", totalGames };
  }, [earlyMacroRows, compareOpponent, compareHeadToHeadOnly]);

  const earlyMacroByOpp = useMemo(
    () => aggregateEarlyMacroByOpponent(earlyMacroRows || []),
    [earlyMacroRows]
  );

  // FS team report (per-role economy/combat/damage + side/length/trend),
  // scoped the same way as earlyMacroAgg so the global filter bar stays
  // meaningful on the Overview/Roles tabs.
  // The team overview is always the full season (all opponents) so it
  // matches the Home page headline, which reads the same All_Scrim sheet. The
  // opponent toggle (compareHeadToHeadOnly) scopes the dedicated comparison and
  // Early-Game-Macro tools below — NOT this overview.
  const fsReport = useMemo(() => {
    const rows = fsReportRows || [];
    if (!rows.length) {
      return { ...aggregateFsTeamReport([]), scopeMode: "empty", scopeLabel: "", totalGames: 0 };
    }
    return { ...aggregateFsTeamReport(rows), scopeMode: "all", scopeLabel: "ทุกเกม", totalGames: rows.length };
  }, [fsReportRows]);

  const fsOverview = useMemo(() => {
    const wins = (fsReport.buckets?.scrim?.wins || 0) + (fsReport.buckets?.official?.wins || 0);
    const g = fsReport.games || 0;
    return { wins, games: g, wr: g ? +((wins / g) * 100).toFixed(1) : 0 };
  }, [fsReport]);

  const heroStatGapRows = useMemo(() => {
    const perBucket = new Map();
    const rows = scopedStatRaw;
    rows.forEach((r) => {
      const bucketKey = r.bucket === "official" ? "official" : (r.bucket === "scrim" ? "scrim" : "");
      if (!bucketKey) return;
      const teamCode = normalizeTeamCode(r.team);
      const picks = Array.isArray(r.picks) ? r.picks : [];
      const positions = Array.isArray(r.positions) ? r.positions : [];
      const roleStats = r.roleStats || {};
      for (let i = 0; i < Math.min(picks.length, positions.length, 5); i++) {
        const hero = String(picks[i] || "").trim();
        const role = normalizeRoleKey(positions[i]);
        if (!hero || !role) continue;
        const stat = roleStats?.[role];
        if (!stat) continue;
        const key = `${teamCode}|${hero}|${role}|${bucketKey}`;
        const cur = perBucket.get(key) || {
          team: teamCode,
          hero,
          role,
          bucket: bucketKey,
          games: 0,
          wins: 0,
          gpmSum: 0, gpmN: 0,
          dpmSum: 0, dpmN: 0,
          killSum: 0, killN: 0,
          deathSum: 0, deathN: 0,
          assistSum: 0, assistN: 0,
        };
        cur.games += 1;
        cur.wins += num(r.win);
        if (Number.isFinite(stat.gpm)) { cur.gpmSum += Number(stat.gpm); cur.gpmN += 1; }
        if (Number.isFinite(stat.dpm)) { cur.dpmSum += Number(stat.dpm); cur.dpmN += 1; }
        if (Number.isFinite(stat.kill)) { cur.killSum += Number(stat.kill); cur.killN += 1; }
        if (Number.isFinite(stat.death)) { cur.deathSum += Number(stat.death); cur.deathN += 1; }
        if (Number.isFinite(stat.assist)) { cur.assistSum += Number(stat.assist); cur.assistN += 1; }
        perBucket.set(key, cur);
      }
    });

    const joined = new Map();
    perBucket.forEach((v) => {
      const baseKey = `${v.team}|${v.hero}|${v.role}`;
      const cur = joined.get(baseKey) || { team: v.team, hero: v.hero, role: v.role, scrim: null, official: null };
      cur[v.bucket] = v;
      joined.set(baseKey, cur);
    });

    const toAvg = (v) => {
      if (!v) return null;
      const kill = v.killN > 0 ? v.killSum / v.killN : null;
      const death = v.deathN > 0 ? v.deathSum / v.deathN : null;
      const assist = v.assistN > 0 ? v.assistSum / v.assistN : null;
      return {
        games: v.games,
        wr: pct(v.wins, v.games),
        gpm: v.gpmN > 0 ? +(v.gpmSum / v.gpmN).toFixed(2) : null,
        dpm: v.dpmN > 0 ? +(v.dpmSum / v.dpmN).toFixed(2) : null,
        kill: kill != null ? +kill.toFixed(2) : null,
        death: death != null ? +death.toFixed(2) : null,
        assist: assist != null ? +assist.toFixed(2) : null,
        kda: (kill != null && death != null && assist != null) ? +(((kill + assist) / Math.max(1, death)).toFixed(2)) : null,
      };
    };

    return [...joined.values()]
      .filter((x) => x.scrim && x.official)
      .map((x) => {
        const s = toAvg(x.scrim);
        const o = toAvg(x.official);
        return {
          team: x.team,
          hero: x.hero,
          role: x.role,
          scrimGames: s?.games || 0,
          officialGames: o?.games || 0,
          scrimWR: s?.wr ?? null,
          officialWR: o?.wr ?? null,
          deltaWR: (s?.wr != null && o?.wr != null) ? +(o.wr - s.wr).toFixed(2) : null,
          scrimGpm: s?.gpm ?? null,
          officialGpm: o?.gpm ?? null,
          deltaGpm: (s?.gpm != null && o?.gpm != null) ? +(o.gpm - s.gpm).toFixed(2) : null,
          scrimDpm: s?.dpm ?? null,
          officialDpm: o?.dpm ?? null,
          deltaDpm: (s?.dpm != null && o?.dpm != null) ? +(o.dpm - s.dpm).toFixed(2) : null,
          scrimKda: s?.kda ?? null,
          officialKda: o?.kda ?? null,
          deltaKda: (s?.kda != null && o?.kda != null) ? +(o.kda - s.kda).toFixed(2) : null,
        };
      })
      .filter((r) => team === "ALL" || r.team === normalizeTeamCode(team))
      .filter((r) => role === "ALL" || r.role === role)
      .filter((r) => r.scrimGames >= Math.max(1, minGames) || r.officialGames >= Math.max(1, minGames))
      .sort((a, b) => Math.abs(num(b.deltaDpm)) - Math.abs(num(a.deltaDpm)));
  }, [scopedStatRaw, team, role, minGames]);

  const fsOpponentHeroGap = useMemo(() => {
    const fsRows = heroStatGapRows.filter((r) => normalizeTeamCode(r.team) === OUR_TEAM);
    const opRows = heroStatGapRows.filter((r) => normalizeTeamCode(r.team) === compareOpponent);
    const avg = (arr, key) => {
      if (!arr.length) return 0;
      return +(arr.reduce((a, r) => a + num(r[key]), 0) / arr.length).toFixed(2);
    };
    return {
      fsCount: fsRows.length,
      opCount: opRows.length,
      fsAvgDeltaDpm: avg(fsRows, "deltaDpm"),
      opAvgDeltaDpm: avg(opRows, "deltaDpm"),
      fsAvgDeltaGpm: avg(fsRows, "deltaGpm"),
      opAvgDeltaGpm: avg(opRows, "deltaGpm"),
      fsAvgDeltaKda: avg(fsRows, "deltaKda"),
      opAvgDeltaKda: avg(opRows, "deltaKda"),
    };
  }, [heroStatGapRows, OUR_TEAM, compareOpponent]);

  const coachHints = useMemo(() => {
    const sortedDelta = [...analysisDelta].sort((a, b) => Math.abs(num(b.deltaWinRate)) - Math.abs(num(a.deltaWinRate)));
    const improving = sortedDelta.filter((r) => num(r.deltaWinRate) > 0);
    const dropping = sortedDelta.filter((r) => num(r.deltaWinRate) < 0).sort((a, b) => num(a.deltaWinRate) - num(b.deltaWinRate));

    const coverageTotal = Math.max(1, stats.rawRows);
    const scrimRatio = (stats.scrimRows * 100) / coverageTotal;
    const officialRatio = (stats.officialRows * 100) / coverageTotal;
    const topPriority = [...(priority || [])].sort((a, b) => (num(b.trainMore) - num(b.stageReady)) - (num(a.trainMore) - num(a.stageReady)))[0];
    const topReady = [...(priority || [])].sort((a, b) => (num(b.stageReady) - num(b.trainMore)) - (num(a.stageReady) - num(a.trainMore)))[0];

    const quadrant = [];
    if (dropping[0]) {
      quadrant.push({
        tone: "risk",
        title: "เร่งแก้ Hero ที่ตกหนักสุด",
        text: `${dropping[0].team} - ${dropping[0].hero} ΔWR ${num(dropping[0].deltaWinRate).toFixed(1)}%: จัดซ้อมเฉพาะ matchup และ objective timing ภายใน 2 เซสชัน`,
      });
    }
    if (improving[0]) {
      quadrant.push({
        tone: "good",
        title: "ล็อก Hero ฟอร์มดีเป็น Core Pick",
        text: `${improving[0].team} - ${improving[0].hero} ΔWR +${num(improving[0].deltaWinRate).toFixed(1)}%: เก็บเป็นแผน A และซ้อมแผน B ป้องกันโดนแบน`,
      });
    }

    const winrate = [];
    if (dropping[0]) {
      winrate.push({
        tone: "warn",
        title: "Scrim ดีแต่ Stage ตก",
        text: `${dropping[0].hero} (${dropping[0].team}) Scrim ${num(dropping[0].scrimWinRate).toFixed(1)}% -> Match ${num(dropping[0].officialWinRate).toFixed(1)}%: เพิ่ม stress drill ช่วงนาที 10-15`,
      });
    }
    if (improving[0]) {
      winrate.push({
        tone: "good",
        title: "Hero ที่ใช้บนเวทีได้ผล",
        text: `${improving[0].hero} (${improving[0].team}) ควรเพิ่มน้ำหนักใน draft รอบแรก และเตรียมคู่คอมโบสำรอง`,
      });
    }

    const coverage = [];
    if (officialRatio < 30) {
      coverage.push({
        tone: "warn",
        title: "ข้อมูลแข่งจริงน้อยไป",
        text: `Match Day มีเพียง ${officialRatio.toFixed(0)}% ของข้อมูลทั้งหมด: ยังไม่ควรล็อก conclusion ระยะยาว`,
      });
    } else {
      coverage.push({
        tone: "good",
        title: "ฐานข้อมูลแข่งจริงพอใช้ตัดสินใจ",
        text: `Match Day ${officialRatio.toFixed(0)}%: ใช้จัด priority แผนซ้อมรายสัปดาห์ได้`,
      });
    }
    if (scrimRatio > 65) {
      coverage.push({
        tone: "warn",
        title: "น้ำหนัก Scrim สูงมาก",
        text: `Scrim ${scrimRatio.toFixed(0)}%: ควรแยก KPI ซ้อมกับแข่งจริงเพื่อลด bias`,
      });
    }

    const teamPriorityHints = [];
    if (topPriority) {
      teamPriorityHints.push({
        tone: "risk",
        title: `ทีมที่ต้องรีบาลานซ์มากสุด: ${topPriority.team}`,
        text: `Train More ${topPriority.trainMore} vs Stage Ready ${topPriority.stageReady}: โฟกัสแก้ 2 hero ที่พังบ่อยก่อน`,
      });
    }
    if (topReady) {
      teamPriorityHints.push({
        tone: "good",
        title: `ทีมที่พร้อมเป็นต้นแบบ: ${topReady.team}`,
        text: `Stage Ready ${topReady.stageReady}: นำ pattern draft/shotcall ไปทำเป็น playbook ทีมอื่น`,
      });
    }

    return { quadrant, winrate, coverage, teamPriority: teamPriorityHints };
  }, [analysisDelta, stats, priority]);

  const actionPlanRows = useMemo(() => {
    const rolePoolOfficial = (analysisDerived?.agg?.rolePool || []).filter((r) => r.bucket === "official");
    const roleMap = new Map();
    rolePoolOfficial.forEach((r) => {
      const key = `${r.team}|${r.role}`;
      const cur = roleMap.get(key) || { team: r.team, role: r.role, heroes: new Set(), totalPick: 0, wrSum: 0, wrN: 0 };
      cur.heroes.add(r.hero);
      cur.totalPick += num(r.pick);
      cur.wrSum += num(r.winRate) * Math.max(1, num(r.pick));
      cur.wrN += Math.max(1, num(r.pick));
      roleMap.set(key, cur);
    });

    const benchmarkMap = new Map();
    (leagueRoleBenchmark?.roleRows || []).forEach((r) => {
      benchmarkMap.set(`${r.team}|${r.role}`, r);
    });
    const fbMap = new Map();
    (leagueRoleBenchmark?.teamFirstBlood || []).forEach((r) => {
      fbMap.set(r.team, r);
    });

    const byTeam = new Map();
    analysisDelta.forEach((r) => {
      const teamKey = r.team || "UNKNOWN";
      const cur = byTeam.get(teamKey) || { team: teamKey, rows: [] };
      cur.rows.push(r);
      byTeam.set(teamKey, cur);
    });

    const out = [];
    const teamKeys = new Set([
      ...[...byTeam.keys()],
      ...[...roleMap.keys()].map((k) => k.split("|")[0]),
    ]);

    [...teamKeys].forEach((teamCode) => {
      const teamRows = byTeam.get(teamCode)?.rows || [];
      const risk = [...teamRows].sort((a, b) => num(a.deltaWinRate) - num(b.deltaWinRate))[0];
      const ready = [...teamRows].sort((a, b) => num(b.deltaWinRate) - num(a.deltaWinRate))[0];
      const draftTeamInfo = draftPreference?.byTeam?.[teamCode];
      const firstBlood = fbMap.get(teamCode);
      const topBanHeavy = (draftTeamInfo?.heroes || [])
        .filter((h) => num(h.banRate) >= 30)
        .slice(0, 3);

      if (risk && num(risk.deltaWinRate) <= -6) {
        out.push({
          team: teamCode,
          role: risk.role || "-",
          priority: "High",
          focus: "Stabilize Hero",
          action: `ซ้อม ${risk.hero} เน้น matchup เดิมและ objective timing`,
          metric: `ΔWR ${num(risk.deltaWinRate).toFixed(1)}%`,
        });
      }
      if (ready && num(ready.deltaWinRate) >= 6) {
        out.push({
          team: teamCode,
          role: ready.role || "-",
          priority: "Medium",
          focus: "Leverage Hero",
          action: `เพิ่มน้ำหนัก draft ของ ${ready.hero} และเตรียม hero สำรอง`,
          metric: `ΔWR +${num(ready.deltaWinRate).toFixed(1)}%`,
        });
      }

      if (firstBlood && num(firstBlood.deltaFirstBloodRate) <= -8) {
        out.push({
          team: teamCode,
          role: "ALL",
          priority: "High",
          focus: "Early Game Control",
          action: `First Blood ต่ำกว่าลีกชัดเจน: เพิ่ม block ซ้อม pathing + invade/cover นาที 0-3`,
          metric: `ΔFB ${num(firstBlood.deltaFirstBloodRate).toFixed(1)}%`,
        });
      }

      if (topBanHeavy.length >= 2 && (draftTeamInfo?.games || 0) >= Math.max(3, minGames)) {
        out.push({
          team: teamCode,
          role: "ALL",
          priority: "Medium",
          focus: "Ban Pressure Risk",
          action: `Hero โดนแบนหนัก (${topBanHeavy.map((h) => h.hero).join(", ")}): เตรียมแผน B/C เพิ่ม`,
          metric: `${topBanHeavy.length} ban-heavy heroes`,
        });
      }

      ["DSL", "JUG", "MID", "ADL", "SUP"].forEach((pos) => {
        const roleInfo = roleMap.get(`${teamCode}|${pos}`);
        const bm = benchmarkMap.get(`${teamCode}|${pos}`);
        if (bm && (num(bm.deltaDpm) <= -250 || num(bm.deltaGpm) <= -25 || num(bm.deltaKda) <= -0.4)) {
          out.push({
            team: teamCode,
            role: pos,
            priority: "High",
            focus: "Close League Gap",
            action: `แก้ performance gap ตำแหน่ง ${pos}: เพิ่ม lane drill + damage conversion + objective setup`,
            metric: `ΔDPM ${num(bm.deltaDpm).toFixed(0)} / ΔGPM ${num(bm.deltaGpm).toFixed(1)} / ΔKDA ${num(bm.deltaKda).toFixed(2)}`,
          });
        }

        if (!roleInfo) {
          out.push({
            team: teamCode,
            role: pos,
            priority: "High",
            focus: "Expand Pool",
            action: `ยังไม่มีข้อมูลตำแหน่ง ${pos}: เติม hero pool และบันทึกซ้อม`,
            metric: "0 heroes",
          });
          return;
        }
        const heroCount = roleInfo.heroes.size;
        const avgWR = roleInfo.wrN > 0 ? roleInfo.wrSum / roleInfo.wrN : 0;
        if (heroCount <= 1) {
          out.push({
            team: teamCode,
            role: pos,
            priority: "High",
            focus: "Expand Pool",
            action: `ตำแหน่ง ${pos} มี hero ใช้งานน้อย: เพิ่มอย่างน้อยอีก 2 ตัว`,
            metric: `${heroCount} heroes`,
          });
        } else if (avgWR < 45) {
          out.push({
            team: teamCode,
            role: pos,
            priority: "Medium",
            focus: "Raise Role WR",
            action: `ตำแหน่ง ${pos} WR ต่ำ: จัด block ซ้อมแก้ lane phase และ fight setup`,
            metric: `WR ${avgWR.toFixed(1)}%`,
          });
        }
      });
    });

    const filtered = out.filter((r) => role === "ALL" || r.role === role);
    const score = { High: 3, Medium: 2, Low: 1 };
    const deduped = [];
    const seen = new Set();
    filtered.forEach((r) => {
      const k = `${r.team}|${r.role}|${r.focus}`;
      if (seen.has(k)) return;
      seen.add(k);
      deduped.push(r);
    });
    return deduped
      .sort((a, b) => (score[b.priority] - score[a.priority]) || a.team.localeCompare(b.team) || a.role.localeCompare(b.role))
      .slice(0, 80);
  }, [analysisDerived, analysisDelta, role, leagueRoleBenchmark, draftPreference, minGames]);

  const heroDimensionRows = useMemo(() => {
    const heroByBucket = new Map();
    (analysisDerived?.agg?.heroSummary || []).forEach((h) => {
      const key = `${h.team}|${h.hero}|${h.bucket}`;
      heroByBucket.set(key, h);
    });

    const roleBenchmarkMap = new Map();
    (leagueRoleBenchmark?.roleRows || []).forEach((r) => {
      roleBenchmarkMap.set(`${r.team}|${r.role}`, r);
    });

    const inferredByTeam = new Map();
    const totalByTeam = new Map();
    scopedRaw.forEach((r) => {
      const t = r.team || "UNKNOWN";
      totalByTeam.set(t, (totalByTeam.get(t) || 0) + 1);
      if (isInferredType(r.type)) inferredByTeam.set(t, (inferredByTeam.get(t) || 0) + 1);
    });

    const rows = (analysisMode === "with_scrim" ? analysisDelta : [])
      .map((d) => {
        const team = d.team;
        const hero = d.hero;
        const roleCode = d.role || roleByTeamHero.get(`${team}|${hero}`)?.role || "-";
        const scrimHero = heroByBucket.get(`${team}|${hero}|scrim`) || null;
        const officialHero = heroByBucket.get(`${team}|${hero}|official`) || null;
        const roleBm = roleBenchmarkMap.get(`${team}|${roleCode}`) || null;

        const totalRows = totalByTeam.get(team) || 0;
        const inferredRows = inferredByTeam.get(team) || 0;
        const inferredRatio = totalRows > 0 ? (inferredRows * 100) / totalRows : 0;

        const deltaWR = num(d.deltaWinRate);
        const deltaPR = num(d.deltaPickRate);
        const deltaPresence = num(d.deltaPresenceRate);
        const officialWR = num(d.officialWinRate);
        const officialPR = num(d.officialPickRate);
        const officialPresence = num(d.officialPresenceRate);
        const officialBanRate = officialHero ? num(officialHero.banRate) : 0;
        const scrimBanRate = scrimHero ? num(scrimHero.banRate) : 0;
        const scrimPick = num(d.scrimPick);
        const officialPick = num(d.officialPick);
        const sampleScore = clamp(((scrimPick + officialPick) / 20) * 100);

        const executionScore = clamp(50 + (officialWR - 50) * 2.1 + deltaWR * 2.2);
        const usageScore = clamp(officialPR * 1.8 + officialPresence * 0.35 + Math.max(0, deltaPR) * 1.5);
        const pressureScore = clamp(officialBanRate * 1.9 + officialPresence * 0.4 + Math.max(0, officialWR - 50) * 1.3);
        const volatility = Math.abs(deltaWR) + Math.abs(deltaPR) * 0.6 + Math.abs(deltaPresence) * 0.35;
        const stabilityScore = clamp(100 - volatility * 3.2 - inferredRatio * 0.7 + sampleScore * 0.24);
        const macroPenalty = roleBm
          ? Math.max(0, -num(roleBm.deltaDpm) / 30) + Math.max(0, -num(roleBm.deltaGpm) / 5) + Math.max(0, -num(roleBm.deltaKda) * 16)
          : 8;
        const macroScore = clamp(100 - macroPenalty * 3.2);

        const composite = clamp(
          executionScore * 0.3 +
          usageScore * 0.2 +
          pressureScore * 0.15 +
          stabilityScore * 0.2 +
          macroScore * 0.15
        );

        const trainPriority = clamp((60 - executionScore) * 0.6 + (55 - stabilityScore) * 0.5 + Math.max(0, -deltaWR) * 1.6 + macroPenalty);
        const status = composite >= 70
          ? "Core"
          : composite >= 55
            ? "Playable"
            : "Train";

        return {
          team,
          hero,
          role: roleCode,
          scrimWR: num(d.scrimWinRate),
          officialWR,
          deltaWR,
          scrimPR: num(d.scrimPickRate),
          officialPR,
          deltaPR,
          scrimPresence: num(d.scrimPresenceRate),
          officialPresence,
          deltaPresence,
          scrimBanRate,
          officialBanRate,
          scrimPick,
          officialPick,
          sampleScore: +sampleScore.toFixed(1),
          executionScore: +executionScore.toFixed(1),
          usageScore: +usageScore.toFixed(1),
          pressureScore: +pressureScore.toFixed(1),
          stabilityScore: +stabilityScore.toFixed(1),
          macroScore: +macroScore.toFixed(1),
          composite: +composite.toFixed(1),
          trainPriority: +trainPriority.toFixed(1),
          status,
        };
      })
      .filter((r) => role === "ALL" || r.role === role)
      .sort((a, b) => b.trainPriority - a.trainPriority || b.composite - a.composite);

    return rows;
  }, [analysisDerived, analysisMode, analysisDelta, leagueRoleBenchmark, role, roleByTeamHero, scopedRaw]);

  const heroDimensionSummary = useMemo(() => {
    const rows = heroDimensionRows;
    if (!rows.length) {
      return { rows: 0, core: 0, playable: 0, train: 0, avgComposite: 0, avgTrainPriority: 0 };
    }
    const core = rows.filter((r) => r.status === "Core").length;
    const playable = rows.filter((r) => r.status === "Playable").length;
    const train = rows.filter((r) => r.status === "Train").length;
    const avgComposite = rows.reduce((a, r) => a + num(r.composite), 0) / rows.length;
    const avgTrainPriority = rows.reduce((a, r) => a + num(r.trainPriority), 0) / rows.length;
    return {
      rows: rows.length,
      core,
      playable,
      train,
      avgComposite: +avgComposite.toFixed(1),
      avgTrainPriority: +avgTrainPriority.toFixed(1),
    };
  }, [heroDimensionRows]);

  const heroPoolHealthRows = useMemo(() => {
    const rolePoolOfficial = (analysisDerived?.agg?.rolePool || []).filter((r) => r.bucket === "official");
    const map = new Map();
    rolePoolOfficial.forEach((r) => {
      const k = `${r.team}|${r.role}`;
      const cur = map.get(k) || { team: r.team, role: r.role, heroes: new Set(), picks: 0, core: 0, weak: 0 };
      cur.heroes.add(r.hero);
      cur.picks += num(r.pick);
      if (num(r.winRate) >= 52 && num(r.pick) >= Math.max(2, minGames)) cur.core += 1;
      if (num(r.winRate) < 45 && num(r.pick) >= Math.max(1, Math.floor(minGames / 2))) cur.weak += 1;
      map.set(k, cur);
    });

    return [...map.values()]
      .filter((r) => team === "ALL" || r.team === team)
      .filter((r) => role === "ALL" || r.role === role)
      .map((r) => {
        const depth = r.heroes.size;
        let status = "Healthy";
        let color = T.success;
        if (depth <= 1 || r.core === 0) { status = "Fragile"; color = T.danger; }
        else if (depth <= 2 || r.weak >= 2) { status = "Watch"; color = T.warn; }
        return {
          ...r,
          depth,
          status,
          statusColor: color,
        };
      })
      .sort((a, b) => (a.status === "Fragile" ? -1 : a.status === "Watch" ? 0 : 1) - (b.status === "Fragile" ? -1 : b.status === "Watch" ? 0 : 1) || a.team.localeCompare(b.team) || a.role.localeCompare(b.role))
      .slice(0, 24);
  }, [analysisDerived, team, role, minGames]);

  /* ── handlers ── */
  const handleMarkProcessFeedback = useCallback(() => {
    const markDate = latestScopedDate || new Date().toISOString().slice(0, 10).replace(/-/g, "");
    setProcessConfig((prev) => ({ ...prev, lastFeedbackAt: markDate }));
  }, [latestScopedDate]);

  const handleResetFilters = () => { setTeam("ALL"); setRole("ALL"); setBucket("ALL"); setMinGames(3); setViewMode("delta"); setInferredMode("all"); setShowInferBadge(true); setHeroCellMode("text"); setDateFrom(""); setDateTo(""); };

  const handleExportDelta = () => {
    const cols = [{ key: "team", label: "team" }, { key: "hero", label: "hero" }, { key: "role", label: "role" }, { key: "scrimWinRate", label: "scrim_win_rate" }, { key: "officialWinRate", label: "official_win_rate" }, { key: "deltaWinRate", label: "delta_win_rate" }, { key: "scrimPickRate", label: "scrim_pick_rate" }, { key: "officialPickRate", label: "official_pick_rate" }, { key: "deltaPickRate", label: "delta_pick_rate" }, { key: "deltaPresenceRate", label: "delta_presence_rate" }, { key: "scrimGames", label: "scrim_games" }, { key: "officialGames", label: "official_games" }];
    downloadCSV("team_performance_delta.csv", toCSV(filteredDelta, cols));
  };

  const handleExportRaw = () => {
    const cols = [{ key: "date", label: "date" }, { key: "team", label: "team" }, { key: "competitor", label: "opponent" }, { key: "bucket", label: "bucket" }, { key: "result", label: "result" }, { key: "type", label: "type" }, { key: "sourceKind", label: "source_kind" }, { key: "sourceSheet", label: "source_sheet" }, { key: "picks", label: "picks" }, { key: "bans", label: "bans" }];
    const rows = filteredRaw.map(r => ({ ...r, picks: (r.picks || []).join(" | "), bans: (r.bans || []).join(" | ") }));
    downloadCSV("team_performance_raw.csv", toCSV(rows, cols));
  };
  const qualityRows = useMemo(() => {
    const inferredBySheet = new Map();
    (data?.raw || []).forEach((r) => {
      if (!isInferredType(r.type)) return;
      const k = r.sourceSheet || r.sheet || "-";
      inferredBySheet.set(k, (inferredBySheet.get(k) || 0) + 1);
    });
    return (data?.meta?.parseStats || []).map((p) => ({
      sheet: p.sheet,
      parsed_rows: p.parsedRows || 0,
      dropped_rows: p.droppedRows || 0,
      inferred_rows: inferredBySheet.get(p.sheet) || 0,
      header_row: p.headerRow ?? -1,
    }));
  }, [data]);

  const inferredModeLabel = useMemo(() => {
    if (inferredMode === "only_inferred") return "Only inferred";
    if (inferredMode === "exclude_inferred") return "Exclude inferred";
    return "All";
  }, [inferredMode]);
  const isOnlyInferredMode = inferredMode === "only_inferred";
  const qualityGate = useMemo(() => {
    const raw = data?.raw || [];
    const totalRows = raw.length;
    const inferredRows = raw.filter((r) => isInferredType(r.type)).length;
    const inferredRatio = totalRows > 0 ? (inferredRows * 100) / totalRows : 0;
    const byTeam = data?.meta?.coverage?.byTeam || [];
    const lowOfficialTeams = byTeam
      .filter((t) => (t.total || 0) >= qualityConfig.minTeamRowsToCheckOfficial && (t.official || 0) < qualityConfig.minOfficialRowsPerTeam)
      .map((t) => `${t.team} (official ${t.official}/${t.total})`);

    const issues = [];
    if (totalRows > 0 && inferredRatio > qualityConfig.inferredWarnPct) {
      issues.push(`Inferred สูง ${inferredRows}/${totalRows} (${inferredRatio.toFixed(1)}%) เกินเกณฑ์ ${qualityConfig.inferredWarnPct}%`);
    }
    if (lowOfficialTeams.length > 0) {
      issues.push(`ทีมที่ official ต่ำ: ${lowOfficialTeams.slice(0, 5).join(", ")}${lowOfficialTeams.length > 5 ? " ..." : ""}`);
    }
    if (totalRows > 0 && totalRows < qualityConfig.minTotalRows) {
      issues.push(`จำนวนแถวรวมยังน้อย (${totalRows} rows, เกณฑ์ขั้นต่ำ ${qualityConfig.minTotalRows})`);
    }

    return {
      totalRows,
      inferredRows,
      inferredRatio: +inferredRatio.toFixed(2),
      lowOfficialTeams,
      issues,
      isWarn: issues.length > 0,
    };
  }, [data, qualityConfig]);
  const executiveSummary = useMemo(() => {
    const rows = analysisDelta || [];
    const highRisk = rows.filter((r) => num(r.deltaWinRate) <= -6);
    const stageReady = rows.filter((r) => num(r.deltaWinRate) >= 6);
    const topRisk = [...highRisk].sort((a, b) => num(a.deltaWinRate) - num(b.deltaWinRate))[0] || null;
    const topReady = [...stageReady].sort((a, b) => num(b.deltaWinRate) - num(a.deltaWinRate))[0] || null;
    const coverageScore = qualityConfig.minTotalRows > 0
      ? clamp((num(stats.rawRows) * 100) / qualityConfig.minTotalRows, 0, 100)
      : 100;
    const confidenceScore = clamp(
      coverageScore
      - num(qualityGate.inferredRatio) * 1.2
      - (qualityGate.issues.length * 12)
      + (num(stats.officialRows) > 0 ? 10 : 0),
      0,
      100
    );
    const confidenceTone = confidenceScore >= 75 ? "good" : confidenceScore >= 55 ? "warn" : "risk";
    return {
      avgDeltaWR: rows.length ? +(rows.reduce((a, r) => a + num(r.deltaWinRate), 0) / rows.length).toFixed(2) : 0,
      highRiskCount: highRisk.length,
      stageReadyCount: stageReady.length,
      confidenceScore: +confidenceScore.toFixed(1),
      confidenceTone,
      topRisk,
      topReady,
      planHighCount: actionPlanRows.filter((r) => r.priority === "High").length,
    };
  }, [analysisDelta, qualityConfig.minTotalRows, qualityGate.inferredRatio, qualityGate.issues.length, stats.rawRows, stats.officialRows, actionPlanRows]);

  const aiTopRisks = useMemo(() => (
    [...analysisDelta]
      .filter((r) => num(r.deltaWinRate) <= -3)
      .sort((a, b) => num(a.deltaWinRate) - num(b.deltaWinRate))
      .slice(0, 8)
      .map((r) => ({
        team: r.team,
        hero: r.hero,
        role: r.role,
        deltaWinRate: +num(r.deltaWinRate).toFixed(2),
        scrimWinRate: +num(r.scrimWinRate).toFixed(2),
        officialWinRate: +num(r.officialWinRate).toFixed(2),
        deltaPickRate: +num(r.deltaPickRate).toFixed(2),
      }))
  ), [analysisDelta]);
  const aiTopStageReady = useMemo(() => (
    [...analysisDelta]
      .filter((r) => num(r.deltaWinRate) >= 3)
      .sort((a, b) => num(b.deltaWinRate) - num(a.deltaWinRate))
      .slice(0, 8)
      .map((r) => ({
        team: r.team,
        hero: r.hero,
        role: r.role,
        deltaWinRate: +num(r.deltaWinRate).toFixed(2),
        scrimWinRate: +num(r.scrimWinRate).toFixed(2),
        officialWinRate: +num(r.officialWinRate).toFixed(2),
        deltaPickRate: +num(r.deltaPickRate).toFixed(2),
      }))
  ), [analysisDelta]);
  const aiTopActions = useMemo(() => (
    actionPlanRows
      .filter((r) => r.priority === "High")
      .slice(0, 10)
      .map((r) => ({
        team: r.team,
        role: r.role,
        priority: r.priority,
        focus: r.focus,
        action: r.action,
        metric: r.metric,
      }))
  ), [actionPlanRows]);

  const commandWindowStats = useMemo(() => {
    const validRows = (compareBaseRows || [])
      .filter((r) => /^\d{8}$/.test(String(r.date || "")))
      .map((r) => ({ ...r, _date: String(r.date) }))
      .sort((a, b) => a._date.localeCompare(b._date));
    if (!validRows.length) {
      return {
        latestDate: "",
        rolling30: { games: 0, wr: 0, wins: 0 },
        prev30: { games: 0, wr: 0, wins: 0 },
        rolling14: { games: 0, wr: 0, wins: 0 },
        diff30: { games: 0, wr: 0 },
        dailyTrend: [],
        worstDaysByWeekday: [],
        belowTargetStreaks: [],
      };
    }
    const latestDate = validRows[validRows.length - 1]._date;
    const latestDt = ymdToDateUTC(latestDate);
    if (!latestDt) {
      return {
        latestDate: "",
        rolling30: { games: 0, wr: 0, wins: 0 },
        prev30: { games: 0, wr: 0, wins: 0 },
        rolling14: { games: 0, wr: 0, wins: 0 },
        diff30: { games: 0, wr: 0 },
        dailyTrend: [],
        worstDaysByWeekday: [],
        belowTargetStreaks: [],
      };
    }
    const makeWindow = (daysBackFrom, daysBackTo) => {
      const from = new Date(latestDt);
      const to = new Date(latestDt);
      from.setUTCDate(from.getUTCDate() - daysBackFrom);
      to.setUTCDate(to.getUTCDate() - daysBackTo);
      const fromYmd = dateToYmdUTC(from);
      const toYmd = dateToYmdUTC(to);
      const rows = validRows.filter((r) => r._date >= fromYmd && r._date <= toYmd);
      const games = rows.length;
      const wins = rows.reduce((a, r) => a + num(r.win), 0);
      return { games, wins, wr: pct(wins, games), from: fromYmd, to: toYmd };
    };
    const rolling30 = makeWindow(29, 0);
    const prev30 = makeWindow(59, 30);
    const rolling14 = makeWindow(13, 0);
    const byDate = new Map();
    validRows.forEach((r) => {
      const cur = byDate.get(r._date) || { date: r._date, games: 0, wins: 0 };
      cur.games += 1;
      cur.wins += num(r.win);
      byDate.set(r._date, cur);
    });
    const dailyTrend = [...byDate.values()]
      .map((r) => ({ date: r.date, games: r.games, wins: r.wins, wr: pct(r.wins, r.games) }))
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(-60);

    const weekdayMap = new Map();
    [...byDate.values()].forEach((r) => {
      const dt = ymdToDateUTC(r.date);
      if (!dt) return;
      const day = dt.toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" });
      const cur = weekdayMap.get(day) || { day, games: 0, wins: 0 };
      cur.games += r.games;
      cur.wins += r.wins;
      weekdayMap.set(day, cur);
    });
    const worstDaysByWeekday = [...weekdayMap.values()]
      .map((r) => ({ day: r.day, games: r.games, wr: pct(r.wins, r.games) }))
      .filter((r) => r.games >= 3)
      .sort((a, b) => num(a.wr) - num(b.wr))
      .slice(0, 4);

    const teamDate = new Map();
    validRows.forEach((r) => {
      const key = `${normalizeTeamCode(r.team)}|${r._date}`;
      const cur = teamDate.get(key) || { team: normalizeTeamCode(r.team), date: r._date, games: 0, wins: 0 };
      cur.games += 1;
      cur.wins += num(r.win);
      teamDate.set(key, cur);
    });
    const grouped = new Map();
    [...teamDate.values()].forEach((r) => {
      const arr = grouped.get(r.team) || [];
      arr.push({ ...r, wr: pct(r.wins, r.games) });
      grouped.set(r.team, arr);
    });
    const belowTargetStreaks = [];
    grouped.forEach((rows, teamCode) => {
      const sorted = [...rows].sort((a, b) => a.date.localeCompare(b.date));
      let start = null;
      let end = null;
      let games = 0;
      const flush = () => {
        if (!start || !end) return;
        const startDt = ymdToDateUTC(start);
        const endDt = ymdToDateUTC(end);
        if (!startDt || !endDt) return;
        const spanDays = Math.floor((endDt.getTime() - startDt.getTime()) / 86400000) + 1;
        if (spanDays >= 14) {
          belowTargetStreaks.push({ team: teamCode, start, end, spanDays, games });
        }
      };
      sorted.forEach((r) => {
        if (num(r.wr) < 50) {
          if (!start) start = r.date;
          end = r.date;
          games += r.games;
        } else {
          flush();
          start = null;
          end = null;
          games = 0;
        }
      });
      flush();
    });

    return {
      latestDate,
      rolling30,
      prev30,
      rolling14,
      diff30: {
        wr: +(num(rolling30.wr) - num(prev30.wr)).toFixed(2),
        games: num(rolling30.games) - num(prev30.games),
      },
      dailyTrend,
      worstDaysByWeekday,
      belowTargetStreaks: belowTargetStreaks.sort((a, b) => num(b.spanDays) - num(a.spanDays)).slice(0, 8),
    };
  }, [compareBaseRows]);

  const monthOverMonthKpi = useMemo(() => {
    const validRows = (compareBaseRows || [])
      .filter((r) => /^\d{8}$/.test(String(r.date || "")))
      .map((r) => ({ ...r, _date: String(r.date) }));
    const byMonth = new Map();
    validRows.forEach((r) => {
      const month = ymdMonthKey(r._date);
      if (!month) return;
      const cur = byMonth.get(month) || {
        month,
        games: 0,
        wins: 0,
        scrimGames: 0,
        scrimWins: 0,
        officialGames: 0,
        officialWins: 0,
        firstBloodGames: 0,
        firstBloodWins: 0,
        heroes: new Set(),
      };
      cur.games += 1;
      cur.wins += num(r.win);
      if (r.bucket === "scrim") {
        cur.scrimGames += 1;
        cur.scrimWins += num(r.win);
      }
      if (r.bucket === "official") {
        cur.officialGames += 1;
        cur.officialWins += num(r.win);
      }
      if (r.firstBloodWon === true || r.firstBloodWon === false) {
        cur.firstBloodGames += 1;
        cur.firstBloodWins += r.firstBloodWon ? 1 : 0;
      }
      (r.picks || []).forEach((h) => cur.heroes.add(String(h || "").trim()));
      byMonth.set(month, cur);
    });
    const months = [...byMonth.keys()].sort();
    const toSnapshot = (m) => {
      if (!m) return null;
      return {
        month: m.month,
        games: m.games,
        wr: pct(m.wins, m.games),
        scrimWR: pct(m.scrimWins, m.scrimGames),
        officialWR: pct(m.officialWins, m.officialGames),
        firstBloodRate: pct(m.firstBloodWins, m.firstBloodGames),
        heroPool: m.heroes.size,
      };
    };
    const cur = toSnapshot(byMonth.get(months[months.length - 1]));
    const prev = toSnapshot(byMonth.get(months[months.length - 2]));
    const delta = !cur || !prev ? null : {
      wr: +(num(cur.wr) - num(prev.wr)).toFixed(2),
      scrimWR: +(num(cur.scrimWR) - num(prev.scrimWR)).toFixed(2),
      officialWR: +(num(cur.officialWR) - num(prev.officialWR)).toFixed(2),
      firstBloodRate: +(num(cur.firstBloodRate) - num(prev.firstBloodRate)).toFixed(2),
      heroPool: num(cur.heroPool) - num(prev.heroPool),
      games: num(cur.games) - num(prev.games),
    };
    const history = months.slice(-6).map((m) => toSnapshot(byMonth.get(m))).filter(Boolean);
    return { current: cur, previous: prev, delta, history };
  }, [compareBaseRows]);

  const forecastSnapshot = useMemo(() => {
    const latestYmd = commandWindowStats.latestDate;
    const latestDt = ymdToDateUTC(latestYmd);
    if (!latestDt) {
      return {
        targetWR: 55,
        currentMonth: null,
        projectedMonthEndWR: null,
        expectedRemainingGames: 0,
        chanceToHitTargetPct: 0,
        method: "insufficient-data",
      };
    }
    const monthStart = `${latestYmd.slice(0, 6)}01`;
    const currentMonthRows = (compareBaseRows || []).filter((r) => {
      const d = String(r.date || "");
      return /^\d{8}$/.test(d) && d >= monthStart && d <= latestYmd;
    });
    const currentGames = currentMonthRows.length;
    const currentWins = currentMonthRows.reduce((a, r) => a + num(r.win), 0);
    const currentWR = pct(currentWins, currentGames);
    const daysInMonth = new Date(Date.UTC(latestDt.getUTCFullYear(), latestDt.getUTCMonth() + 1, 0)).getUTCDate();
    const dayOfMonth = latestDt.getUTCDate();
    const daysLeft = Math.max(0, daysInMonth - dayOfMonth);
    const recent14GamesPerDay = num(commandWindowStats.rolling14.games) / 14;
    const expectedRemainingGames = Math.max(0, Math.round(recent14GamesPerDay * daysLeft));
    const recent14WR = num(commandWindowStats.rolling14.wr);
    const projectedWins = currentWins + (expectedRemainingGames * (recent14WR / 100));
    const projectedGames = currentGames + expectedRemainingGames;
    const projectedMonthEndWR = projectedGames > 0 ? +((projectedWins * 100) / projectedGames).toFixed(2) : null;
    const targetWR = 55;
    const chanceToHitTargetPct = projectedMonthEndWR == null
      ? 0
      : +clamp(50 + (projectedMonthEndWR - targetWR) * 4, 5, 95).toFixed(1);
    return {
      targetWR,
      currentMonth: { month: latestYmd.slice(0, 6), games: currentGames, wr: +currentWR.toFixed(2) },
      projectedMonthEndWR,
      expectedRemainingGames,
      chanceToHitTargetPct,
      method: "blend-current-month-with-rolling14-pace",
    };
  }, [commandWindowStats, compareBaseRows]);

  const performerTierSummary = useMemo(() => {
    const rows = [...(teamGapSummary || [])].sort((a, b) => num(b.avgDeltaWR) - num(a.avgDeltaWR));
    if (!rows.length) return { top: [], mid: [], low: [], counts: { top: 0, mid: 0, low: 0 } };
    const topCut = Math.ceil(rows.length / 3);
    const lowCut = Math.floor((rows.length * 2) / 3);
    const top = rows.slice(0, topCut).map((r) => ({ team: r.team, avgDeltaWR: r.avgDeltaWR, note: "expand winning pattern" }));
    const mid = rows.slice(topCut, lowCut).map((r) => ({ team: r.team, avgDeltaWR: r.avgDeltaWR, note: "stabilize draft and early game" }));
    const low = rows.slice(lowCut).map((r) => ({ team: r.team, avgDeltaWR: r.avgDeltaWR, note: "urgent correction plan 14 days" }));
    return {
      top,
      mid,
      low,
      counts: { top: top.length, mid: mid.length, low: low.length },
    };
  }, [teamGapSummary]);

  const factorCorrelations = useMemo(() => {
    const pairs = {
      avgGPM: [],
      avgDPM: [],
      avgKDA: [],
      firstBloodWon: [],
    };
    (scopedStatRaw || []).forEach((r) => {
      const roles = Object.values(r.roleStats || {});
      if (!roles.length) return;
      const getAvg = (key) => {
        const vals = roles.map((x) => Number(x?.[key])).filter((v) => Number.isFinite(v));
        if (!vals.length) return null;
        return vals.reduce((a, v) => a + v, 0) / vals.length;
      };
      const y = num(r.win);
      const gpm = getAvg("gpm");
      const dpm = getAvg("dpm");
      const kill = getAvg("kill");
      const death = getAvg("death");
      const assist = getAvg("assist");
      const kda = (kill != null && death != null && assist != null) ? ((kill + assist) / Math.max(1, death)) : null;
      if (gpm != null) pairs.avgGPM.push({ x: gpm, y });
      if (dpm != null) pairs.avgDPM.push({ x: dpm, y });
      if (kda != null) pairs.avgKDA.push({ x: kda, y });
      if (r.firstBloodWon === true || r.firstBloodWon === false) pairs.firstBloodWon.push({ x: r.firstBloodWon ? 1 : 0, y });
    });
    const rows = [
      { factor: "avgGPM", corr: pearsonFromPairs(pairs.avgGPM), samples: pairs.avgGPM.length },
      { factor: "avgDPM", corr: pearsonFromPairs(pairs.avgDPM), samples: pairs.avgDPM.length },
      { factor: "avgKDA", corr: pearsonFromPairs(pairs.avgKDA), samples: pairs.avgKDA.length },
      { factor: "firstBloodWon", corr: pearsonFromPairs(pairs.firstBloodWon), samples: pairs.firstBloodWon.length },
    ]
      .filter((r) => r.corr != null && r.samples >= 8)
      .sort((a, b) => Math.abs(num(b.corr)) - Math.abs(num(a.corr)));
    return rows;
  }, [scopedStatRaw]);

  const aiIntentStarterPack = useMemo(() => {
    const top3 = aiTopActions.slice(0, 3);
    const mediumLow = actionPlanRows
      .filter((r) => r.priority !== "High")
      .slice(0, 6)
      .map((r) => ({
        team: r.team,
        role: r.role,
        priority: r.priority,
        action: r.action,
        metric: r.metric,
      }));
    const executive5 = [
      `Data confidence ${executiveSummary.confidenceScore}% จาก rows ${stats.rawRows} (official ${stats.officialRows}, scrim ${stats.scrimRows})`,
      `ช่วง 30 วันล่าสุด WR ${num(commandWindowStats.rolling30.wr).toFixed(2)}% (${commandWindowStats.rolling30.games} games)`,
      `MoM WR เปลี่ยน ${num(monthOverMonthKpi?.delta?.wr).toFixed(2)} จุด (${monthOverMonthKpi?.previous?.month || "-"} -> ${monthOverMonthKpi?.current?.month || "-"})`,
      `High-risk heroes ${aiTopRisks.length} ตัว, stage-ready heroes ${aiTopStageReady.length} ตัว`,
      `คาดการณ์สิ้นเดือน WR ${forecastSnapshot.projectedMonthEndWR ?? "-"}% โอกาสถึงเป้า ${forecastSnapshot.chanceToHitTargetPct}%`,
    ];
    return {
      top3WeeklyFocus: top3,
      mediumLowActions: mediumLow,
      executiveSummary5Lines: executive5,
    };
  }, [
    actionPlanRows,
    aiTopActions,
    aiTopRisks.length,
    aiTopStageReady.length,
    commandWindowStats.rolling30.games,
    commandWindowStats.rolling30.wr,
    executiveSummary.confidenceScore,
    forecastSnapshot.chanceToHitTargetPct,
    forecastSnapshot.projectedMonthEndWR,
    monthOverMonthKpi,
    stats.officialRows,
    stats.rawRows,
    stats.scrimRows,
  ]);

  const aiInputSummary = useMemo(() => {
    const combo2Rows = tierFsComboRows.filter((c) => String(c.type || "").startsWith("2"));
    const combo3Rows = tierFsComboRows.filter((c) => String(c.type || "").startsWith("3"));
    return {
      generatedAt: new Date().toISOString(),
      scope: { team, role, bucket, analysisMode, inferredMode, minGames, dateFrom, dateTo },
      topOneHero: tierFsOneHeroRows.slice(0, 12),
      topCombo2: combo2Rows.slice(0, 12),
      topCombo3: combo3Rows.slice(0, 12),
      totals: {
        games: stats.rawRows,
        scrimGames: stats.scrimRows,
        officialGames: stats.officialRows,
        oneHeroCount: tierFsOneHeroRows.length,
        combo2Count: combo2Rows.length,
        combo3Count: combo3Rows.length,
      },
      roleStats: benchmarkRowsView.slice(0, 60).map((r) => ({
        team: r.team,
        role: r.role,
        bucket: r.bucket,
        games: r.rows,
        gpm: r.gpm,
        dpm: r.dpm,
        kill: r.kill,
        death: r.death,
        assist: r.assist,
        kda: r.kda,
        leagueGpm: r.leagueGpm,
        leagueDpm: r.leagueDpm,
        leagueKda: r.leagueKda,
        deltaGpm: r.deltaGpm,
        deltaDpm: r.deltaDpm,
        deltaKda: r.deltaKda,
      })),
      highLevel: {
        heroesAnalyzed: stats.heroCount,
        rawRows: stats.rawRows,
        scrimRows: stats.scrimRows,
        officialRows: stats.officialRows,
        avgDeltaWinRate: +num(stats.avgWinGap).toFixed(2),
        avgDeltaPickRate: +num(stats.avgPickGap).toFixed(2),
        confidenceScore: executiveSummary.confidenceScore,
      },
      executive: {
        highRiskHeroes: executiveSummary.highRiskCount,
        stageReadyHeroes: executiveSummary.stageReadyCount,
      },
      commandCenter: {
        rollingWindow: commandWindowStats,
        monthOverMonthKpi,
        forecast: forecastSnapshot,
        performerTiers: performerTierSummary,
        factorCorrelations,
        starterPack: aiIntentStarterPack,
      },
      topRisks: aiTopRisks,
      topStageReady: aiTopStageReady,
      teamGapSummary: teamGapSummary.slice(0, 8),
      highPriorityActions: aiTopActions,
      underTargetStreaks: commandWindowStats.belowTargetStreaks.slice(0, 6),
      outliers: {
        heroHigh: aiTopStageReady.slice(0, 5),
        heroLow: aiTopRisks.slice(0, 5),
        teamLow: [...teamGapSummary].sort((a, b) => num(a.avgDeltaWR) - num(b.avgDeltaWR)).slice(0, 5),
        teamHigh: [...teamGapSummary].sort((a, b) => num(b.avgDeltaWR) - num(a.avgDeltaWR)).slice(0, 5),
      },
      draftCutCandidates: aiIntentStarterPack.mediumLowActions.slice(0, 4),
      actionPlan14Days: aiTopActions.slice(0, 6),
      executiveSummary5Lines: aiIntentStarterPack.executiveSummary5Lines,
      qualityIssues: qualityGate.issues.slice(0, 5),
    };
  }, [
    analysisMode,
    aiIntentStarterPack,
    aiTopActions,
    aiTopRisks,
    aiTopStageReady,
    bucket,
    commandWindowStats,
    dateFrom,
    dateTo,
    executiveSummary.confidenceScore,
    executiveSummary.highRiskCount,
    executiveSummary.stageReadyCount,
    factorCorrelations,
    forecastSnapshot,
    inferredMode,
    minGames,
    monthOverMonthKpi,
    performerTierSummary,
    qualityGate.issues,
    role,
    stats.avgPickGap,
    stats.avgWinGap,
    stats.heroCount,
    stats.officialRows,
    stats.rawRows,
    stats.scrimRows,
    team,
    teamGapSummary,
    tierFsOneHeroRows,
    tierFsComboRows,
    benchmarkRowsView,
  ]);

  const handleAnalyzeByAI = useCallback(async () => {
    if (aiLoading) return;
    setAiLoading(true);
    setAiError("");
    try {
      const analysis = await analyzeTeamByAI(aiInputSummary);
      setAiResult(analysis);
      setAiUpdatedAt(new Date().toLocaleString());
    } catch (err) {
      setAiError(err?.message || "ไม่สามารถวิเคราะห์ด้วย AI ได้");
    } finally {
      setAiLoading(false);
    }
  }, [aiInputSummary, aiLoading]);

  const handleAskCoach = useCallback(async () => {
    const q = String(coachQuestion || "").trim();
    if (!q || coachLoading) return;
    setCoachLoading(true);
    setCoachError("");
    try {
      const next = await askCoachByAI(q, aiInputSummary);
      setCoachReplies((prev) => [next, ...prev].slice(0, 6));
      setCoachQuestion("");
    } catch (err) {
      setCoachError(err?.message || "ไม่สามารถถาม AI Coach ได้");
    } finally {
      setCoachLoading(false);
    }
  }, [aiInputSummary, coachLoading, coachQuestion]);

  const handleApplyUxPreset = useCallback((preset) => {
    setUxPreset(preset);
    if (preset === "coach") {
      setCoachMode(true);
      setSimpleView(true);
      setViewMode("delta");
      setAnalysisMode("with_scrim");
      return;
    }
    if (preset === "analyst") {
      setCoachMode(true);
      setSimpleView(false);
      setViewMode("delta");
      setAnalysisMode("with_scrim");
      return;
    }
    setCoachMode(false);
    setSimpleView(true);
    setViewMode("delta");
    setAnalysisMode("official_only");
  }, []);
  const handleExportQuality = () => {
    const cols = [
      { key: "sheet", label: "sheet" },
      { key: "parsed_rows", label: "parsed_rows" },
      { key: "dropped_rows", label: "dropped_rows" },
      { key: "inferred_rows", label: "inferred_rows" },
      { key: "header_row", label: "header_row" },
    ];
    downloadCSV("team_performance_quality.csv", toCSV(qualityRows, cols));
  };
  const handleExportActionPlan = () => {
    const cols = [
      { key: "team", label: "team" },
      { key: "role", label: "role" },
      { key: "priority", label: "priority" },
      { key: "focus", label: "focus" },
      { key: "action", label: "action" },
      { key: "metric", label: "metric" },
    ];
    downloadCSV("team_performance_action_plan.csv", toCSV(actionPlanRows, cols));
  };
  const scopedInferredCount = useMemo(() => scopedRaw.filter((r) => isInferredType(r.type)).length, [scopedRaw]);

  /* ── team list for chart colors ── */
  const uniqueTeams = useMemo(() => [...new Set(analysisDelta.map(d => d.team))].sort(), [analysisDelta]);

  /* ═══════════════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════════════ */
  return (
    <div className="tp-root" style={{ minHeight: "100vh", background: `radial-gradient(ellipse 1400px 600px at 15% 0%, rgba(255,16,38,0.14), transparent 60%), linear-gradient(180deg,${T.bg},#ffeff2)`, color: T.text, padding: "20px 18px 40px", position: "relative", overflow: "hidden" }}>
      <GlobalInject />
      <GridBg />

      <div className="tp-main-with-ai" style={{ maxWidth: 1700, margin: "0 auto", position: "relative", zIndex: 1 }}>

        {/* ── HEADER ── */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14, flexWrap: "wrap", marginBottom: 18 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 28, fontWeight: 900, fontFamily: "'Orbitron', sans-serif", background: `linear-gradient(135deg,${T.primary},${T.primaryLight})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              ⚡ Team Performance Intel
            </h1>
            <p style={{ color: T.muted, margin: "4px 0 0", fontSize: 14 }}>เปรียบเทียบ Scrim vs Match Day ต่อทีม/ฮีโร่ — อ่านจากชีตทีมโดยตรง · stat จาก {sourceLabel} เท่านั้น</p>
            <div style={{ marginTop: 6, display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 800, color: T.primaryDark, border: "1px solid rgba(255,16,38,0.35)", borderRadius: 999, padding: "3px 10px", background: T.tintMd }}>
              CHART UI V2
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <BtnGlow onClick={handleExportDelta} icon={Download}>Delta CSV</BtnGlow>
            <BtnGlow onClick={handleExportRaw} icon={Download}>Raw CSV</BtnGlow>
            <BtnGlow onClick={handleExportQuality} icon={Download}>Quality CSV</BtnGlow>
            <BtnGlow onClick={handleExportActionPlan} icon={Download}>Action CSV</BtnGlow>
            <BtnGlow onClick={() => setInferredMode((m) => (m === "only_inferred" ? "all" : "only_inferred"))} icon={Filter} variant={inferredMode === "only_inferred" ? "primary" : "default"}>
              {inferredMode === "only_inferred" ? "Audit Inferred" : "Normal Scope"}
            </BtnGlow>
            <BtnGlow onClick={() => setInferredMode((m) => (m === "exclude_inferred" ? "all" : "exclude_inferred"))} icon={Shield} variant={inferredMode === "exclude_inferred" ? "primary" : "default"}>
              {inferredMode === "exclude_inferred" ? "Exclude Inferred" : "Only Real Rows"}
            </BtnGlow>
            <BtnGlow onClick={() => setShowInferBadge(v => !v)} icon={Activity} variant={showInferBadge ? "primary" : "default"}>
              {showInferBadge ? "Infer Badge ON" : "Infer Badge OFF"}
            </BtnGlow>
            <BtnGlow onClick={() => setCoachMode(v => !v)} icon={Target} variant={coachMode ? "primary" : "default"}>
              {coachMode ? "Coach Mode ON" : "Coach Mode OFF"}
            </BtnGlow>
            <BtnGlow onClick={() => setSimpleView((v) => !v)} icon={Layers} variant={simpleView ? "primary" : "default"}>
              {simpleView ? "Simple View" : "Advanced View"}
            </BtnGlow>
            <BtnGlow onClick={handleResetFilters} icon={RotateCcw}>Reset</BtnGlow>
            <BtnGlow onClick={() => navigate("/dashboard")} icon={ArrowLeft}>Dashboard</BtnGlow>
            <BtnGlow onClick={() => navigate("/draft")} icon={FileEdit} variant="primary">Draft</BtnGlow>
            <BtnGlow onClick={() => navigate("/draft-recap")} icon={Layers}>Draft Recap</BtnGlow>
          </div>
        </motion.div>

        <div className="tp-top-grid">
          <GlassCard style={{ padding: "14px 16px", border: `1.5px solid ${T.borderHover}` }} delay={0.8}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <SectionTitle icon={Zap} title="Executive Summary" subtitle="สรุปประเด็นหลักให้ตัดสินใจเร็วขึ้น" />
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <span style={{ color: T.muted, fontSize: 11, fontWeight: 700 }}>MODE PRESET</span>
                <BtnGlow onClick={() => handleApplyUxPreset("coach")} variant={uxPreset === "coach" ? "primary" : "default"} icon={Target} style={{ padding: "6px 10px", fontSize: 11 }}>Coach</BtnGlow>
                <BtnGlow onClick={() => handleApplyUxPreset("analyst")} variant={uxPreset === "analyst" ? "primary" : "default"} icon={BarChart3} style={{ padding: "6px 10px", fontSize: 11 }}>Analyst</BtnGlow>
                <BtnGlow onClick={() => handleApplyUxPreset("quick")} variant={uxPreset === "quick" ? "primary" : "default"} icon={Zap} style={{ padding: "6px 10px", fontSize: 11 }}>Quick</BtnGlow>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 8, marginTop: 8 }}>
              <MetricCard
                label="Avg ΔWinRate"
                value={<DeltaCell value={executiveSummary.avgDeltaWR} big />}
                icon={TrendingUp}
                helperText="คำนวณจาก WinRate official - WinRate scrim (ค่าลบ = แข่งจริงต่ำกว่าซ้อม)"
                delay={0}
              />
              <MetricCard label="High Risk Heroes" value={executiveSummary.highRiskCount} icon={TrendingDown} color={T.danger} delay={0} />
              <MetricCard label="Stage-ready Heroes" value={executiveSummary.stageReadyCount} icon={Trophy} color={T.success} delay={0} />
              <MetricCard label="High Priority Actions" value={executiveSummary.planHighCount} icon={FileEdit} color={T.warn} delay={0} />
              <MetricCard label="Data Confidence" value={`${executiveSummary.confidenceScore}%`} icon={Shield}
                color={executiveSummary.confidenceTone === "good" ? T.success : executiveSummary.confidenceTone === "warn" ? T.warn : T.danger} delay={0} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(290px,1fr))", gap: 8, marginTop: 10 }}>
              <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, background: T.tintXs, padding: "8px 10px", fontSize: 12 }}>
                <div style={{ color: T.warn, fontWeight: 800, marginBottom: 2 }}>Risk Focus</div>
                <div style={{ color: T.text }}>{executiveSummary.topRisk ? `${executiveSummary.topRisk.team} · ${executiveSummary.topRisk.hero} (ΔWR ${num(executiveSummary.topRisk.deltaWinRate).toFixed(1)}%)` : "ยังไม่มีฮีโร่ที่ติดความเสี่ยงหนักตามเกณฑ์"}</div>
              </div>
              <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, background: T.tintXs, padding: "8px 10px", fontSize: 12 }}>
                <div style={{ color: T.success, fontWeight: 800, marginBottom: 2 }}>Ready Focus</div>
                <div style={{ color: T.text }}>{executiveSummary.topReady ? `${executiveSummary.topReady.team} · ${executiveSummary.topReady.hero} (ΔWR +${num(executiveSummary.topReady.deltaWinRate).toFixed(1)}%)` : "ยังไม่มีฮีโร่ที่ฟอร์มขึ้นชัดเจนตามเกณฑ์"}</div>
              </div>
            </div>
          </GlassCard>

          <GlassCard style={{ padding: "10px 10px" }} neon delay={0.9} className="tp-ai-dock">
            <div>
              <div className="tp-ai-command">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <span className="tp-ai-orb" />
                    <div style={{ color: T.accent, fontWeight: 900, fontSize: 15, letterSpacing: 0.3 }}>AI Command Center</div>
                  </div>
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 8, color: T.muted, fontSize: 11, fontWeight: 700 }}>
                    <span style={{ border: "1px solid rgba(255,16,38,0.30)", borderRadius: 999, padding: "2px 8px", color: aiLoading || coachLoading ? T.warn : T.success, background: aiLoading || coachLoading ? "rgba(255,209,102,0.12)" : "rgba(0,208,132,0.12)" }}>
                      {aiLoading || coachLoading ? "PROCESSING" : "READY"}
                    </span>
                    <span>{aiUpdatedAt ? `SYNC ${aiUpdatedAt}` : "NO SYNC YET"}</span>
                  </div>
                </div>

                <BtnGlow onClick={handleAnalyzeByAI} variant="primary" icon={Zap} style={{ padding: "8px 10px", fontSize: 12, width: "100%", justifyContent: "center" }}>
                  {aiLoading ? "กำลังวิเคราะห์..." : "วิเคราะห์โดย AI"}
                </BtnGlow>

                {aiError && <div style={{ color: T.warn, marginTop: 6, fontSize: 12 }}>{aiError}</div>}
                {aiResult && (
                  <div style={{ marginTop: 8, border: `1px solid rgba(255,16,38,0.24)`, borderRadius: 12, background: T.tintXs, padding: "10px 12px" }}>
                    <div style={{ color: T.accent, fontWeight: 800, fontSize: 13 }}>AI Analyst Brief</div>
                    <div style={{ color: T.text, marginTop: 6, fontSize: 12 }}>{aiResult?.summary || "-"}</div>
                    <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
                      <div>
                        <div style={{ color: T.success, fontWeight: 700, fontSize: 12 }}>จุดแข็ง</div>
                        <div style={{ color: T.text, fontSize: 12, marginTop: 3 }}>
                          {(aiResult?.strengths || []).slice(0, 4).map((item, idx) => <div key={`ai-s-${idx}`}>- {item}</div>)}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: T.warn, fontWeight: 700, fontSize: 12 }}>จุดอ่อน</div>
                        <div style={{ color: T.text, fontSize: 12, marginTop: 3 }}>
                          {(aiResult?.weaknesses || []).slice(0, 4).map((item, idx) => <div key={`ai-w-${idx}`}>- {item}</div>)}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: T.primaryLight, fontWeight: 700, fontSize: 12 }}>แผนซ้อมถัดไป</div>
                        <div style={{ color: T.text, fontSize: 12, marginTop: 3 }}>
                          {(aiResult?.nextActions || []).slice(0, 4).map((item, idx) => <div key={`ai-a-${idx}`}>- {item}</div>)}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ marginTop: 10, color: T.primaryLight, fontWeight: 800, fontSize: 13 }}>AI Coach Chat</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
                  {AI_QUICK_PROMPTS.map((q) => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => setCoachQuestion(q)}
                      style={{
                        border: "1px solid rgba(255,16,38,0.28)",
                        borderRadius: 999,
                        padding: "4px 10px",
                        color: T.accent,
                        background: T.tintMd,
                        fontSize: 11,
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      {q}
                    </button>
                  ))}
                </div>
                <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "1fr auto", gap: 8 }}>
                  <input
                    className="tp-input"
                    value={coachQuestion}
                    onChange={(e) => setCoachQuestion(e.target.value)}
                    placeholder="ถามเช่น: สัปดาห์นี้ควรโฟกัส role ไหนก่อน?"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleAskCoach();
                    }}
                  />
                  <BtnGlow onClick={handleAskCoach} variant="primary" icon={Target} style={{ padding: "8px 12px" }}>
                    {coachLoading ? "กำลังตอบ..." : "ถาม Coach"}
                  </BtnGlow>
                </div>
                {coachError && <div style={{ color: T.warn, fontSize: 12, marginTop: 6 }}>{coachError}</div>}
                <div style={{ marginTop: 8, display: "grid", gap: 8, maxHeight: 320, overflowY: "auto", paddingRight: 4 }}>
                  {coachReplies.length === 0 && (
                    <div style={{ color: T.muted, fontSize: 12 }}>ยังไม่มีคำถาม ลองถามเรื่อง draft, hero pool, หรือแผนซ้อมรายสัปดาห์</div>
                  )}
                  {coachReplies.map((item, idx) => (
                    <div key={`coach-${idx}-${item.at}`} style={{ border: "1px solid rgba(255,16,38,0.22)", borderRadius: 10, padding: "8px 10px", background: "rgba(255,245,247,0.92)" }}>
                      <div style={{ color: T.accent, fontSize: 12, fontWeight: 700 }}>Q: {item.question}</div>
                      <div style={{ marginTop: 4, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                        {item.intent && (
                          <span style={{ border: "1px solid rgba(255,16,38,0.28)", borderRadius: 999, padding: "2px 8px", color: T.accent, fontSize: 10, fontWeight: 800, background: T.tintMd }}>
                            INTENT {item.intent}
                          </span>
                        )}
                        {Number.isFinite(Number(item.intentConfidence)) && (
                          <span style={{ border: "1px solid rgba(255,209,102,0.35)", borderRadius: 999, padding: "2px 8px", color: num(item.intentConfidence) >= 0.75 ? T.success : T.warn, fontSize: 10, fontWeight: 800, background: "rgba(255,209,102,0.08)" }}>
                            CONF {(num(item.intentConfidence) * 100).toFixed(0)}%
                          </span>
                        )}
                        {item.faithfulness && item.faithfulness.total > 0 && (
                          <span
                            title={item.faithfulness.isFaithful
                              ? "ตัวเลขทุกตัวในคำตอบอ้างอิงจากข้อมูลจริงที่ป้อนให้ AI"
                              : `พบตัวเลขที่ตรวจไม่พบในข้อมูล: ${item.faithfulness.ungrounded.join(", ")}`}
                            style={{ border: `1px solid ${item.faithfulness.isFaithful ? "rgba(46,204,113,0.4)" : "rgba(255,107,107,0.45)"}`, borderRadius: 999, padding: "2px 8px", color: item.faithfulness.isFaithful ? T.success : T.warn, fontSize: 10, fontWeight: 800, background: item.faithfulness.isFaithful ? "rgba(46,204,113,0.08)" : "rgba(255,107,107,0.10)" }}
                          >
                            {item.faithfulness.isFaithful ? "✓ " : "⚠ "}TRUST {item.faithfulness.groundingRate}% ({item.faithfulness.grounded}/{item.faithfulness.total})
                          </span>
                        )}
                      </div>
                      {Number.isFinite(Number(item.intentConfidence)) && num(item.intentConfidence) < 0.65 && (
                        <div style={{ color: T.warn, fontSize: 11, marginTop: 4 }}>
                          intent confidence ต่ำ แนะนำระบุเงื่อนไขเพิ่ม เช่น ช่วงเวลา/ทีม/บทบาท
                        </div>
                      )}
                      {item.faithfulness && !item.faithfulness.isFaithful && (
                        <div style={{ color: T.warn, fontSize: 11, marginTop: 4 }}>
                          ⚠ AI อ้างตัวเลขที่ตรวจไม่พบในข้อมูล ({item.faithfulness.ungrounded.join(", ")}) — โปรดตรวจสอบก่อนใช้ตัดสินใจ
                        </div>
                      )}
                      <div style={{ color: T.text, fontSize: 12, marginTop: 4 }}>A: {item.answer || "-"}</div>
                      {item.suggestions.length > 0 && (
                        <div style={{ marginTop: 4, color: T.muted, fontSize: 12 }}>
                          {item.suggestions.map((s, i) => <div key={`coach-s-${i}`}>- {s}</div>)}
                        </div>
                      )}
                      {item.intentReasons?.length > 0 && (
                        <div style={{ marginTop: 4, color: T.muted, fontSize: 11 }}>
                          matched: {item.intentReasons.join(", ")}
                        </div>
                      )}
                      <div style={{ color: T.muted, fontSize: 10, marginTop: 4 }}>{item.at}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </GlassCard>
        </div>

        {/* ── FILTERS + METRICS ── */}
        <GlassCard style={{ padding: "18px 20px" }} delay={1}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(155px,1fr))", gap: 12 }}>
            <FieldLabel label="Team" icon={Users}>
              <select className="tp-select" value={team} onChange={e => setTeam(e.target.value)}>
                {teams.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </FieldLabel>
            <FieldLabel label="Role" icon={Shield}>
              <select className="tp-select" value={role} onChange={e => setRole(e.target.value)}>
                {["ALL", "DSL", "JUG", "MID", "ADL", "SUP"].map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </FieldLabel>
            <FieldLabel label="View" icon={Layers}>
              <select className="tp-select" value={viewMode} onChange={e => setViewMode(e.target.value)}>
                <option value="delta">Delta (รวมผล)</option>
                <option value="raw">Raw Rows (ไม่รวม)</option>
              </select>
            </FieldLabel>
            <FieldLabel label="Analysis Basis" icon={BarChart3}>
              <select className="tp-select" value={analysisMode} onChange={e => setAnalysisMode(e.target.value)}>
                <option value="with_scrim">รวมผลซ้อม + แข่ง</option>
                <option value="official_only">ไม่รวมผลซ้อม (แข่งล้วน)</option>
              </select>
            </FieldLabel>
            <FieldLabel label="Bucket" icon={Filter}>
              <select className="tp-select" value={bucket} onChange={e => setBucket(e.target.value)}>
                <option value="ALL">ALL</option>
                <option value="scrim">scrim</option>
                <option value="official">match day</option>
                <option value="other">other</option>
              </select>
            </FieldLabel>
            <FieldLabel label="Inferred" icon={Activity}>
              <select className="tp-select" value={inferredMode} onChange={e => setInferredMode(e.target.value)}>
                <option value="all">All</option>
                <option value="only_inferred">Only inferred</option>
                <option value="exclude_inferred">Exclude inferred</option>
              </select>
            </FieldLabel>
            <FieldLabel label="Infer Badge" icon={Target}>
              <select className="tp-select" value={showInferBadge ? "on" : "off"} onChange={e => setShowInferBadge(e.target.value === "on")}>
                <option value="on">Show</option>
                <option value="off">Hide</option>
              </select>
            </FieldLabel>
            <FieldLabel label="Hero Cell" icon={Swords}>
              <select className="tp-select" value={heroCellMode} onChange={e => setHeroCellMode(e.target.value)}>
                <option value="text">Text</option>
                <option value="image">Image</option>
              </select>
            </FieldLabel>
            <FieldLabel label="Date From" icon={Calendar}>
              <input className="tp-input" type="text" value={dateFrom} onChange={e => setDateFrom(e.target.value.replace(/[^\d]/g, "").slice(0, 8))} placeholder="YYYYMMDD" />
            </FieldLabel>
            <FieldLabel label="Date To" icon={Calendar}>
              <input className="tp-input" type="text" value={dateTo} onChange={e => setDateTo(e.target.value.replace(/[^\d]/g, "").slice(0, 8))} placeholder="YYYYMMDD" />
            </FieldLabel>
            <FieldLabel label="Min Games" icon={Target}>
              <input className="tp-input" type="number" min={1} max={50} value={minGames} onChange={e => setMinGames(Math.max(1, Number(e.target.value) || 1))} />
            </FieldLabel>
          </div>

          {/* metrics row */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(145px,1fr))", gap: 10, marginTop: 16 }}>
            <MetricCard label="Heroes Analyzed" value={stats.heroCount} icon={Swords} color={T.primaryLight} delay={2} />
            <MetricCard label="Raw Rows" value={stats.rawRows} icon={Layers} color={T.accent} delay={2.5} />
            <MetricCard label="Scrim Rows" value={stats.scrimRows} icon={Activity} color={T.primary} delay={3} />
            <MetricCard label="Match Day" value={stats.officialRows} icon={Trophy} color={T.warn} delay={3.5} />
            <MetricCard label="Avg ΔWinRate" value={<DeltaCell value={stats.avgWinGap} big />} icon={TrendingUp} delay={4} />
            <MetricCard label="Avg ΔPickRate" value={<DeltaCell value={stats.avgPickGap} big />} icon={TrendingDown} delay={4.5} />
          </div>
        </GlassCard>

        {data && (
          <GlassCard style={{ padding: "12px 16px", marginTop: 14, border: `1.5px solid ${qualityGate.isWarn ? T.warn : T.success}` }} delay={1.2}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 800, color: qualityGate.isWarn ? T.warn : T.success }}>
                {qualityGate.isWarn ? "⚠️ Quality Gate: ตรวจพบจุดเสี่ยงก่อนวิเคราะห์" : "✅ Quality Gate: คุณภาพข้อมูลอยู่ในเกณฑ์พร้อมวิเคราะห์"}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <div style={{ color: T.muted, fontSize: 12 }}>
                  rows {qualityGate.totalRows} · inferred {qualityGate.inferredRows} ({qualityGate.inferredRatio}%)
                </div>
                <BtnGlow onClick={() => setQualityConfig(DEFAULT_QUALITY_CONFIG)} icon={RotateCcw} style={{ padding: "6px 10px", fontSize: 11 }}>
                  Reset Threshold
                </BtnGlow>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 8, marginTop: 8 }}>
              <FieldLabel label="Inferred Warn %">
                <input className="tp-input" type="number" min={0} max={100} value={qualityConfig.inferredWarnPct}
                  onChange={e => setQualityConfig(prev => ({ ...prev, inferredWarnPct: Math.max(0, Number(e.target.value) || 0) }))} />
              </FieldLabel>
              <FieldLabel label="Min Official / Team">
                <input className="tp-input" type="number" min={0} max={200} value={qualityConfig.minOfficialRowsPerTeam}
                  onChange={e => setQualityConfig(prev => ({ ...prev, minOfficialRowsPerTeam: Math.max(0, Number(e.target.value) || 0) }))} />
              </FieldLabel>
              <FieldLabel label="Min Team Rows Check">
                <input className="tp-input" type="number" min={1} max={1000} value={qualityConfig.minTeamRowsToCheckOfficial}
                  onChange={e => setQualityConfig(prev => ({ ...prev, minTeamRowsToCheckOfficial: Math.max(1, Number(e.target.value) || 1) }))} />
              </FieldLabel>
              <FieldLabel label="Min Total Rows">
                <input className="tp-input" type="number" min={1} max={50000} value={qualityConfig.minTotalRows}
                  onChange={e => setQualityConfig(prev => ({ ...prev, minTotalRows: Math.max(1, Number(e.target.value) || 1) }))} />
              </FieldLabel>
            </div>
            {qualityGate.issues.length > 0 && (
              <div style={{ marginTop: 8, color: T.text, fontSize: 12, lineHeight: 1.7 }}>
                {qualityGate.issues.map((msg, i) => <div key={msg}>{i + 1}. {msg}</div>)}
              </div>
            )}
          </GlassCard>
        )}

        <GlassCard style={{ padding: "14px 18px", marginTop: 14 }} delay={2}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <SectionTitle icon={Layers} title="Analysis Scope" subtitle={analysisMode === "with_scrim" ? "โหมดรวมผลซ้อม + แข่ง (ใช้หา delta)" : "โหมดไม่รวมผลซ้อม (ประเมินแข่งล้วน)"} />
            <div style={{ display: "flex", gap: 14, color: T.muted, fontSize: 12, flexWrap: "wrap" }}>
              <span>Teams: <strong style={{ color: T.text }}>{scopedSummary.teams}</strong></span>
              <span>Rows: <strong style={{ color: T.text }}>{scopedSummary.rows}</strong></span>
              <span>Scrim: <strong style={{ color: T.primary }}>{scopedSummary.scrim}</strong></span>
              <span>Official: <strong style={{ color: T.accent }}>{scopedSummary.official}</strong></span>
              <span>Inferred: <strong style={{ color: T.warn }}>{scopedInferredCount}</strong></span>
              <span>Delta Heroes: <strong style={{ color: T.text }}>{scopedSummary.deltaHeroes}</strong></span>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", width: "100%" }}>
              <ScopeChip label="Analysis" value={analysisMode === "with_scrim" ? "Scrim + Match" : "Official only"} tone={analysisMode === "with_scrim" ? "good" : "default"} />
              <ScopeChip label="Inferred" value={inferredModeLabel} tone={inferredMode === "only_inferred" ? "warn" : inferredMode === "exclude_inferred" ? "good" : "default"} />
              <ScopeChip label="Bucket" value={bucketLabel(bucket === "ALL" ? "ALL" : bucket)} />
              <ScopeChip label="View" value={viewMode === "delta" ? "Delta" : "Raw"} />
              <ScopeChip label="UX" value={simpleView ? "Simple" : "Advanced"} tone={simpleView ? "good" : "default"} />
            </div>
          </div>
        </GlassCard>

        {/* ═══════════════════════════════════════════════════════════
            TAB BAR — chart-first redesign. Charts on top, the full
            existing analytics preserved under "ขั้นสูง (ทั้งหมด)".
            ═══════════════════════════════════════════════════════════ */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "16px 0 2px", position: "sticky", top: 0, zIndex: 6, padding: "8px 0", background: `linear-gradient(180deg, ${T.bg} 60%, transparent)` }}>
          {[
            { key: "overview", label: "ภาพรวม", icon: BarChart3 },
            { key: "earlymacro", label: "Early Macro", icon: Clock },
            { key: "roles", label: "รายตำแหน่ง", icon: Users },
            { key: "draft", label: "ดราฟต์", icon: FileEdit },
            { key: "advanced", label: "ขั้นสูง (ทั้งหมด)", icon: Layers },
          ].map((t) => (
            <BtnGlow key={t.key} icon={t.icon} variant={activeTab === t.key ? "primary" : "default"} onClick={() => setActiveTab(t.key)}>
              {t.label}
            </BtnGlow>
          ))}
        </div>

        {/* ── TAB: OVERVIEW (team summary, side / scrim-vs-match / length / trend) ── */}
        {activeTab === "overview" && (
          <>
            <GlassCard style={{ padding: "14px 18px", marginTop: 10 }}>
              <SectionTitle icon={BarChart3} title={`ภาพรวมทีม ${TEAM_SHORT}`} subtitle={`อ่านจากชีท All_Scrim · ${fsReport.scopeLabel || "ทุกเกม"} · ${fsReport.games} เกม`} />
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 8, marginTop: 10 }}>
                <MetricCard label="เกมทั้งหมด" value={fsReport.games} icon={Activity} />
                <MetricCard label="อัตราชนะรวม" value={`${fsOverview.wr}%`} icon={Trophy} color={fsOverview.wr >= 55 ? T.success : fsOverview.wr >= 45 ? T.warn : T.danger} />
                <MetricCard label="ซ้อม WR" value={`${fsReport.buckets.scrim.wr}%`} icon={Swords} color={T.accent} helperText={`${fsReport.buckets.scrim.games} เกม`} />
                <MetricCard label="แข่งจริง WR" value={`${fsReport.buckets.official.wr}%`} icon={Target} color={fsReport.buckets.official.wr >= 50 ? T.success : T.danger} helperText={`${fsReport.buckets.official.games} เกม`} />
                <MetricCard label="ความยาวเกมเฉลี่ย" value={`${fsReport.gameLength.avg}′`} icon={Clock} helperText={`กลาง ${fsReport.gameLength.median}′ · ${fsReport.gameLength.min}–${fsReport.gameLength.max}′`} />
                <MetricCard label={`Kills เฉลี่ย (${TEAM_SHORT} / คู่แข่ง)`} value={`${fsReport.kills.fsAvg} / ${fsReport.kills.opAvg}`} icon={Swords} color={T.primaryLight} />
              </div>
            </GlassCard>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(340px,1fr))", gap: 12, marginTop: 12 }}>
              <GlassCard style={{ padding: "12px 14px" }}><SectionTitle icon={Shield} title="อัตราชนะตามฝั่ง" subtitle="Blue vs Red" /><div style={{ marginTop: 8 }}><SideWinRateChart side={fsReport.side} /></div></GlassCard>
              <GlassCard style={{ padding: "12px 14px" }}><SectionTitle icon={Target} title="ซ้อม vs แข่งจริง" subtitle="ฟอร์มหลุดวันแข่งหรือไม่" /><div style={{ marginTop: 8 }}><ScrimVsOfficialChart buckets={fsReport.buckets} /></div></GlassCard>
              <GlassCard style={{ padding: "12px 14px" }}><SectionTitle icon={Clock} title="ความยาวเกม + อัตราชนะ" subtitle="เกมสั้น/ยาวสัมพันธ์กับผลแพ้ชนะ" /><div style={{ marginTop: 8 }}><GameLengthHistogram gameLength={fsReport.gameLength} /></div></GlassCard>
              <GlassCard style={{ padding: "12px 14px" }}><SectionTitle icon={TrendingUp} title="เทรนด์รายสัปดาห์" subtitle="อัตราชนะแต่ละสัปดาห์ (รวม / ซ้อม / แข่งจริง)" /><div style={{ marginTop: 8 }}><WinRateTrendChart trend={fsReport.trendByWeek} /></div></GlassCard>
            </div>
          </>
        )}

        {/* ── TAB: EARLY MACRO (charts) ── */}
        {activeTab === "earlymacro" && (
          <EarlyGameMacroSection
            agg={earlyMacroAgg}
            byOpponent={earlyMacroByOpp}
            compareOpponent={compareOpponent}
            compareHeadToHeadOnly={compareHeadToHeadOnly}
          />
        )}

        {/* ── TAB: ROLES (per-role FS vs OP) ── */}
        {activeTab === "roles" && (
          <>
            <GlassCard style={{ padding: "14px 18px", marginTop: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <SectionTitle icon={Users} title={`ผลงานรายตำแหน่ง ${TEAM_SHORT} vs คู่แข่ง`} subtitle={`KDA / GPM / Gold / %ดาเมจ ต่อ role · ${fsReport.scopeLabel || "ทุกเกม"} · ${fsReport.games} เกม`} />
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {[["kda", "KDA"], ["gpm", "GPM"], ["gold", "Gold"], ["dmgShare", "%ดาเมจ"]].map(([k, lbl]) => (
                    <BtnGlow key={k} variant={roleMetric === k ? "primary" : "default"} onClick={() => setRoleMetric(k)} style={{ padding: "6px 12px", fontSize: 12 }}>{lbl}</BtnGlow>
                  ))}
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 8, marginTop: 10 }}>
                <MetricCard label={`ทีม GPM (${TEAM_SHORT})`} value={fsReport.rolesTotals.fsAllGpm} icon={TrendingUp} color={T.primaryLight} helperText={`คู่แข่ง ${fsReport.rolesTotals.opAllGpm}`} />
                <MetricCard label={`Kills เฉลี่ย ${TEAM_SHORT}`} value={fsReport.kills.fsAvg} icon={Swords} color={T.success} helperText={`คู่แข่ง ${fsReport.kills.opAvg}`} />
              </div>
            </GlassCard>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(340px,1fr))", gap: 12, marginTop: 12 }}>
              <GlassCard style={{ padding: "12px 14px" }}><SectionTitle icon={Activity} title="เรดาร์รายตำแหน่ง" /><div style={{ marginTop: 8 }}><RolePerformanceRadar roles={fsReport.roles} metric={roleMetric} /></div></GlassCard>
              <GlassCard style={{ padding: "12px 14px" }}><SectionTitle icon={BarChart3} title="เทียบรายตำแหน่ง" /><div style={{ marginTop: 8 }}><RoleGroupedBars roles={fsReport.roles} metric={roleMetric} /></div></GlassCard>
            </div>
            <GlassCard style={{ padding: "12px 14px", marginTop: 12 }}><SectionTitle icon={PieChartIcon} title="การกระจายดาเมจตามตำแหน่ง" subtitle={`${TEAM_SHORT} เทียบคู่แข่ง (รวม = 100%)`} /><div style={{ marginTop: 8 }}><RoleDamageShareChart roles={fsReport.roles} /></div></GlassCard>
          </>
        )}

        {/* ── TAB: DRAFT (pick / ban / win rate) ── */}
        {activeTab === "draft" && (
          <>
            <GlassCard style={{ padding: "14px 18px", marginTop: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <SectionTitle icon={FileEdit} title="แนวทางดราฟต์" subtitle={`Pick / Ban / Win rate ต่อฮีโร่ · ทีม ${draftTeam || "-"}${selectedDraftTeam ? ` · ${selectedDraftTeam.games} เกม` : ""}`} />
                {draftTeamOptions.length > 0 && (
                  <select className="tp-select" style={{ width: "auto", minWidth: 120 }} value={draftTeam} onChange={(e) => setDraftTeam(e.target.value)}>
                    {draftTeamOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                )}
              </div>
            </GlassCard>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(340px,1fr))", gap: 12, marginTop: 12 }}>
              <GlassCard style={{ padding: "12px 14px" }}><SectionTitle icon={Layers} title="Pick / Ban บ่อยสุด" /><div style={{ marginTop: 8 }}><DraftPickBanChart heroes={selectedDraftTeam?.heroes} /></div></GlassCard>
              <GlassCard style={{ padding: "12px 14px" }}><SectionTitle icon={Trophy} title="อัตราชนะต่อฮีโร่" /><div style={{ marginTop: 8 }}><DraftWinRateChart heroes={selectedDraftTeam?.heroes} /></div></GlassCard>
            </div>
            {draftTeam === "FS" && (
              <GlassCard style={{ padding: "12px 14px", marginTop: 12 }}><SectionTitle icon={Shield} title="ความถนัดฝั่ง (Blue / Red)" subtitle="จำนวนเกม + อัตราชนะแต่ละฝั่ง" /><div style={{ marginTop: 8 }}><SideWinRateChart side={fsReport.side} /></div></GlassCard>
            )}
          </>
        )}

        {/* ── TAB: ADVANCED — the full existing analytics suite, unchanged ── */}
        {activeTab === "advanced" && (
        <>
        <GlassCard style={{ padding: "16px 18px", marginTop: 14, border: `1.5px solid ${processConfig.autoPriority && processChecklist.items.some((x) => !x.ok) ? T.warn : T.border}` }} delay={2.15}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <SectionTitle icon={Zap} title="Patch + Season Auto Process" subtitle="ทำ workflow นี้อัตโนมัติทุกครั้งที่เปิดซีซั่นใหม่หรือมี patch ใหม่" />
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <BtnGlow onClick={() => setProcessConfig((prev) => ({ ...prev, autoPriority: !prev.autoPriority }))} variant={processConfig.autoPriority ? "primary" : "default"} icon={Target}>
                {processConfig.autoPriority ? "Auto Priority ON" : "Auto Priority OFF"}
              </BtnGlow>
              <BtnGlow onClick={handleMarkProcessFeedback} icon={FileEdit}>
                Mark Feedback Done
              </BtnGlow>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 8, marginTop: 8 }}>
            <FieldLabel label="Season Tag">
              <input className="tp-input" value={processConfig.seasonTag} onChange={(e) => setProcessConfig((prev) => ({ ...prev, seasonTag: e.target.value }))} placeholder="เช่น RPL 2026 Summer" />
            </FieldLabel>
            <FieldLabel label="Patch Tag">
              <input className="tp-input" value={processConfig.patchTag} onChange={(e) => setProcessConfig((prev) => ({ ...prev, patchTag: e.target.value }))} placeholder="เช่น 1.58.2" />
            </FieldLabel>
            <FieldLabel label="Patch Start Date">
              <input className="tp-input" value={processConfig.patchStartDate} onChange={(e) => setProcessConfig((prev) => ({ ...prev, patchStartDate: e.target.value.replace(/[^\d]/g, "").slice(0, 8) }))} placeholder="YYYYMMDD" />
            </FieldLabel>
            <FieldLabel label="Last Feedback">
              <input className="tp-input" value={processConfig.lastFeedbackAt} onChange={(e) => setProcessConfig((prev) => ({ ...prev, lastFeedbackAt: e.target.value.replace(/[^\d]/g, "").slice(0, 8) }))} placeholder="YYYYMMDD" />
            </FieldLabel>
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
            <ScopeChip label="Rows Since Patch" value={patchScopedRows.length} tone={patchScopedRows.length > 0 ? "good" : "warn"} />
            <ScopeChip label="Latest Data" value={ymdLabel(latestScopedDate)} tone="default" />
            <ScopeChip label="Last Feedback" value={ymdLabel(processConfig.lastFeedbackAt)} tone={processChecklist.staleByDate ? "warn" : "good"} />
          </div>

          <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(250px,1fr))", gap: 8 }}>
            {processChecklist.items.map((item) => (
              <div key={item.key} style={{ border: `1px solid ${item.ok ? "rgba(0,208,132,0.38)" : "rgba(255,209,102,0.48)"}`, borderRadius: 10, background: item.ok ? "rgba(0,208,132,0.10)" : "rgba(255,209,102,0.12)", padding: "8px 10px" }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: item.ok ? T.success : T.warn }}>{item.ok ? "✅" : "⚠️"} {item.label}</div>
                <div style={{ fontSize: 11, color: T.text, marginTop: 3 }}>{item.detail}</div>
              </div>
            ))}
          </div>

          {processConfig.autoPriority && processChecklist.staleByDate && (
            <div style={{ marginTop: 8, border: `1px solid ${T.warn}`, borderRadius: 10, background: "rgba(255,209,102,0.12)", color: T.warn, padding: "8px 10px", fontSize: 12, fontWeight: 700 }}>
              มีข้อมูลใหม่หลัง feedback ล่าสุด ({ymdLabel(latestScopedDate)} &gt; {ymdLabel(processConfig.lastFeedbackAt)}) ควรรันรอบวิเคราะห์และส่ง feedback อีกครั้ง
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 10, marginTop: 12 }}>
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, background: T.tintXs, padding: "10px 12px" }}>
              <div style={{ color: T.primaryLight, fontWeight: 800, marginBottom: 6 }}>Patch Meta Focus (Top by Role)</div>
              <div className="tp-scroll" style={{ maxHeight: 210, overflowY: "auto" }}>
                {patchFeedbackRows.map((r) => (
                  <div key={r.role} style={{ borderTop: "1px solid rgba(255,16,38,0.06)", padding: "8px 0", fontSize: 12 }}>
                    <div><span style={{ color: T.accent, fontWeight: 700 }}>{r.role}</span> · <span style={{ color: T.text, fontWeight: 700 }}>{r.focus}</span></div>
                    <div style={{ color: T.muted, marginTop: 2 }}>{r.note}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, background: T.tintXs, padding: "10px 12px" }}>
              <div style={{ color: T.accent, fontWeight: 800, marginBottom: 6 }}>Patch Pool Recommendation</div>
              <div className="tp-scroll" style={{ maxHeight: 210, overflowY: "auto" }}>
                {patchRoleRecs.length === 0 && <div style={{ color: T.muted, fontSize: 12 }}>ยังไม่มี recommendation หลัง patch ตามเงื่อนไข</div>}
                {patchRoleRecs.slice(0, 14).map((r) => (
                  <div key={`${r.team}-${r.role}-${r.hero}`} style={{ borderTop: "1px solid rgba(255,16,38,0.06)", padding: "8px 0", fontSize: 12, display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <span><span style={{ color: T.primaryLight, fontWeight: 700 }}>{r.team}</span> <span style={{ color: T.accent }}>{r.role}</span> · {r.hero}</span>
                    <span style={{ color: r.label === "Core" ? T.success : r.label === "Weak" ? T.danger : T.warn, fontWeight: 700 }}>{r.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </GlassCard>

        <GlassCard style={{ padding: "14px 18px", marginTop: 14 }} delay={2.3}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <SectionTitle icon={Users} title="TO BE vs Opponent" subtitle={`เทียบสถิติทีมเรา (${TEAM_SHORT} — ใช้รหัส FS ในชีต All_Scrim_Stats) กับทีมคู่แข่งที่เลือก · stat จาก ${sourceLabel} เท่านั้น`} />
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <select className="tp-select" value={compareOpponent} onChange={(e) => setCompareOpponent(e.target.value)} style={{ minWidth: 130 }}>
                {compareOpponentOptions.length === 0 && <option value="">No opponent</option>}
                {compareOpponentOptions.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <BtnGlow
                onClick={() => setCompareHeadToHeadOnly((v) => !v)}
                icon={Swords}
                variant={compareHeadToHeadOnly ? "primary" : "default"}
              >
                {compareHeadToHeadOnly ? "Head-to-Head Only" : "All Games"}
              </BtnGlow>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 10, marginTop: 10 }}>
            {[compareSummary.fs, compareSummary.opponent].map((s) => (
              <div key={s.team} style={{ border: `1px solid ${T.border}`, borderRadius: 12, background: T.tintXs, padding: "10px 12px" }}>
                <div style={{ color: s.team === OUR_TEAM ? T.primaryLight : T.accent, fontWeight: 900, fontSize: 17 }}>{s.team}</div>
                <div style={{ marginTop: 6, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontSize: 12 }}>
                  <div style={{ color: T.muted }}>Games</div><div style={{ textAlign: "right", color: T.text }}>{s.games}</div>
                  <div style={{ color: T.muted }}>WR</div><div style={{ textAlign: "right", color: T.text }}>{s.wr.toFixed(2)}%</div>
                  <div style={{ color: T.muted }}>Scrim WR</div><div style={{ textAlign: "right", color: T.text }}>{s.scrimWR.toFixed(2)}% ({s.scrimGames})</div>
                  <div style={{ color: T.muted }}>Official WR</div><div style={{ textAlign: "right", color: T.text }}>{s.officialWR.toFixed(2)}% ({s.officialGames})</div>
                  <div style={{ color: T.muted }}>Unique Heroes</div><div style={{ textAlign: "right", color: T.text }}>{s.uniqueHeroes}</div>
                  <div style={{ color: T.muted }}>Avg ΔWR</div><div style={{ textAlign: "right" }}><DeltaCell value={s.avgDeltaWR} /> ({s.deltaHeroes})</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 12 }}>
            <div style={{ color: T.muted, fontSize: 12, marginBottom: 6 }}>
              Radar Compare ({OUR_TEAM} vs {compareOpponent || "Opponent"})
            </div>
            <ChartPanel minHeight={330}>
              <ResponsiveContainer width="100%" height={310}>
                <RadarChart data={compareRadarData} outerRadius="72%">
                  <PolarGrid stroke="rgba(255,16,38,0.15)" />
                  <PolarAngleAxis dataKey="metric" tick={{ fill: BRAND_COLORS.chartTick, fontSize: 11, fontWeight: 700, fontFamily: FONT }} />
                  <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                  <Tooltip content={<ChartTooltip formatter={(v) => `${num(v).toFixed(1)}`} />} />
                  <Legend wrapperStyle={CHART_LEGEND_STYLE} />
                  <Radar name={OUR_TEAM} dataKey="fs" stroke={T.primaryLight} fill={T.primaryLight} fillOpacity={0.28} strokeWidth={2.3} />
                  <Radar name={compareOpponent || "Opponent"} dataKey="opponent" stroke={T.accent} fill={T.accent} fillOpacity={0.20} strokeWidth={2.1} />
                </RadarChart>
              </ResponsiveContainer>
            </ChartPanel>
          </div>
          <div style={{ marginTop: 12, border: `1px solid ${T.border}`, borderRadius: 12, background: T.tintXs, padding: "10px 12px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
              <div style={{ color: T.primaryLight, fontWeight: 800 }}>
                Position Duel ({compareRole})
              </div>
              <select className="tp-select" value={compareRole} onChange={(e) => setCompareRole(e.target.value)} style={{ minWidth: 120 }}>
                {["DSL", "JUG", "MID", "ADL", "SUP"].map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 8, fontSize: 12, fontWeight: 800 }}>
              <span style={{ color: T.primaryLight }}>ซ้าย = TO BE</span>
              <span style={{ color: T.primaryDark }}>ขวา = {compareOpponent || "Opponent"}</span>
            </div>
            <div style={{ display: "grid", gap: 8 }}>
              {roleDuel.metrics.map((m) => {
                const lv = m.left == null ? 0 : num(m.left);
                const rv = m.right == null ? 0 : num(m.right);
                const maxV = Math.max(1, lv, rv);
                const leftW = m.left == null ? 0 : Math.max(8, (lv / maxV) * 100);
                const rightW = m.right == null ? 0 : Math.max(8, (rv / maxV) * 100);
                const fmt = (v) => {
                  if (v == null) return "ไม่มีข้อมูล";
                  return m.suffix ? `${num(v).toFixed(2)}${m.suffix}` : num(v).toFixed(2);
                };
                return (
                  <div key={m.key} style={{ display: "grid", gridTemplateColumns: "140px 1fr 140px", gap: 8, alignItems: "center" }}>
                    <div style={{ textAlign: "left", color: T.primaryLight, fontWeight: 900 }}>{fmt(m.left)}</div>
                    <div>
                      <div style={{ textAlign: "center", color: T.text, fontSize: 12, fontWeight: 800, marginBottom: 3 }}>{m.label}</div>
                      {m.left == null && m.right == null && (
                        <div style={{ textAlign: "center", color: T.warn, fontSize: 11, marginBottom: 3 }}>
                          {m.missingNote || "ไม่มีข้อมูลในชีท"}
                        </div>
                      )}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
                        <div style={{ height: 8, background: T.tintMd, borderRadius: 999, overflow: "hidden" }}>
                          <div style={{ width: `${leftW}%`, height: "100%", background: `linear-gradient(90deg,${T.primaryLight},${T.primary})` }} />
                        </div>
                        <div style={{ height: 8, background: T.tintMd, borderRadius: 999, overflow: "hidden" }}>
                          <div style={{ width: `${rightW}%`, height: "100%", marginLeft: "auto", background: `linear-gradient(90deg,${T.primary},${T.primaryDark})` }} />
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: "right", color: T.primaryDark, fontWeight: 900 }}>{fmt(m.right)}</div>
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: 10 }}>
              <div style={{ color: T.muted, fontSize: 11, marginBottom: 4 }}>
                Position Radar ({compareRole}) - normalized
              </div>
              {!roleDuelRadarData.hasData && (
                <div style={{ color: T.warn, fontSize: 11, marginBottom: 4 }}>
                  ยังไม่มีข้อมูลในชีทสำหรับ metric นี้ จึงแสดงค่าเป็น 0
                </div>
              )}
              <ChartPanel minHeight={270}>
                <ResponsiveContainer width="100%" height={250}>
                  <RadarChart data={roleDuelRadarData.points} outerRadius="68%">
                    <PolarGrid stroke="rgba(255,16,38,0.15)" />
                    <PolarAngleAxis dataKey="metric" tick={{ fill: BRAND_COLORS.chartTick, fontSize: 10, fontWeight: 700, fontFamily: FONT }} />
                    <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                    <Tooltip content={<ChartTooltip formatter={(v) => `${num(v).toFixed(1)}`} />} />
                    <Legend wrapperStyle={CHART_LEGEND_STYLE} />
                    <Radar name={OUR_TEAM} dataKey="fs" stroke={T.primaryLight} fill={T.primaryLight} fillOpacity={0.28} strokeWidth={2.2} />
                    <Radar name={compareOpponent || "Opponent"} dataKey="opponent" stroke={T.accent} fill={T.accent} fillOpacity={0.20} strokeWidth={2.1} />
                  </RadarChart>
                </ResponsiveContainer>
              </ChartPanel>
            </div>
            <div style={{ marginTop: 6, color: T.muted, fontSize: 11 }}>
              ซ้าย = {OUR_TEAM} ({roleDuel.left.games} games) | ขวา = {compareOpponent || "Opponent"} ({roleDuel.right.games} games)
            </div>
          </div>

          <div style={{ marginTop: 14 }}>
            <div style={{ color: T.muted, fontSize: 12, marginBottom: 8 }}>
              TO BE เทียบทุกทีม ({compareHeadToHeadOnly ? "Head-to-Head" : "All Games"})
            </div>
            <div className="tp-scroll" style={{ overflowX: "auto", maxHeight: 300, overflowY: "auto" }}>
              <table className="tp-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr>
                    {["Opponent", "TO BE Games", "TO BE WR", "Opponent Games", "Opponent WR", "Gap (TO BE-OP)", "TO BE Scrim/Official", "OP Scrim/Official"].map((h) => (
                      <th key={h} style={{ padding: "9px 8px", whiteSpace: "nowrap", textAlign: "left", color: T.muted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", background: T.tintLg, borderBottom: `2px solid ${T.border}` }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {fsVsAllSummary.length === 0 && (
                    <tr>
                      <td colSpan={8} style={{ padding: 16, color: T.muted, textAlign: "center" }}>
                        ยังไม่มีข้อมูลเทียบ TO BE กับทีมอื่นตามเงื่อนไขที่เลือก
                      </td>
                    </tr>
                  )}
                  {fsVsAllSummary.map((r) => (
                    <tr key={r.opponent}>
                      <td style={tdS}><span style={{ color: T.primaryLight, fontWeight: 700 }}>{r.opponent}</span></td>
                      <td style={tdS}>{r.fsGames}</td>
                      <td style={tdS}>{r.fsWR.toFixed(2)}%</td>
                      <td style={tdS}>{r.oppGames}</td>
                      <td style={tdS}>{r.oppWR.toFixed(2)}%</td>
                      <td style={tdS}><DeltaCell value={r.wrGap} /></td>
                      <td style={tdS}>{r.fsScrimWR.toFixed(2)}% / {r.fsOfficialWR.toFixed(2)}%</td>
                      <td style={tdS}>{r.oppScrimWR.toFixed(2)}% / {r.oppOfficialWR.toFixed(2)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </GlassCard>

        {/* Early Game Macro now lives in its own "Early Macro" tab (rendered above). */}

        <GlassCard style={{ padding: "18px 20px", marginTop: 16 }} neon delay={2.9}>
          <SectionTitle icon={Swords} title="TierData Sync (Hero + Combo)" subtitle="สถิติชุดเดียวกับ TierData: TO BE one-hero จาก RPL_player_stats, combo จาก ComboStats, ฝั่งคู่แข่งจาก Competitor_Hero_stats/ชีททีม" />
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
            <ScopeChip label="TO BE Hero Rows" value={tierFsOneHeroRows.length} tone={tierFsOneHeroRows.length > 0 ? "good" : "warn"} />
            <ScopeChip label="TO BE Combo Rows" value={tierFsComboRows.length} tone={tierFsComboRows.length > 0 ? "good" : "warn"} />
            <ScopeChip label={`${compareOpponent || "Opponent"} Hero`} value={tierOpponentOneHeroRows.length} tone={tierOpponentOneHeroRows.length > 0 ? "good" : "warn"} />
            <ScopeChip label={`${compareOpponent || "Opponent"} Combo`} value={tierOpponentComboRows.length} tone={tierOpponentComboRows.length > 0 ? "good" : "warn"} />
          </div>
          {tierStatsLoading && (
            <div style={{ color: T.muted, fontSize: 12, marginTop: 8 }}>กำลังโหลดสถิติฝั่ง {compareOpponent || "opponent"} ...</div>
          )}
          {tierStatsError && (
            <div style={{ color: T.warn, fontSize: 12, marginTop: 8 }}>{tierStatsError}</div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 10, marginTop: 10 }}>
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, background: T.tintXs, padding: "10px 12px" }}>
              <div style={{ color: T.primaryLight, fontWeight: 800, marginBottom: 6 }}>TO BE One-Hero (TierData)</div>
              <div className="tp-scroll" style={{ maxHeight: 240, overflowY: "auto", overflowX: "auto" }}>
                <table className="tp-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr>
                      {["Hero", "Role", "Games", "Win", "WR"].map((h) => <th key={h} style={{ padding: "7px 6px", textAlign: "left", color: T.muted, fontSize: 10, textTransform: "uppercase", background: T.tintLg, borderBottom: `2px solid ${T.border}` }}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {tierFsOneHeroRows.length === 0 && <tr><td colSpan={5} style={{ padding: 12, textAlign: "center", color: T.muted }}>ยังไม่มีข้อมูล one-hero</td></tr>}
                    {tierFsOneHeroRows.slice(0, 12).map((r) => (
                      <tr key={`fs-one-${r.hero}-${r.role}`}>
                        <td style={tdS}>{r.hero}</td>
                        <td style={tdS}>{r.role}</td>
                        <td style={tdS}>{r.total}</td>
                        <td style={tdS}>{r.win}</td>
                        <td style={tdS}>{r.wr.toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, background: T.tintXs, padding: "10px 12px" }}>
              <div style={{ color: T.accent, fontWeight: 800, marginBottom: 6 }}>TO BE Combo (TierData)</div>
              <div className="tp-scroll" style={{ maxHeight: 240, overflowY: "auto", overflowX: "auto" }}>
                <table className="tp-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr>
                      {["Type", "Combo", "Games", "Win", "WR"].map((h) => <th key={h} style={{ padding: "7px 6px", textAlign: "left", color: T.muted, fontSize: 10, textTransform: "uppercase", background: T.tintLg, borderBottom: `2px solid ${T.border}` }}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {tierFsComboRows.length === 0 && <tr><td colSpan={5} style={{ padding: 12, textAlign: "center", color: T.muted }}>ยังไม่มีข้อมูล combo</td></tr>}
                    {tierFsComboRows.slice(0, 12).map((r, i) => (
                      <tr key={`fs-combo-${r.combo}-${i}`}>
                        <td style={tdS}>{r.type}</td>
                        <td style={{ ...tdS, minWidth: 170 }}>{r.combo}</td>
                        <td style={tdS}>{r.total}</td>
                        <td style={tdS}>{r.win}</td>
                        <td style={tdS}>{r.wr.toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, background: T.tintXs, padding: "10px 12px" }}>
              <div style={{ color: T.primaryLight, fontWeight: 800, marginBottom: 6 }}>{compareOpponent || "Opponent"} One-Hero (TierData)</div>
              <div className="tp-scroll" style={{ maxHeight: 240, overflowY: "auto", overflowX: "auto" }}>
                <table className="tp-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr>
                      {["Hero", "Role", "Games", "Win", "WR"].map((h) => <th key={h} style={{ padding: "7px 6px", textAlign: "left", color: T.muted, fontSize: 10, textTransform: "uppercase", background: T.tintLg, borderBottom: `2px solid ${T.border}` }}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {tierOpponentOneHeroRows.length === 0 && <tr><td colSpan={5} style={{ padding: 12, textAlign: "center", color: T.muted }}>ยังไม่มีข้อมูล one-hero</td></tr>}
                    {tierOpponentOneHeroRows.slice(0, 12).map((r) => (
                      <tr key={`opp-one-${r.hero}-${r.role}`}>
                        <td style={tdS}>{r.hero}</td>
                        <td style={tdS}>{r.role}</td>
                        <td style={tdS}>{r.total}</td>
                        <td style={tdS}>{r.win}</td>
                        <td style={tdS}>{r.wr.toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, background: T.tintXs, padding: "10px 12px" }}>
              <div style={{ color: T.accent, fontWeight: 800, marginBottom: 6 }}>{compareOpponent || "Opponent"} Combo (TierData)</div>
              <div className="tp-scroll" style={{ maxHeight: 240, overflowY: "auto", overflowX: "auto" }}>
                <table className="tp-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr>
                      {["Type", "Combo", "Games", "Win", "WR"].map((h) => <th key={h} style={{ padding: "7px 6px", textAlign: "left", color: T.muted, fontSize: 10, textTransform: "uppercase", background: T.tintLg, borderBottom: `2px solid ${T.border}` }}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {tierOpponentComboRows.length === 0 && <tr><td colSpan={5} style={{ padding: 12, textAlign: "center", color: T.muted }}>ยังไม่มีข้อมูล combo</td></tr>}
                    {tierOpponentComboRows.slice(0, 12).map((r, i) => (
                      <tr key={`opp-combo-${r.combo}-${i}`}>
                        <td style={tdS}>{r.type}</td>
                        <td style={{ ...tdS, minWidth: 170 }}>{r.combo}</td>
                        <td style={tdS}>{r.total}</td>
                        <td style={tdS}>{r.win}</td>
                        <td style={tdS}>{r.wr.toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </GlassCard>

        <GlassCard style={{ padding: "18px 20px", marginTop: 16 }} neon delay={3.05}>
          <SectionTitle icon={BarChart3} title="Hero Stat Gap: Scrim vs Match" subtitle="เทียบค่าสเตทฮีโร่ของแต่ละทีม ระหว่างซ้อมกับแข่ง (Official - Scrim)" />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 8, marginTop: 8 }}>
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: "8px 10px", background: T.tintXs }}>
              <div style={{ fontSize: 10, color: T.muted }}>{OUR_TEAM} Hero Rows</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: T.primaryLight }}>{fsOpponentHeroGap.fsCount}</div>
              <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>Avg ΔDPM <strong style={{ color: T.text }}>{fsOpponentHeroGap.fsAvgDeltaDpm}</strong></div>
            </div>
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: "8px 10px", background: T.tintXs }}>
              <div style={{ fontSize: 10, color: T.muted }}>{compareOpponent || "Opponent"} Hero Rows</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: T.accent }}>{fsOpponentHeroGap.opCount}</div>
              <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>Avg ΔDPM <strong style={{ color: T.text }}>{fsOpponentHeroGap.opAvgDeltaDpm}</strong></div>
            </div>
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: "8px 10px", background: T.tintXs }}>
              <div style={{ fontSize: 10, color: T.muted }}>{OUR_TEAM} Avg ΔGPM / ΔKDA</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: T.text }}>{fsOpponentHeroGap.fsAvgDeltaGpm} / {fsOpponentHeroGap.fsAvgDeltaKda}</div>
            </div>
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: "8px 10px", background: T.tintXs }}>
              <div style={{ fontSize: 10, color: T.muted }}>{compareOpponent || "Opponent"} Avg ΔGPM / ΔKDA</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: T.text }}>{fsOpponentHeroGap.opAvgDeltaGpm} / {fsOpponentHeroGap.opAvgDeltaKda}</div>
            </div>
          </div>
          <div className="tp-scroll" style={{ overflowX: "auto", maxHeight: simpleView ? 320 : 380, overflowY: "auto", marginTop: 10 }}>
            <table className="tp-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr>
                  {["Team", "Hero", "Role", "Scrim G", "Off G", "Scrim WR", "Off WR", "ΔWR", "Scrim GPM", "Off GPM", "ΔGPM", "Scrim DPM", "Off DPM", "ΔDPM", "Scrim KDA", "Off KDA", "ΔKDA"].map((h) => (
                    <th key={h} style={{ padding: "8px 6px", whiteSpace: "nowrap", textAlign: "left", color: T.muted, fontSize: 10, textTransform: "uppercase", background: T.tintLg, borderBottom: `2px solid ${T.border}` }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {heroStatGapRows.length === 0 && (
                  <tr>
                    <td colSpan={17} style={{ padding: 14, color: T.muted, textAlign: "center" }}>
                      ยังไม่มีข้อมูลสเตทฮีโร่ที่มีทั้ง Scrim และ Match ตามเงื่อนไข
                    </td>
                  </tr>
                )}
                {heroStatGapRows.slice(0, 120).map((r) => (
                  <tr key={`${r.team}-${r.hero}-${r.role}`}>
                    <td style={tdS}><span style={{ color: T.primaryLight, fontWeight: 700 }}>{r.team}</span></td>
                    <td style={tdS}>{r.hero}</td>
                    <td style={tdS}><span style={{ color: T.accent }}>{r.role}</span></td>
                    <td style={tdS}>{r.scrimGames}</td>
                    <td style={tdS}>{r.officialGames}</td>
                    <td style={tdS}>{fmtMetric(r.scrimWR)}%</td>
                    <td style={tdS}>{fmtMetric(r.officialWR)}%</td>
                    <td style={tdS}>{r.deltaWR == null ? "-" : <DeltaCell value={r.deltaWR} />}</td>
                    <td style={tdS}>{fmtMetric(r.scrimGpm)}</td>
                    <td style={tdS}>{fmtMetric(r.officialGpm)}</td>
                    <td style={tdS}>{r.deltaGpm == null ? "-" : <DeltaCell value={r.deltaGpm} />}</td>
                    <td style={tdS}>{fmtMetric(r.scrimDpm)}</td>
                    <td style={tdS}>{fmtMetric(r.officialDpm)}</td>
                    <td style={tdS}>{r.deltaDpm == null ? "-" : <DeltaCell value={r.deltaDpm} />}</td>
                    <td style={tdS}>{fmtMetric(r.scrimKda)}</td>
                    <td style={tdS}>{fmtMetric(r.officialKda)}</td>
                    <td style={tdS}>{r.deltaKda == null ? "-" : <DeltaCell value={r.deltaKda} />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </GlassCard>

        {!simpleView && (
        <GlassCard style={{ padding: "18px 20px", marginTop: 16 }} neon delay={3.2}>
          <SectionTitle
            icon={Target}
            title="Role Deep Stats vs League Avg"
            subtitle="เทียบ GPM / DMG per min / KDA รายตำแหน่ง กับค่าเฉลี่ยลีก (ตาม scope ที่เลือก)"
          />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 10, marginTop: 8 }}>
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, background: T.tintXs, padding: "10px 12px" }}>
              <div style={{ color: T.muted, fontSize: 11, textTransform: "uppercase" }}>League First Blood Avg</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: T.primaryLight }}>
                {fmtMetric(leagueRoleBenchmark.leagueFirstBloodRate)}%
              </div>
            </div>
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, background: T.tintXs, padding: "10px 12px" }}>
              <div style={{ color: T.muted, fontSize: 11, textTransform: "uppercase" }}>Role Benchmark Rows</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: T.accent }}>{benchmarkRowsView.length}</div>
            </div>
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, background: T.tintXs, padding: "10px 12px" }}>
              <div style={{ color: T.muted, fontSize: 11, textTransform: "uppercase" }}>First Blood Teams</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: T.warn }}>{firstBloodView.length}</div>
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <div style={{ color: T.muted, fontSize: 12, marginBottom: 6 }}>Role Metrics (Team vs League)</div>
            <div className="tp-scroll" style={{ overflowX: "auto", maxHeight: 300, overflowY: "auto" }}>
              <table className="tp-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr>
                    {["Team", "Role", "GPM", "League GPM", "ΔGPM", "DMG/min", "League DMG/min", "ΔDMG/min", "KDA", "League KDA", "ΔKDA", "Rows"].map((h) => (
                      <th key={h} style={{ padding: "9px 8px", whiteSpace: "nowrap", textAlign: "left", color: T.muted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", background: T.tintLg, borderBottom: `2px solid ${T.border}` }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {benchmarkRowsView.length === 0 && (
                    <tr>
                      <td colSpan={12} style={{ padding: 16, color: T.muted, textAlign: "center" }}>
                        ยังไม่มีข้อมูล role deep stats ตามเงื่อนไข
                      </td>
                    </tr>
                  )}
                  {benchmarkRowsView.map((r) => (
                    <tr key={`${r.team}-${r.role}-${r.bucket}`}>
                      <td style={tdS}><span style={{ color: T.primaryLight, fontWeight: 700 }}>{r.team}</span></td>
                      <td style={tdS}><span style={{ color: T.accent, fontWeight: 700 }}>{r.role}</span></td>
                      <td style={tdS}>{fmtMetric(r.gpm)}</td>
                      <td style={tdS}>{fmtMetric(r.leagueGpm)}</td>
                      <td style={tdS}>{r.deltaGpm == null ? "-" : <DeltaCell value={r.deltaGpm} />}</td>
                      <td style={tdS}>{fmtMetric(r.dpm)}</td>
                      <td style={tdS}>{fmtMetric(r.leagueDpm)}</td>
                      <td style={tdS}>{r.deltaDpm == null ? "-" : <DeltaCell value={r.deltaDpm} />}</td>
                      <td style={tdS}>{fmtMetric(r.kda)}</td>
                      <td style={tdS}>{fmtMetric(r.leagueKda)}</td>
                      <td style={tdS}>{r.deltaKda == null ? "-" : <DeltaCell value={r.deltaKda} />}</td>
                      <td style={tdS}>{r.rows}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <div style={{ color: T.muted, fontSize: 12, marginBottom: 6 }}>First Blood Control</div>
            <div className="tp-scroll" style={{ overflowX: "auto", maxHeight: 240, overflowY: "auto" }}>
              <table className="tp-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr>
                    {["Team", "Games", "First Blood", "FB Rate", "League Avg", "ΔFB"].map((h) => (
                      <th key={h} style={{ padding: "9px 8px", whiteSpace: "nowrap", textAlign: "left", color: T.muted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", background: T.tintLg, borderBottom: `2px solid ${T.border}` }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {firstBloodView.length === 0 && (
                    <tr>
                      <td colSpan={6} style={{ padding: 16, color: T.muted, textAlign: "center" }}>
                        ยังไม่มีข้อมูล First Blood ตามเงื่อนไข
                      </td>
                    </tr>
                  )}
                  {firstBloodView.map((r) => (
                    <tr key={`${r.team}-${r.bucket}`}>
                      <td style={tdS}><span style={{ color: T.primaryLight, fontWeight: 700 }}>{r.team}</span></td>
                      <td style={tdS}>{r.games}</td>
                      <td style={tdS}>{r.firstBlood}</td>
                      <td style={tdS}>{fmtMetric(r.firstBloodRate)}%</td>
                      <td style={tdS}>{fmtMetric(r.leagueFirstBloodRate)}%</td>
                      <td style={tdS}><DeltaCell value={r.deltaFirstBloodRate} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </GlassCard>
        )}

        {!simpleView && (
        <GlassCard style={{ padding: "18px 20px", marginTop: 16 }} neon delay={3.6}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <SectionTitle icon={Swords} title="Preference Comp / Ban" subtitle="ดึงจาก match data จริง: ทีมชอบหยิบ/แบนอะไร และคอมป์ที่ใช้บ่อย" />
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <select className="tp-select" value={draftTeam} onChange={(e) => setDraftTeam(e.target.value)} style={{ minWidth: 140 }}>
                {draftTeamOptions.length === 0 && <option value="">No team</option>}
                {draftTeamOptions.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))", gap: 12, marginTop: 10 }}>
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, background: T.tintXs, padding: "10px 12px" }}>
              <div style={{ color: T.primaryLight, fontWeight: 800, marginBottom: 8 }}>
                {selectedDraftTeam ? `${selectedDraftTeam.team} Hero Preference` : "Hero Preference"}
              </div>
              <div className="tp-scroll" style={{ maxHeight: 260, overflowY: "auto", overflowX: "auto" }}>
                <table className="tp-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr>
                      {["Hero", "Pick", "Ban", "Presence", "Pick Rate", "Ban Rate", "WR when Picked"].map((h) => (
                        <th key={h} style={{ padding: "8px 6px", textAlign: "left", color: T.muted, fontSize: 10, textTransform: "uppercase", background: T.tintLg, borderBottom: `2px solid ${T.border}` }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {!selectedDraftTeam && <tr><td colSpan={7} style={{ padding: 12, color: T.muted, textAlign: "center" }}>ยังไม่มีข้อมูลทีมตามเงื่อนไข</td></tr>}
                    {selectedDraftTeam?.heroes?.slice(0, 14).map((h) => (
                      <tr key={`${selectedDraftTeam.team}-${h.hero}`}>
                        <td style={tdS}>{h.hero}</td>
                        <td style={tdS}>{h.pick}</td>
                        <td style={tdS}>{h.ban}</td>
                        <td style={tdS}>{h.presence}</td>
                        <td style={tdS}>{fmtMetric(h.pickRate)}%</td>
                        <td style={tdS}>{fmtMetric(h.banRate)}%</td>
                        <td style={tdS}>{fmtMetric(h.winRate)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, background: T.tintXs, padding: "10px 12px" }}>
              <div style={{ color: T.accent, fontWeight: 800, marginBottom: 8 }}>
                {selectedDraftTeam ? `${selectedDraftTeam.team} Most Used Compositions` : "Most Used Compositions"}
              </div>
              <div className="tp-scroll" style={{ maxHeight: 260, overflowY: "auto", overflowX: "auto" }}>
                <table className="tp-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr>
                      {["Comp", "Games", "WR"].map((h) => (
                        <th key={h} style={{ padding: "8px 6px", textAlign: "left", color: T.muted, fontSize: 10, textTransform: "uppercase", background: T.tintLg, borderBottom: `2px solid ${T.border}` }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {!selectedDraftTeam && <tr><td colSpan={3} style={{ padding: 12, color: T.muted, textAlign: "center" }}>ยังไม่มีข้อมูล comp ตามเงื่อนไข</td></tr>}
                    {selectedDraftTeam?.comps?.slice(0, 10).map((c) => (
                      <tr key={`${selectedDraftTeam.team}-${c.comp}`}>
                        <td style={{ ...tdS, minWidth: 220 }}>{c.comp}</td>
                        <td style={tdS}>{c.count}</td>
                        <td style={tdS}>{fmtMetric(c.winRate)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div style={{ marginTop: 12, border: `1px solid ${T.border}`, borderRadius: 12, background: T.tintXs, padding: "10px 12px" }}>
            <div style={{ color: T.warn, fontWeight: 800, marginBottom: 8 }}>
              {compareOpponent ? `Opponent Tendency: ${compareOpponent}` : "Opponent Tendency"}
            </div>
            <div className="tp-scroll" style={{ maxHeight: 220, overflowY: "auto", overflowX: "auto" }}>
              <table className="tp-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr>
                    {["Hero", "Pick", "Ban", "Presence", "Pick Rate", "Ban Rate"].map((h) => (
                      <th key={h} style={{ padding: "8px 6px", textAlign: "left", color: T.muted, fontSize: 10, textTransform: "uppercase", background: T.tintLg, borderBottom: `2px solid ${T.border}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {!selectedOpponentPref && <tr><td colSpan={6} style={{ padding: 12, color: T.muted, textAlign: "center" }}>ยังไม่มี opponent tendency ตามเงื่อนไข</td></tr>}
                  {selectedOpponentPref?.heroes?.slice(0, 12).map((h) => (
                    <tr key={`${selectedOpponentPref.opponent}-${h.hero}`}>
                      <td style={tdS}>{h.hero}</td>
                      <td style={tdS}>{h.pick}</td>
                      <td style={tdS}>{h.ban}</td>
                      <td style={tdS}>{h.presence}</td>
                      <td style={tdS}>{fmtMetric(h.pickRate)}%</td>
                      <td style={tdS}>{fmtMetric(h.banRate)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </GlassCard>
        )}

        <GlassCard style={{ padding: "18px 20px", marginTop: 16 }} neon delay={4}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
            <SectionTitle icon={Target} title="🎯 Actionable Feedback Plan" subtitle="รายการงานที่ลงมือซ้อมต่อได้ทันที (Team / Role / Priority)" />
            <div style={{ color: T.muted, fontSize: 12 }}>แสดง {actionPlanRows.length} รายการ</div>
          </div>
          <div className="tp-scroll" style={{ overflowX: "auto", maxHeight: 340, overflowY: "auto" }}>
            <table className="tp-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  {["Priority", "Team", "Role", "Focus", "Action", "Metric"].map((h) => (
                    <th key={h} style={{ padding: "10px 8px", whiteSpace: "nowrap", textAlign: "left", color: T.muted, fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", background: T.tintLg, borderBottom: `2px solid ${T.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {actionPlanRows.length === 0 && <tr><td colSpan={6} style={{ padding: 20, color: T.muted, textAlign: "center" }}>ยังไม่มี action plan ตามเงื่อนไขที่เลือก</td></tr>}
                {actionPlanRows.map((r, i) => {
                  const pColor = r.priority === "High" ? T.danger : r.priority === "Medium" ? T.warn : T.accent;
                  return (
                    <tr key={`${r.team}-${r.role}-${r.focus}-${i}`}>
                      <td style={tdS}>
                        <span style={{ fontSize: 11, fontWeight: 800, color: pColor, border: `1px solid ${pColor}66`, background: `${pColor}22`, borderRadius: 999, padding: "2px 8px" }}>
                          {r.priority}
                        </span>
                      </td>
                      <td style={tdS}><span style={{ color: T.primaryLight, fontWeight: 700 }}>{r.team}</span></td>
                      <td style={tdS}><span style={{ color: T.accent, fontWeight: 700 }}>{r.role}</span></td>
                      <td style={tdS}>{r.focus}</td>
                      <td style={{ ...tdS, minWidth: 320 }}>{r.action}</td>
                      <td style={tdS}>{r.metric}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </GlassCard>

        {!simpleView && (
        <>
        <GlassCard style={{ padding: "18px 20px", marginTop: 16 }} neon delay={4.2}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <SectionTitle icon={BarChart3} title="Hero 360 Multi-Dimension (Scrim vs Match)" subtitle="ทุกมิติรายฮีโร่: ผลลัพธ์, การใช้งาน, แบน/แรงกดดัน, ความเสถียร, macro gap ราย role" />
            <div style={{ color: T.muted, fontSize: 12 }}>Rows {heroDimensionSummary.rows}</div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 8, marginTop: 8 }}>
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: "8px 10px", background: T.tintXs }}>
              <div style={{ fontSize: 10, color: T.muted }}>Core</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: T.success }}>{heroDimensionSummary.core}</div>
            </div>
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: "8px 10px", background: T.tintXs }}>
              <div style={{ fontSize: 10, color: T.muted }}>Playable</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: T.warn }}>{heroDimensionSummary.playable}</div>
            </div>
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: "8px 10px", background: T.tintXs }}>
              <div style={{ fontSize: 10, color: T.muted }}>Train</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: T.danger }}>{heroDimensionSummary.train}</div>
            </div>
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: "8px 10px", background: T.tintXs }}>
              <div style={{ fontSize: 10, color: T.muted }}>Avg Composite</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: T.accent }}>{heroDimensionSummary.avgComposite}</div>
            </div>
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: "8px 10px", background: T.tintXs }}>
              <div style={{ fontSize: 10, color: T.muted }}>Avg Train Priority</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: T.primaryLight }}>{heroDimensionSummary.avgTrainPriority}</div>
            </div>
          </div>

          <div className="tp-scroll" style={{ overflowX: "auto", maxHeight: 380, overflowY: "auto", marginTop: 10 }}>
            <table className="tp-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr>
                  {["Priority", "Status", "Team", "Hero", "Role", "SWR", "MWR", "ΔWR", "SPR", "MPR", "ΔPR", "SBan", "MBan", "Execution", "Usage", "Pressure", "Stability", "Macro", "Composite"].map((h) => (
                    <th key={h} style={{ padding: "9px 8px", whiteSpace: "nowrap", textAlign: "left", color: T.muted, fontSize: 10, fontWeight: 700, textTransform: "uppercase", background: T.tintLg, borderBottom: `2px solid ${T.border}` }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {heroDimensionRows.length === 0 && (
                  <tr>
                    <td colSpan={19} style={{ padding: 16, color: T.muted, textAlign: "center" }}>
                      ยังไม่มีข้อมูล Hero 360 ตามเงื่อนไข
                    </td>
                  </tr>
                )}
                {heroDimensionRows.slice(0, 120).map((r) => (
                  <tr key={`${r.team}-${r.hero}`}>
                    <td style={tdS}><strong style={{ color: r.trainPriority >= 30 ? T.danger : r.trainPriority >= 18 ? T.warn : T.success }}>{r.trainPriority.toFixed(1)}</strong></td>
                    <td style={tdS}><span style={{ color: r.status === "Core" ? T.success : r.status === "Playable" ? T.warn : T.danger, fontWeight: 700 }}>{r.status}</span></td>
                    <td style={tdS}><span style={{ color: T.primaryLight, fontWeight: 700 }}>{r.team}</span></td>
                    <td style={tdS}>{r.hero}</td>
                    <td style={tdS}><span style={{ color: T.accent }}>{r.role}</span></td>
                    <td style={tdS}>{r.scrimWR.toFixed(1)}%</td>
                    <td style={tdS}>{r.officialWR.toFixed(1)}%</td>
                    <td style={tdS}><DeltaCell value={r.deltaWR} /></td>
                    <td style={tdS}>{r.scrimPR.toFixed(1)}%</td>
                    <td style={tdS}>{r.officialPR.toFixed(1)}%</td>
                    <td style={tdS}><DeltaCell value={r.deltaPR} /></td>
                    <td style={tdS}>{r.scrimBanRate.toFixed(1)}%</td>
                    <td style={tdS}>{r.officialBanRate.toFixed(1)}%</td>
                    <td style={tdS}>{r.executionScore}</td>
                    <td style={tdS}>{r.usageScore}</td>
                    <td style={tdS}>{r.pressureScore}</td>
                    <td style={tdS}>{r.stabilityScore}</td>
                    <td style={tdS}>{r.macroScore}</td>
                    <td style={tdS}><strong style={{ color: r.composite >= 70 ? T.success : r.composite >= 55 ? T.warn : T.danger }}>{r.composite}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </GlassCard>

        {/* ── CHARTS ROW ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(460px,1fr))", gap: 14, marginTop: 16 }}>
          <GlassCard style={{ padding: "18px 16px", border: `1.5px solid ${T.primary}`, boxShadow: T.glowSm }} delay={5}>
            <SectionTitle icon={BarChart3} title="Quadrant Breakdown" subtitle={analysisMode === "with_scrim" ? "สรุปจำนวนฮีโร่ในแต่ละโซน พร้อมค่าเฉลี่ย ΔWR/ΔPR" : "ต้องใช้โหมดรวมผลซ้อม + แข่ง เพื่อดู Delta"} />
            <DeltaScatterChart data={analysisDelta} teamList={uniqueTeams} />
            {coachMode && <CoachHints items={coachHints.quadrant} />}
          </GlassCard>

          <GlassCard style={{ padding: "18px 16px", border: `1.5px solid ${T.borderHover}` }} delay={5.5}>
            <SectionTitle icon={Activity} title="Scrim vs Match Day Win Rate" subtitle={analysisMode === "with_scrim" ? "Top 10 heroes ตาม |ΔWinRate| สูงสุด (อ่านง่ายขึ้น)" : "โหมดแข่งล้วนจะไม่แสดงการเทียบ scrim"} />
            <WinRateCompareChart data={analysisDelta} />
            {coachMode && <CoachHints items={coachHints.winrate} />}
          </GlassCard>

          <GlassCard style={{ padding: "18px 16px", border: `1.5px solid rgba(255,16,38,0.30)` }} delay={5.8}>
            <SectionTitle icon={TrendingUp} title="Scrim -> Match Slope" subtitle={analysisMode === "with_scrim" ? "ดูการเปลี่ยน WR ต่อฮีโร่แบบเส้นตรง อ่านจุดตก/พุ่งได้เร็ว" : "โหมดแข่งล้วนจะไม่แสดง slope ระหว่าง scrim กับ match"} />
            <ScrimMatchSlopeChart data={analysisDelta} />
          </GlassCard>

          <GlassCard style={{ padding: "18px 16px" }} delay={6}>
            <SectionTitle icon={PieChartIcon} title="Coverage Breakdown" subtitle="สัดส่วนข้อมูล Scrim / Match Day / Other" />
            <CoverageDonut stats={stats} />
            {coachMode && <CoachHints items={coachHints.coverage} />}
          </GlassCard>

          <GlassCard style={{ padding: "18px 16px" }} delay={6.5}>
            <SectionTitle icon={Trophy} title="Team Priority" subtitle="Train More (ซ้อมดี แต่แข่งไม่ดี) vs Stage Ready (พร้อมแข่ง)" />
            <PriorityBarChart priority={priority} />
            {coachMode && <CoachHints items={coachHints.teamPriority} />}
          </GlassCard>
        </div>

        <GlassCard style={{ padding: "18px 20px", marginTop: 16 }} neon delay={6.8}>
          <SectionTitle icon={Activity} title="📈 Team Scrim vs Match Gap" subtitle={analysisMode === "with_scrim" ? "เทียบค่าเฉลี่ยรายทีม: WR / Pick Rate / Presence (แข่ง - ซ้อม)" : "สลับเป็นโหมดรวมผลซ้อม + แข่ง เพื่อดูช่องว่างรายทีม"} />
          <TeamGapBarChart rows={teamGapSummary} />
          <div className="tp-scroll" style={{ overflowX: "auto", marginTop: 12 }}>
            <table className="tp-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  {["Team", "Heroes", "Avg Scrim WR", "Avg Match WR", "Avg ΔWR", "Avg ΔPR", "Avg ΔPresence", "Best Hero", "Risk Hero"].map((h) => (
                    <th key={h} style={{ padding: "10px 8px", whiteSpace: "nowrap", textAlign: "left", color: T.muted, fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", background: T.tintLg, borderBottom: `2px solid ${T.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {teamGapSummary.length === 0 && <tr><td colSpan={9} style={{ padding: 20, color: T.muted, textAlign: "center" }}>ยังไม่มีข้อมูลช่องว่างรายทีมตามเงื่อนไข</td></tr>}
                {teamGapSummary.map((r) => (
                  <tr key={r.team}>
                    <td style={tdS}><span style={{ color: T.primaryLight, fontWeight: 700 }}>{r.team}</span></td>
                    <td style={tdS}>{r.heroes}</td>
                    <td style={tdS}>{r.avgScrimWR.toFixed(2)}%</td>
                    <td style={tdS}>{r.avgOfficialWR.toFixed(2)}%</td>
                    <td style={tdS}><DeltaCell value={r.avgDeltaWR} /></td>
                    <td style={tdS}><DeltaCell value={r.avgDeltaPR} /></td>
                    <td style={tdS}><DeltaCell value={r.avgDeltaPresence} /></td>
                    <td style={tdS}>{r.topUpHero}</td>
                    <td style={tdS}>{r.topDownHero}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </GlassCard>

        {/* ══════════════════════════════════════════════════════
           CROSS-TEAM HERO COMPARISON
           ══════════════════════════════════════════════════════ */}
        <GlassCard style={{ padding: "18px 20px", marginTop: 16 }} neon delay={7}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
            <SectionTitle icon={Swords} title="🔀 Cross-Team Hero Comparison" subtitle={analysisMode === "with_scrim" ? "เทียบ Scrim WR vs Match WR ของฮีโร่เดียวกันข้ามทีม" : "เทียบฮีโร่เดียวกันข้ามทีม — โหมดแข่งล้วน"} />
            <select className="tp-select" value={selectedHero} onChange={e => setSelectedHero(e.target.value)} style={{ maxWidth: 220 }}>
              <option value="">เลือก Hero (Top pick)</option>
              {crossTeamData.slice(0, 40).map(h => (
                <option key={h.hero} value={h.hero}>{h.hero} ({h.teamCount} ทีม, {h.totalPicks} picks)</option>
              ))}
            </select>
          </div>
          {selectedCross ? (
            <div>
              <div style={{ fontSize: 20, fontWeight: 900, color: T.primaryLight, marginBottom: 4 }}>{selectedCross.hero}</div>
              <div style={{ color: T.muted, fontSize: 12, marginBottom: 16 }}>ใช้โดย {selectedCross.teamCount} ทีม — รวม {selectedCross.totalPicks} picks</div>
              <ChartPanel minHeight={Math.max(250, selectedCrossDeltaRows.length * 34 + 82)}>
                <ResponsiveContainer width="100%" height={Math.max(240, selectedCrossDeltaRows.length * 34 + 70)}>
                  <LineChart data={selectedCrossDeltaRows} margin={{ top: 6, right: 24, bottom: 6, left: 55 }}>
                    <CartesianGrid {...CHART_GRID} />
                    <XAxis dataKey="name" stroke={T.axisStroke} tick={CHART_AXIS_TICK} interval={0} />
                    <YAxis stroke={T.axisStroke} tick={CHART_AXIS_TICK} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                    <Tooltip content={<ChartTooltip formatter={(v) => `${num(v).toFixed(1)}%`} />} />
                    <Legend wrapperStyle={CHART_LEGEND_STYLE} />
                    <Line type="monotone" dataKey="scrim" name="Scrim WR" stroke={T.primaryLight} strokeWidth={2.4} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="official" name="Match WR" stroke={T.primaryDark} strokeWidth={2.4} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </ChartPanel>
              {/* Detail table */}
              <div className="tp-scroll" style={{ overflowX: "auto", marginTop: 12 }}>
                <table className="tp-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr>
                      {["Team", "Scrim WR", "Match WR", "ΔWR", "Scrim PR", "Match PR", "Scrim Pick", "Match Pick"].map(h => (
                        <th key={h} style={{ padding: "10px 8px", whiteSpace: "nowrap", textAlign: "left", color: T.muted, fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", background: T.tintLg, borderBottom: `2px solid ${T.border}` }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {selectedCross.teams.map(t => (
                      <tr key={t.team}>
                        <td style={tdS}><span style={{ color: T.primaryLight, fontWeight: 700 }}>{t.team}</span></td>
                        <td style={tdS}>{t.scrimWR != null ? t.scrimWR.toFixed(1) + "%" : "-"}</td>
                        <td style={tdS}>{t.officialWR != null ? t.officialWR.toFixed(1) + "%" : "-"}</td>
                        <td style={tdS}>{t.deltaWR != null ? <DeltaCell value={t.deltaWR} /> : "-"}</td>
                        <td style={tdS}>{t.scrimPR != null ? t.scrimPR.toFixed(1) + "%" : "-"}</td>
                        <td style={tdS}>{t.officialPR != null ? t.officialPR.toFixed(1) + "%" : "-"}</td>
                        <td style={tdS}>{t.scrimPick}</td>
                        <td style={tdS}>{t.officialPick}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div style={{ color: T.muted, fontSize: 13, textAlign: "center", padding: 30 }}>ไม่มีข้อมูล Cross-Team ตามเงื่อนไข</div>
          )}
        </GlassCard>

        {/* ══════════════════════════════════════════════════════
           HERO META BY POSITION
           ══════════════════════════════════════════════════════ */}
        <GlassCard style={{ padding: "18px 20px", marginTop: 16 }} delay={8}>
          <SectionTitle icon={Trophy} title="🎯 Hero Meta by Position" subtitle="วิเคราะห์ Hero Meta ตามตำแหน่ง — คำแนะนำ Pool Hero สำหรับฝึกซ้อม" />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 14, marginTop: 14 }}>
            {["DSL", "JUG", "MID", "ADL", "SUP"].map(pos => {
              const posIcons = { DSL: "⚔️", JUG: "🌲", MID: "🔮", ADL: "🎯", SUP: "💚" };
              const posLabels = { DSL: "Dark Slayer", JUG: "Jungle", MID: "Mid Lane", ADL: "Abyssal Dragon", SUP: "Support" };
              const heroes = (heroMeta[pos] || []).slice(0, 8);
              return (
                <div key={pos} style={{ background: T.glass, border: `1px solid ${T.border}`, borderRadius: 14, padding: "14px 16px", position: "relative", overflow: "hidden" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: `linear-gradient(135deg,${T.primary},${T.primaryDark})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>{posIcons[pos]}</div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: T.primaryLight }}>{pos}</div>
                      <div style={{ fontSize: 10, color: T.muted }}>{posLabels[pos]}</div>
                    </div>
                    <div style={{ marginLeft: "auto", fontSize: 11, color: T.muted }}>{heroes.length} heroes</div>
                  </div>
                  {heroes.length === 0 && <div style={{ color: T.muted, fontSize: 12, padding: "8px 0" }}>ยังไม่มีข้อมูล</div>}
                  {heroes.map((h, i) => (
                    <div key={h.hero} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderTop: i > 0 ? "1px solid rgba(255,16,38,0.06)" : "none" }}>
                      <div style={{ minWidth: 20, fontSize: 12, color: T.muted, fontWeight: 700 }}>#{i + 1}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: T.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{h.hero}</div>
                        <div style={{ fontSize: 10, color: T.muted }}>
                          WR {h.wr}% · PR {h.pr}% · {h.pick} picks · {h.teamCount} ทีม
                          {h.avgDeltaWR != null && <span style={{ color: h.avgDeltaWR >= 0 ? T.success : T.danger }}> · Δ{h.avgDeltaWR > 0 ? "+" : ""}{h.avgDeltaWR}%</span>}
                        </div>
                        {h.recommendation && <div style={{ fontSize: 10, color: h.statusColor, marginTop: 2 }}>{h.recommendation}</div>}
                      </div>
                      <div style={{ padding: "3px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700, background: `${h.statusColor}18`, color: h.statusColor, whiteSpace: "nowrap", flexShrink: 0 }}>{h.status}</div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </GlassCard>
        </>
        )}

        {/* ── DATA TABLE + SIDEBAR ── */}
        <div style={{ display: "grid", gridTemplateColumns: simpleView ? "minmax(0,1fr)" : "minmax(0,2.2fr) minmax(280px,1fr)", gap: 14, marginTop: 16 }}>

          {/* main table */}
          <GlassCard style={{ padding: "18px 16px" }} neon delay={7}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <SectionTitle icon={viewMode === "delta" ? Zap : Layers} title={viewMode === "delta" ? "Scrim vs Match Day Delta" : "Raw Rows (No Aggregate)"} />
              <div style={{ color: T.muted, fontSize: 11 }}>แสดงสูงสุด 200 แถว</div>
            </div>
            {viewMode === "raw" && isOnlyInferredMode && (
              <div style={{ marginBottom: 8, border: `1px solid ${T.warn}`, background: "rgba(255,209,102,0.12)", borderRadius: 10, padding: "8px 10px", color: T.warn, fontSize: 12, fontWeight: 700 }}>
                โหมดนี้กำลังแสดงเฉพาะข้อมูลที่ระบบ infer
              </div>
            )}
            <div className="tp-scroll" style={{ overflowX: "auto", maxHeight: 600, overflowY: "auto" }}>
              {viewMode === "delta" ? (
                <table className="tp-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr>
                      {["Team", "Hero", "Role", "Scrim WR", "Match Day WR", "ΔWR", "Scrim PR", "Match Day PR", "ΔPR", "ΔPresence"].map(h => (
                        <th key={h} style={{ padding: "10px 8px", whiteSpace: "nowrap", textAlign: "left", color: T.muted, fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", background: T.tintLg, borderBottom: `2px solid ${T.border}` }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDelta.length === 0 && <tr><td colSpan={10} style={{ padding: 20, color: T.muted, textAlign: "center" }}>ไม่พบข้อมูล Delta ตามเงื่อนไขที่เลือก</td></tr>}
                    {filteredDelta.slice(0, 200).map(r => (
                      <tr key={`${r.team}-${r.hero}`}>
                        <td style={tdS}><span style={{ color: T.primaryLight, fontWeight: 700 }}>{r.team}</span></td>
                        <td style={tdS}>{r.hero}</td>
                        <td style={tdS}><span style={{ color: T.accent, fontWeight: 600 }}>{r.role}</span></td>
                        <td style={tdS}>{num(r.scrimWinRate).toFixed(2)}</td>
                        <td style={tdS}>{num(r.officialWinRate).toFixed(2)}</td>
                        <td style={tdS}><DeltaCell value={r.deltaWinRate} /></td>
                        <td style={tdS}>{num(r.scrimPickRate).toFixed(2)}</td>
                        <td style={tdS}>{num(r.officialPickRate).toFixed(2)}</td>
                        <td style={tdS}><DeltaCell value={r.deltaPickRate} /></td>
                        <td style={tdS}><DeltaCell value={r.deltaPresenceRate} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="tp-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr>
                      {["Date", "Team", "Opponent", "Bucket", "Result", "Type", "SourceKind", "Source", "Picks", "Bans"].map(h => (
                        <th key={h} style={{ padding: "10px 8px", whiteSpace: "nowrap", textAlign: "left", color: T.muted, fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", background: T.tintLg, borderBottom: `2px solid ${T.border}` }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRaw.length === 0 && <tr><td colSpan={10} style={{ padding: 20, color: T.muted, textAlign: "center" }}>ไม่พบข้อมูล Raw Rows ตามเงื่อนไขที่เลือก</td></tr>}
                    {[...filteredRaw].sort((a, b) => String(b.date || "").localeCompare(String(a.date || ""))).slice(0, 200).map((r, i) => (
                      <tr key={`${r.team}-${r.date}-${r.game}-${i}`} style={isOnlyInferredMode ? { background: "rgba(255,209,102,0.06)" } : undefined}>
                        <td style={tdS}>{r.date || "-"}</td>
                        <td style={tdS}><span style={{ color: T.primaryLight, fontWeight: 700 }}>{r.team}</span></td>
                        <td style={tdS}>{r.competitor || "-"}</td>
                        <td style={tdS}>
                          {bucketLabel(r.bucket)}
                          {showInferBadge && isInferredType(r.type) && <span style={{ marginLeft: 6, fontSize: 10, color: T.warn, fontWeight: 700 }}>[infer]</span>}
                        </td>
                        <td style={{ ...tdS, color: r.result === "Win" ? T.success : r.result === "Lose" ? T.danger : T.text, fontWeight: 700 }}>{r.result}</td>
                        <td style={tdS}>{r.type || "-"}</td>
                        <td style={tdS}>{r.sourceKind || "-"}</td>
                        <td style={tdS}>{r.sourceSheet}</td>
                        <td style={{ ...tdS, minWidth: 190 }}><HeroCells heroes={r.picks || []} mode={heroCellMode} /></td>
                        <td style={{ ...tdS, minWidth: 140 }}><HeroCells heroes={r.bans || []} mode={heroCellMode} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </GlassCard>

          {/* sidebar panels */}
          {!simpleView && (
          <div style={{ display: "grid", gap: 14, alignContent: "start" }}>

            {/* Hero Pool Health */}
            <GlassCard style={{ padding: "16px 18px" }} delay={7.8}>
              <SectionTitle icon={Shield} title="Hero Pool Health" />
              {heroPoolHealthRows.length === 0 && <div style={{ color: T.muted, fontSize: 13, marginTop: 8 }}>ยังไม่มีข้อมูล health ตามเงื่อนไข</div>}
              <div style={{ marginTop: 8 }}>
                {heroPoolHealthRows.map((r) => (
                  <div key={`${r.team}-${r.role}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid rgba(255,16,38,0.06)", padding: "9px 0", gap: 8 }}>
                    <div style={{ fontSize: 12 }}>
                      <div><span style={{ color: T.primaryLight, fontWeight: 700 }}>{r.team}</span> <span style={{ color: T.accent }}>{r.role}</span></div>
                      <div style={{ color: T.muted, marginTop: 2 }}>Depth {r.depth} · Core {r.core} · Weak {r.weak}</div>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 999, border: `1px solid ${r.statusColor}66`, background: `${r.statusColor}22`, color: r.statusColor }}>
                      {r.status}
                    </span>
                  </div>
                ))}
              </div>
            </GlassCard>

            {/* Role Pool */}
            <GlassCard style={{ padding: "16px 18px" }} delay={8}>
              <SectionTitle icon={Shield} title="Role Pool (Match Day)" />
              {roleRecs.slice(0, 16).length === 0 && <div style={{ color: T.muted, fontSize: 13, marginTop: 8 }}>ยังไม่มีข้อมูลตามเงื่อนไข</div>}
              <div style={{ marginTop: 8 }}>
                {roleRecs.slice(0, 16).map(r => (
                  <div key={`${r.team}-${r.role}-${r.hero}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid rgba(255,16,38,0.06)", padding: "9px 0", gap: 8 }}>
                    <span style={{ fontSize: 13 }}><span style={{ color: T.primaryLight, fontWeight: 700 }}>{r.team}</span> <span style={{ color: T.accent }}>{r.role}</span> — {r.hero}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: r.label === "Core" ? "rgba(0,208,132,0.15)" : r.label === "Weak" ? "rgba(255,77,109,0.15)" : T.tintSm, color: r.label === "Core" ? T.success : r.label === "Weak" ? T.danger : T.muted }}>{r.label}</span>
                  </div>
                ))}
              </div>
            </GlassCard>

            {/* Parse Stats */}
            <GlassCard style={{ padding: "16px 18px" }} delay={9}>
              <SectionTitle icon={BarChart3} title="Parse Stats" />
              <div style={{ color: T.muted, fontSize: 12, lineHeight: 1.9, marginTop: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}><span>Team sheets</span><strong style={{ color: T.text }}>{data?.meta?.teamSheets?.length || 0}</strong></div>
                <div style={{ display: "flex", justifyContent: "space-between" }}><span>Parsed rows</span><strong style={{ color: T.text }}>{data?.meta?.totalRows || 0}</strong></div>
                <div style={{ display: "flex", justifyContent: "space-between" }}><span>Duplicate dropped</span><strong style={{ color: T.text }}>{data?.meta?.duplicateDropped || 0}</strong></div>
                <div style={{ display: "flex", justifyContent: "space-between" }}><span>Inferred rows</span><strong style={{ color: T.warn }}>{(data?.raw || []).filter(r => isInferredType(r.type)).length}</strong></div>
              </div>
              <div className="tp-scroll" style={{ marginTop: 10, maxHeight: 220, overflowY: "auto", borderTop: "1px solid rgba(255,16,38,0.08)", paddingTop: 8 }}>
                <table className="tp-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                  <thead>
                    <tr>
                      {["Sheet", "Parsed", "Dropped", "Inferred"].map((h) => (
                        <th key={h} style={{ padding: "6px 4px", textAlign: "left", color: T.muted, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", borderBottom: "1px solid rgba(255,16,38,0.08)" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...qualityRows]
                      .sort((a, b) => (b.inferred_rows - a.inferred_rows) || (b.dropped_rows - a.dropped_rows))
                      .slice(0, 12)
                      .map((r) => (
                        <tr key={r.sheet}>
                          <td style={{ ...tdS, fontSize: 11, padding: "6px 4px" }}>{r.sheet}</td>
                          <td style={{ ...tdS, fontSize: 11, padding: "6px 4px", color: T.text }}>{r.parsed_rows}</td>
                          <td style={{ ...tdS, fontSize: 11, padding: "6px 4px", color: r.dropped_rows > r.parsed_rows ? T.warn : T.muted }}>{r.dropped_rows}</td>
                          <td style={{ ...tdS, fontSize: 11, padding: "6px 4px", color: r.inferred_rows > 0 ? T.warn : T.muted, fontWeight: r.inferred_rows > 0 ? 700 : 500 }}>{r.inferred_rows}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </GlassCard>

            {/* Coverage */}
            <GlassCard style={{ padding: "16px 18px" }} delay={10}>
              <SectionTitle icon={Layers} title="Coverage by Team" />
              <div style={{ marginTop: 8 }}>
                {(data?.meta?.coverage?.byTeam || []).slice(0, 14).map(c => (
                  <div key={c.team} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid rgba(255,16,38,0.06)", padding: "8px 0", gap: 8, fontSize: 12 }}>
                    <span style={{ fontWeight: 700, color: T.primaryLight }}>{c.team}</span>
                    <span style={{ color: T.muted }}>
                      <span style={{ color: T.primary }}>S:{c.scrim}</span>{" "}
                      <span style={{ color: T.accent }}>M:{c.official}</span>{" "}
                      O:{c.other}
                    </span>
                  </div>
                ))}
                <div style={{ marginTop: 8, color: T.muted, fontSize: 11 }}>
                  {bySource.map(s => `${s.source}: ${s.rows}`).join(" | ")}
                </div>
              </div>
            </GlassCard>
          </div>
          )}
        </div>
        </>
        )}
      </div>
    </div>
  );
}

/* ── table cell style ── */
const tdS = { padding: "9px 8px", borderTop: "1px solid rgba(255,16,38,0.05)", transition: "background .2s" };
